// @ts-nocheck
import React, { useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { useShadcnInsetChrome } from "@/contexts/ShadcnInsetChromeContext";
import { useShadcnInsetHeaderActions } from "@/contexts/ShadcnInsetHeaderActionsContext";
import { BrokerManagementHeaderActions, BrokerImportTradesButton, BrokerResyncTradesButton } from "./BrokerManagementHeaderActions";
import { ConnectBrokerWizardDialog } from "../connectBroker/ConnectBrokerWizardDialog";
import {
  parseConnectBrokerWizardOpenSearch,
  stripConnectBrokerWizardOpenParams,
  type ConnectBrokerWizardOpenSeed,
} from "../connectBroker/connectBrokerWizardConstants";
import type { ConnectBrokerImportMethodId } from "../connectBroker/connectBrokerImportMethods";
import { Tabs as ShadcnTabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button as ShadcnButton } from "@/components/ui/button";
import { Field, FieldDescription, FieldLabel } from "@/components/ui/field";
import { Input as ShadcnInput } from "@/components/ui/input";
import { toastError, presentMutationError, toastWarning, toastUserFacingError } from "../../../../utils/showApiErrorToast";
import { isApiRequestError } from "../../../../utils/apiRequestError";
import {
  Select as BrokerFormSelect,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { AccountSettingsFieldSelect } from "./AccountSettingsFieldSelect";
import { TimezoneCombobox } from "@/components/TimezoneCombobox";
import { Checkbox } from "@/components/ui/checkbox";
import { buildTimezoneSelectOptions } from "@/utils/ianaTimezones";
import { RadioGroup as ShadcnRadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import BrokerConnectedAccountsTable from "./BrokerConnectedAccountsTable";
import {
  buildBrokerAvailableAccountRows,
  buildBrokerConnectedAccountRows,
} from "../brokerConnectedAccountRows";
import { RemoveBrokerCredentialsDialog, REMOVE_BROKER_CREDENTIALS_CONFIRM_PHRASE, DISCONNECT_BROKER_OAUTH_CONFIRM_PHRASE } from "./RemoveBrokerCredentialsDialog";
import { RemoveBrokerDialog, REMOVE_BROKER_CONFIRM_PHRASE } from "./RemoveBrokerDialog";
import { StrategyPineDetailDialog } from "@/components/Strategies/StrategyPineDetailDialog";
import { BrokerMaskedCredentialInput } from "../../../../components/Broker/BrokerMaskedCredentialInput";
import { SingleDatePicker } from "@/components/ui/date-picker";
import {
  BROKER_ALERT_CLASS,
  BROKER_ALERT_DESC_CLASS,
  BROKER_ALERT_TITLE_CLASS,
  BROKER_FIELD_DESC_CLASS,
  BrokerAlertIcon,
  brokerAlertVariant,
  BROKER_OUTLINE_BADGE_CLASS,
} from "../../../../components/Broker/brokerAlertUi";
import { Card, CardContent } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import {
  statisticsShadcnCardContentClassName,
} from "../../Statistics/components/statisticsShadcnCardShell";
import { accountSettingsNestedCardClassName } from "../accountSettingsCardShell";
import { accountSettingsCardStackClassName, accountSettingsFieldClassName } from "./SettingCard";
import {
  StatisticsEmptyState,
  statisticsPageMinHeight,
} from "../../Statistics/components/statisticsEmptyState";
import {
  csvImportMaxSizeMessage,
  isCsvImportFileWithinLimit,
} from "../../../../constants/securityLimits";
import { useActiveBrokerAccount } from "../../../../hooks/useActiveBrokerAccount";
import { useRunInstrumentSetupAfterSync } from "../../../../hooks/useRunInstrumentSetupAfterSync";
import { useRateLimitCooldown } from "../../../../hooks/useRateLimitCooldown";
import type { TradeAccountOption } from "../../../../contexts/ActiveBrokerAccountContext";
import { clearAllTradeCaches, notifyTradesUpdated } from "../../../../hooks/useTradeStatistics";
import { afterBrokerResyncWaitForAnalytics } from "../../../../utils/brokerSyncStatusPoll";
import {
  BrokerAccount,
  BrokerCapabilities,
  deleteBrokerAccount,
  deleteBrokerCredentials,
  fetchBrokerAccounts,
  fetchBrokerCapabilities,
  fetchBrokerCredentialsForm,
  fetchCtraderOAuthAuthorizeUrl,
  getBrokerCredentials,
  isCustomCsvBroker,
  resolveBrokerEditFormPath,
  saveBrokerCredentials,
  setActiveBrokerAccount,
  syncBrokerTrades,
  testBrokerConnection,
  uploadBrokerCsvImport,
} from "../../../../services/brokerIntegrationApi";
import {
  BINANCE_FIELD_HINTS,
  BINANCE_FIELD_LABELS,
  BINANCE_IGNORED_CREDENTIAL_FIELDS,
  binanceCredentialsFormToFormValues,
  buildBinanceCredentialsPayload,
  buildBinanceTestConnectionPayload,
  isBinanceAccountStartDateField,
  isBinanceApiKeyField,
  isBinanceApiSecretField,
  isBinanceReconnectError,
  isBinanceSpotSymbolsField,
  isBinanceSyncCredentialField,
  snapshotToBinanceCredentialForm,
  validateBinanceAuthCredentials,
  validateBinanceCredentialValues,
  getBinanceAccountStartDateHint,
  getBrokerCredentialInputAttributes,
  isBrokerMaskedCredentialField,
} from "../../../../utils/binanceBrokerCredentials";
import {
  clearBinanceHistoryPath,
  loadBinanceHistoryPath,
  resolveBinanceTradeSync,
  saveBinanceHistoryPath,
  type BinanceHistoryPath,
} from "../../../../utils/binanceTradeSync";
import {
  binanceCsvImportStatsAreEmpty,
  binanceCsvImportZeroTradesHint,
  detectBinanceCsvFormat,
  validateBinanceCsvForImport,
} from "../../../../utils/binanceCsvImport";
import BinanceSpotSymbolsField from "../../../../components/Broker/BinanceSpotSymbolsField";
import BinanceTradeSyncGuidance from "../../../../components/Broker/BinanceTradeSyncGuidance";
import BinanceCsvImportGuidance, {
  BinanceCsvTransactionHistoryWarning,
} from "../../../../components/Broker/BinanceCsvImportGuidance";
import BinanceHistoryPathChoice from "../../../../components/Broker/BinanceHistoryPathChoice";
import BrokerHistoryPathChoice from "../../../../components/Broker/BrokerHistoryPathChoice";
import IbkrFlexSyncGuidance from "../../../../components/Broker/IbkrFlexSyncGuidance";
import IbkrHistoryPathBanner from "../../../../components/Broker/IbkrHistoryPathBanner";
import TradingViewBacktestGuidance from "../../../../components/Broker/TradingViewBacktestGuidance";
import TradingViewPaperGuidance from "../../../../components/Broker/TradingViewPaperGuidance";
import Mt5TradeHistoryGuidance from "../../../../components/Broker/Mt5TradeHistoryGuidance";
import Mt4StatementImportGuidance from "../../../../components/Broker/Mt4StatementImportGuidance";
import CtraderStatementImportGuidance from "../../../../components/Broker/CtraderStatementImportGuidance";
import CtraderOpenApiSyncGuidance from "../../../../components/Broker/CtraderOpenApiSyncGuidance";
import CsvImportAccountCodeField, {
  isCsvAccountIdentityField,
} from "../../../../components/Broker/CsvImportAccountCodeField";
import { BrokerCsvFileUploadField } from "../../../../components/Broker/BrokerCsvFileUploadField";
import CustomCsvImportWizard, {
  type CustomCsvWizardNavigationHandle,
} from "../../../../components/Broker/CustomCsvImport/CustomCsvImportWizard";
import {
  resolveBrokerDisplayName,
} from "../../../../utils/customCsvImport";
import { brokerSupportsLiveBrokerAccountsApi, brokerSkipsCredentialsPrefetch, parseBrokerCredentialSyncFlags, isStatementImportAccountsBroker, statementImportAccountsManagementCopy } from "../../../../utils/brokerLiveSync";
import FuturesOrdersCsvImportGuidance from "../../../../components/Broker/FuturesOrdersCsvImportGuidance";
import HyperliquidCsvImportGuidance from "../../../../components/Broker/HyperliquidCsvImportGuidance";
import LiquidBrokersStatementImportGuidance from "../../../../components/Broker/LiquidBrokersStatementImportGuidance";
import OandaTransactionsImportGuidance from "../../../../components/Broker/OandaTransactionsImportGuidance";
import { toast } from "sonner";
import { brokerSonnerToast } from "../../../../utils/brokerSonnerToast";
import IbkrSetupInstructions from "../../../../components/Broker/IbkrSetupInstructions";
import {
  brokerCsvImportFieldLabel,
  brokerTradeSyncCredentialLabel,
  brokerTradeSyncShortDescription,
  clearBrokerHistoryPath,
  loadBrokerHistoryPath,
  resolveBrokerCsvImport,
  resolveBrokerTradeSync,
  saveBrokerHistoryPath,
  type BrokerHistoryPath,
} from "../../../../utils/brokerTradeSync";
import {
  buildIbkrCredentialPayload,
  isIbkrConnectCredentialField,
  isIbkrFlexMissingTimezoneError,
} from "../../../../utils/ibkrBrokerSetup";
import {
  buildTradingViewCsvImportParams,
  formatAssetClassLabel,
  isTradedAssetClassesField,
  isTradingViewBroker,
  isTradingViewHiddenCsvField,
  TRADINGVIEW_DEFAULT_ASSET_CLASSES,
  tradingViewCsvFieldLabel,
} from "../../../../utils/tradingViewBroker";
import {
  buildTradingViewPaperCsvImportParams,
  formatTradingViewPaperImportSuccessDescription,
  formatTradingViewPaperOpenLotsWarning,
  isTradingViewPaperBroker,
  isTradingViewPaperHiddenCsvField,
  looksLikeTradingViewStrategyTesterCsv,
  TRADINGVIEW_PAPER_DEFAULT_EXECUTING_BROKER,
  tradingViewPaperCsvFieldLabel,
  tradingViewPaperWrongFileHint,
} from "../../../../utils/tradingViewPaperBroker";
import {
  buildHyperliquidConnectCredentialPayload,
  buildHyperliquidCsvImportParams,
  hyperliquidCsvFieldLabel,
  isHyperliquidBroker,
  isHyperliquidHiddenCredentialField,
  isHyperliquidHiddenCsvField,
} from "../../../../utils/hyperliquidBroker";
import {
  buildMt5XlsxImportParams,
  formatMt5ReconciliationWarning,
  isMt5Broker,
  isMt5HiddenImportField,
  isMt5XlsxFile,
  MT5_DEFAULT_ASSET_CLASSES,
  mt5ImportFieldLabel,
} from "../../../../utils/mt5Broker";
import {
  buildMt4HtmlImportParams,
  formatMt4ReconciliationWarning,
  isMt4Broker,
  isMt4HiddenCredentialField,
  isMt4HiddenImportField,
  isMt4HtmlFile,
  MT4_DEFAULT_ASSET_CLASSES,
  mt4ImportFieldLabel,
} from "../../../../utils/mt4Broker";
import {
  buildFuturesOrdersCsvImportParams,
  formatFuturesOrdersFixedAssetClassesLabel,
  FUTURES_ORDERS_CASH_HISTORY_FIELD,
  futuresOrdersCashHistoryFieldHint,
  futuresOrdersCashHistoryFieldLabel,
  futuresOrdersCsvImportFieldLabel,
  futuresOrdersImportSuccessSuffix,
  futuresOrdersPlatformDisplayName,
  getFuturesOrdersFeesFollowUpToasts,
  isFuturesOrdersCsvBroker,
  isFuturesOrdersCsvFile,
  isFuturesOrdersCsvHiddenCredentialField,
  isFuturesOrdersCsvHiddenImportField,
} from "../../../../utils/futuresOrdersCsvBroker";
import {
  buildCtraderConnectCredentialPayload,
  resolveCtraderAccountCodeFromValues,
  buildCtraderCsvImportParams,
  CTRADER_DEFAULT_ASSET_CLASSES,
  CTRADER_UTC_OFFSET_FIELD,
  ctraderImportFieldLabel,
  formatCtraderImportDetailLines,
  formatCtraderImportSuccessDescription,
  formatCtraderReconciliationWarning,
  isCtraderBroker,
  isCtraderCsvFile,
  isCtraderHiddenCredentialField,
  isCtraderHiddenImportField,
  isCtraderUtcOffsetField,
  parseCtraderSavedAssetClasses,
} from "../../../../utils/ctraderBroker";
import {
  ctraderCsvImportStatsAreEmpty,
  ctraderCsvImportZeroTradesHint,
  ctraderCsvHasNonBreakingSpaces,
  sanitizeCtraderCsvText,
  validateCtraderCsvForImport,
} from "../../../../utils/ctraderCsvImport";
import {
  ctraderDisplayFromCredentials,
  formatCtraderConnectedSummary,
  formatCtraderSyncHint,
} from "../../../../utils/ctraderAccountDisplay";
import { resolveProfileBrokerFromCapabilities, normalizeProfileBrokerSlug } from "../../../../utils/profileBrokerNavigation";
import { canResyncTradeAccountForUi } from "../../../../utils/tradeAccountSource";
import { accountSettingsTabPath } from "@/constants/accountSettingsSidebar";
import {
  buildGenericBrokerCsvImportParams,
  mergeTradedAssetClassesIntoPayload,
  parseSavedTradedAssetClasses,
  resolveConnectTradedAssetClasses,
  resolveCsvTradedAssetClasses,
  shouldRenderConnectAssetClassPicker,
  shouldRenderConnectAssetClassReadOnly,
  tradedAssetClassesFieldLabel,
  formatTradedAssetClassesReadOnly,
  type BrokerTradedAssetClassesConfig,
} from "../../../../utils/brokerTradedAssetClasses";
import {
  executingBrokerFieldHint,
  executingBrokerFieldLabel,
  isExecutingBrokerNameField,
  isStatementImportPlatform,
  mergeExecutingBrokerIntoPayload,
  parseSavedExecutingBrokerName,
  resolveConnectExecutingBroker,
  resolveCsvExecutingBroker,
  shouldShowExecutingBrokerOnConnect,
  shouldShowExecutingBrokerOnCsvImport,
  type BrokerExecutingBrokerConfig,
} from "../../../../utils/brokerExecutingBroker";
import {
  clearCtraderHistoryPath,
  loadCtraderHistoryPath,
  resolveCtraderOpenApiSync,
  saveCtraderHistoryPath,
  stashCtraderOAuthAssetClasses,
  stashCtraderOAuthEnvironment,
  type CtraderHistoryPath,
} from "../../../../utils/ctraderOpenApiSync";
import {
  isCtraderOAuthPopupMessage,
  openCtraderOAuthPopup,
} from "../../../../utils/ctraderOAuthPopup";

const BROKER_INTEGRATION_BODY_TEXT_CLASS =
  "font-[family-name:var(--font-primary)] text-[13px] text-gray-700 dark:text-gray-100";
const BROKER_INTEGRATION_SECONDARY_TEXT_CLASS =
  "font-[family-name:var(--font-primary)] text-sm text-gray-500 dark:text-white";
const BROKER_INTEGRATION_SAVED_BADGE_CLASS =
  "inline-flex h-5 w-fit shrink-0 items-center justify-center rounded-[6px] border border-green-500 bg-transparent px-2 text-xs font-normal text-green-500";

const normalizeBrokerName = (brokerName: string): string => {
  if (brokerName === "liquid") return "liquidbrokers";
  return brokerName;
};

const isOandaEnvironmentCredentialField = (field: string): boolean =>
  field === "oanda_environment" || field === "oandaEnvironment";

type CtraderEnvironment = "demo" | "live";

function parseCtraderEnvironment(
  credentials: Record<string, unknown> | null | undefined
): CtraderEnvironment | null {
  if (!credentials) return null;
  const raw = String(
    credentials.environment ??
      credentials.ctrader_environment ??
      credentials.ctraderEnvironment ??
      ""
  )
    .trim()
    .toLowerCase();
  if (raw === "demo" || raw === "live") return raw;
  return null;
}

/** Used only when capabilities omit options so the form still shows Live / Practice. */
const DEFAULT_OANDA_ENVIRONMENT_OPTIONS: { value: string; label: string }[] = [
  { value: "live", label: "Live (fxTrade)" },
  { value: "practice", label: "Practice (fxTrade Practice / demo)" },
];

function parseOandaEnvironmentOptionsFromCapability(cap: Record<string, any> | null | undefined) {
  if (!cap) return [];
  const auth = cap.auth || {};
  const lists = [
    auth.oanda_environment_options,
    auth.oandaEnvironmentOptions,
    cap.oanda_environment_options,
    cap.oandaEnvironmentOptions,
  ];
  for (const raw of lists) {
    if (!Array.isArray(raw) || raw.length === 0) continue;
    const cleaned = raw
      .map((item: any) => {
        if (!item || typeof item !== "object") return null;
        const value = item.value ?? item.Value;
        const label = item.label ?? item.Label ?? (value != null ? String(value) : "");
        if (value == null || String(value).trim() === "") return null;
        return { value: String(value).trim(), label: String(label || value).trim() };
      })
      .filter(Boolean) as { value: string; label: string }[];
    if (cleaned.length > 0) return cleaned;
  }
  return [];
}

function resolveOandaEnvironmentOptions(cap: BrokerCapabilities | null | undefined) {
  if (!cap) return [];
  const fromApi = parseOandaEnvironmentOptionsFromCapability(cap as any);
  if (fromApi.length > 0) return fromApi;
  const req = cap.auth?.required_fields ?? [];
  const opt = cap.auth?.optional_fields ?? [];
  const hasField = [...req, ...opt].some(isOandaEnvironmentCredentialField);
  if (normalizeBrokerName(cap.broker_name || "") === "oanda" && hasField) {
    return DEFAULT_OANDA_ENVIRONMENT_OPTIONS;
  }
  return [];
}

const makeLabel = (field: string): string => {
  if (BINANCE_FIELD_LABELS[field]) return BINANCE_FIELD_LABELS[field];
  return field
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
};

const FIELD_META: Record<
  string,
  Record<string, { placeholder: string; hint: string }>
> = {
  liquidbrokers: {
    username: {
      placeholder: "Enter your Liquid Charts username/email",
      hint: "Use your Liquid Charts login username or email.",
    },
    password: {
      placeholder: "Enter your Liquid Charts password",
      hint: "Password is encrypted and never returned from the API.",
    },
    account_code: {
      placeholder: "e.g. default:DEM_123456",
      hint: "Pick the account code that should be synced.",
    },
    statement_time_zone: {
      placeholder: "Select statement timezone",
      hint: "Timezone used to parse statement timestamps.",
    },
    traded_asset_classes: {
      placeholder: "",
      hint: "Optional at connect. Ambiguous symbols are confirmed in instrument setup after sync.",
    },
  },
  oanda: {
    oanda_access_token: {
      placeholder: "Paste your OANDA access token",
      hint: "Generate a personal access token from OANDA settings.",
    },
    oanda_environment: {
      placeholder: "Select environment",
      hint: "Live is for real fxTrade account tokens. Practice is for OANDA demo (fxTrade Practice) tokens. Use the one that matches how you created your token.",
    },
    oandaEnvironment: {
      placeholder: "Select environment",
      hint: "Live is for real fxTrade account tokens. Practice is for OANDA demo (fxTrade Practice) tokens. Use the one that matches how you created your token.",
    },
    oanda_account_id: {
      placeholder: "e.g. 101-001-1234567-001",
      hint: "Primary OANDA account ID to sync.",
    },
    account_code: {
      placeholder: "e.g. oanda_main",
      hint: "Logical account code to associate synced trades.",
    },
    traded_asset_classes: {
      placeholder: "",
      hint: "Optional at connect. Ambiguous symbols are confirmed in instrument setup after sync.",
    },
  },
  binance: {
    binance_api_key: {
      placeholder: "Paste your Binance API key",
      hint: BINANCE_FIELD_HINTS.binance_api_key,
    },
    binance_api_secret: {
      placeholder: "Paste your Binance API secret",
      hint: BINANCE_FIELD_HINTS.binance_api_secret,
    },
    binance_spot_symbols: {
      placeholder: "",
      hint: BINANCE_FIELD_HINTS.binance_spot_symbols,
    },
    binance_account_start_date: {
      placeholder: "",
      hint: BINANCE_FIELD_HINTS.binance_account_start_date,
    },
    traded_asset_classes: {
      placeholder: "",
      hint: "Optional at connect. Ambiguous symbols are confirmed in instrument setup after sync.",
    },
  },
  ibkr: {
    traded_asset_classes: {
      placeholder: "",
      hint: "Optional at connect. Ambiguous symbols are confirmed in instrument setup after sync.",
    },
    ibkr_flex_token: {
      placeholder: "Paste your IBKR Flex token",
      hint: "Get this from IBKR Flex Web Service configuration.",
    },
    ibkr_query_id: {
      placeholder: "Enter your Flex Query ID (Report ID)",
      hint: "Report ID from your IBKR Flex query configuration.",
    },
  },
  tradingview: {
    account_name: {
      placeholder: "e.g. My gold backtest",
      hint: "A label so you can tell this account apart in Tessera.",
    },
    instrument: {
      placeholder: "Chart symbol",
      hint: "Must match the symbol on your TradingView chart. Applies to every row in this file.",
    },
    time_zone: {
      placeholder: "Select chart timezone",
      hint: "Must match the timezone on your TradingView chart. Wrong zone shifts all entry and exit times.",
    },
    traded_asset_classes: {
      placeholder: "",
      hint: "One asset class per import. Each backtest file is for a single instrument.",
    },
    executing_broker_name: {
      placeholder: "e.g. IC Markets, FTMO",
      hint: "The brokerage your backtest represents, not TradingView.",
    },
  },
  tradingview_paper: {
    account_name: {
      placeholder: "e.g. TV Paper Main",
      hint: "A label for this Paper Trading account in Tessera (stored as your account code).",
    },
    time_zone: {
      placeholder: "Select export timezone",
      hint: "Must match the timezone used in the Order History export timestamps.",
    },
    traded_asset_classes: {
      placeholder: "",
      hint: "Optional at import. Ambiguous symbols are confirmed in instrument setup after import.",
    },
    executing_broker_name: {
      placeholder: "Paper",
      hint: "Usually Paper. Use a prop/broker name only if you want that label on the account picker.",
    },
  },
  mt5: {
    time_zone: {
      placeholder: "Select report timezone",
      hint: "Usually your broker server time. Match the times shown in the report, not necessarily your PC clock.",
    },
    traded_asset_classes: {
      placeholder: "",
      hint: "Optional at import. Ambiguous symbols are confirmed in instrument setup after import.",
    },
    executing_broker_name: {
      placeholder: "e.g. Pepperstone, IC Markets",
      hint: "The brokerage that issued this MT5 report.",
    },
  },
  mt4: {
    time_zone: {
      placeholder: "Select report timezone",
      hint: "Match the timestamps shown in the MT4 report (usually broker server time).",
    },
    traded_asset_classes: {
      placeholder: "",
      hint: "Optional at import. Ambiguous symbols are confirmed in instrument setup after import.",
    },
    executing_broker_name: {
      placeholder: "e.g. Pepperstone, IC Markets",
      hint: "The brokerage that issued this MT4 report.",
    },
  },
  tradovate: {
    time_zone: {
      placeholder: "Select Fill Time timezone",
      hint: "Usually your PC local time. Match the Fill Time column in the CSV.",
    },
  },
  ninjatrader: {
    time_zone: {
      placeholder: "Select Fill Time timezone",
      hint: "Usually your PC local time. Match the Fill Time column in the CSV.",
    },
  },
  hyperliquid: {
    account_name: {
      placeholder: "e.g. Hyperliquid main",
      hint: "A label for this Hyperliquid account in Tessera (stored as your account code).",
    },
    time_zone: {
      placeholder: "Select trade history timezone",
      hint: "IANA timezone for naive timestamps in the Hyperliquid export (time column).",
    },
    traded_asset_classes: {
      placeholder: "",
      hint: "Defaults to crypto when omitted.",
    },
  },
  ctrader: {
    executing_broker_name: {
      placeholder: "e.g. FxPro, IC Markets",
      hint: "The CFD/FX broker for this account, not cTrader.",
    },
    account_code: {
      placeholder: "e.g. 5826993",
      hint: "Your cTrader account number (same as shown in the cTrader app).",
    },
    traded_asset_classes: {
      placeholder: "",
      hint: "What you trade on this account (usually FX for cTrader CFD/FX).",
    },
    [CTRADER_UTC_OFFSET_FIELD]: {
      placeholder: "e.g. 8",
      hint: "Only if export columns don't show (UTC+8) etc. Leave blank when headers include the offset.",
    },
  },
};

const DEFAULT_FIELD_META = {
  placeholder: "",
  hint: "",
};

const getFieldMeta = (brokerName: string, field: string) => {
  const fromBroker = FIELD_META[brokerName]?.[field];
  if (fromBroker) return fromBroker;
  if (brokerName === "binance" && BINANCE_FIELD_HINTS[field]) {
    return { placeholder: "", hint: BINANCE_FIELD_HINTS[field] };
  }
  return DEFAULT_FIELD_META;
};

function filterCredentialFields(fields: string[], brokerName: string): string[] {
  const normalized = normalizeBrokerName(brokerName);
  if (normalized === "binance") {
    return fields.filter((f) => !BINANCE_IGNORED_CREDENTIAL_FIELDS.has(f));
  }
  if (isFuturesOrdersCsvBroker(normalized)) {
    return fields.filter((f) => !isFuturesOrdersCsvHiddenCredentialField(f));
  }
  if (isCtraderBroker(normalized)) {
    return fields.filter((f) => !isCtraderHiddenCredentialField(f));
  }
  if (isMt4Broker(normalized)) {
    return fields.filter((f) => !isMt4HiddenCredentialField(f));
  }
  if (isHyperliquidBroker(normalized)) {
    return fields.filter((f) => !isHyperliquidHiddenCredentialField(f));
  }
  return fields;
}

function buildCredentialPayloadForApi(
  brokerName: string,
  values: Record<string, string>,
  purpose: "save" | "test" = "save",
  binanceSecretsOnFile?: { hasApiKeyOnFile: boolean; hasApiSecretOnFile: boolean },
  connectAssetClasses: string[] = [],
  connectTradedAssetClasses: BrokerTradedAssetClassesConfig | null = null,
  executingBrokerName = "",
  connectExecutingBroker: BrokerExecutingBrokerConfig | null = null
): Record<string, unknown> {
  let payload: Record<string, unknown>;
  if (normalizeBrokerName(brokerName) === "binance") {
    payload =
      purpose === "test"
        ? buildBinanceTestConnectionPayload(values)
        : buildBinanceCredentialsPayload(values, binanceSecretsOnFile);
  } else if (normalizeBrokerName(brokerName) === "ibkr") {
    payload = buildIbkrCredentialPayload(values);
  } else if (isCtraderBroker(brokerName)) {
    payload = buildCtraderConnectCredentialPayload(
      connectAssetClasses,
      executingBrokerName,
      resolveCtraderAccountCodeFromValues(values)
    );
  } else if (isHyperliquidBroker(brokerName)) {
    return buildHyperliquidConnectCredentialPayload(values);
  } else if (isStatementImportPlatform(brokerName)) {
    payload = {
      broker_name: normalizeBrokerName(brokerName),
    };
  } else {
    const {
      traded_asset_classes: _a,
      tradedAssetClasses: _b,
      executing_broker_name: _c,
      executingBrokerName: _d,
      ...rest
    } = values;
    payload = { ...rest };
  }
  const withAssetClasses = mergeTradedAssetClassesIntoPayload(
    payload,
    connectAssetClasses,
    connectTradedAssetClasses
  );
  return mergeExecutingBrokerIntoPayload(
    withAssetClasses,
    executingBrokerName ||
      values.executing_broker_name ||
      values.executingBrokerName ||
      "",
    connectExecutingBroker
  );
}

const IANA_TIMEZONES: string[] =
  typeof Intl !== "undefined" &&
  typeof (Intl as any).supportedValuesOf === "function"
    ? ((Intl as any).supportedValuesOf("timeZone") as string[])
    : ["UTC"];

const IANA_TIMEZONE_SET = new Set(IANA_TIMEZONES);

const isTimezoneField = (field: string): boolean => {
  const lower = field.toLowerCase();
  return lower.includes("time_zone") || lower.includes("timezone");
};

/** First non-empty trimmed string — used so OANDA sync can fall back from logical `account_code` to `oanda_account_id`. */
function pickFirstNonEmptyTrimmed(
  ...vals: Array<string | undefined | null>
): string {
  for (const v of vals) {
    if (v == null) continue;
    const t = String(v).trim();
    if (t) return t;
  }
  return "";
}

function tradeAccountMatchesImportIdentity(
  account: TradeAccountOption,
  importIdentity: string
): boolean {
  const needle = importIdentity.trim().toLowerCase();
  if (!needle) return false;
  const candidates = [
    account.accountCode,
    account.tradeLabel,
    account.customName,
  ]
    .map((s) => String(s ?? "").trim().toLowerCase())
    .filter(Boolean);
  return candidates.includes(needle);
}

function isTradingViewReimportToExistingAccount(
  accountName: string,
  accounts: TradeAccountOption[]
): boolean {
  const name = accountName.trim();
  if (!name) return false;
  return accounts.some((acc) => tradeAccountMatchesImportIdentity(acc, name));
}

function formatCsvImportStatsDescription(payload: any): string | null {
  const stats = payload?.sync_stats ?? payload?.syncStats;
  if (!stats || typeof stats !== "object") return null;
  const c = stats.created ?? stats.Created;
  const u = stats.updated ?? stats.Updated;
  const s = stats.skipped ?? stats.Skipped;
  const t = stats.total ?? stats.Total;
  if (c == null && u == null && s == null && t == null) return null;
  const parts: string[] = [];
  if (typeof c === "number") parts.push(`${c} new`);
  if (typeof u === "number") parts.push(`${u} updated`);
  if (typeof s === "number") parts.push(`${s} skipped`);
  if (typeof t === "number") parts.push(`${t} total`);
  return parts.length ? parts.join(", ") : null;
}

export type EmbeddedSetupBackHandle = {
  tryHandleBack: () => boolean;
};

export type EmbeddedWizardFooterAction = {
  label: string;
  onClick: () => void;
  disabled?: boolean;
  isLoading?: boolean;
  loadingLabel?: string;
};

const BrokerIntegrationSection: React.FC<{
  embeddedInConnectWizard?: boolean;
  embeddedBrokerName?: string;
  embeddedImportMethod?: ConnectBrokerImportMethodId;
  embeddedSetupBackRef?: React.RefObject<EmbeddedSetupBackHandle | null>;
  onEmbeddedWizardFooterChange?: (action: EmbeddedWizardFooterAction | null) => void;
  onEmbeddedWizardImportMethodChange?: (method: ConnectBrokerImportMethodId) => void;
  /** Called when embedded setup finishes so the parent dialog can close. */
  onEmbeddedWizardComplete?: () => void;
}> = ({
  embeddedInConnectWizard = false,
  embeddedBrokerName = "",
  embeddedImportMethod,
  embeddedSetupBackRef,
  onEmbeddedWizardFooterChange,
  onEmbeddedWizardImportMethodChange,
  onEmbeddedWizardComplete,
}) => {
  const location = useLocation();
  const history = useHistory();
  const shadcnInset = useShadcnInsetChrome();
  const { setActions: setInsetHeaderActions } = useShadcnInsetHeaderActions();
  const brokerManagementTopRef = useRef<HTMLDivElement | null>(null);
  const csvImportFormRef = useRef<HTMLDivElement | null>(null);
  const customCsvWizardNavRef = useRef<CustomCsvWizardNavigationHandle | null>(null);
  const {
    refetch: refetchTradeAccounts,
    setActiveTradeAccount,
    removeTradeAccount,
    activeBrokerName,
    activeAccountCode,
    accounts: tradeAccountOptions,
    loading: tradeAccountsLoading,
    hasMultipleCtraderAccounts,
    ctraderFoundAccounts,
  } = useActiveBrokerAccount();
  const runInstrumentSetupAfterSync = useRunInstrumentSetupAfterSync();

  const [capabilities, setCapabilities] = useState<BrokerCapabilities[]>([]);
  const [selectedBroker, setSelectedBroker] = useState("");
  const [credentialValues, setCredentialValues] = useState<Record<string, string>>({});
  const [csvValues, setCsvValues] = useState<Record<string, string>>({});
  const [csvAssetClasses, setCsvAssetClasses] = useState<string[]>([]);
  const [accounts, setAccounts] = useState<BrokerAccount[]>([]);
  const [accountCode, setAccountCode] = useState("");
  const [csvFile, setCsvFile] = useState<File | null>(null);
  const [futuresOrdersCashHistoryFile, setFuturesOrdersCashHistoryFile] = useState<File | null>(
    null
  );
  /** Always read latest Cash History pick — connect-wizard footer onClick can close over stale state. */
  const futuresOrdersCashHistoryFileRef = useRef<File | null>(null);
  futuresOrdersCashHistoryFileRef.current = futuresOrdersCashHistoryFile;
  const [connectAssetClasses, setConnectAssetClasses] = useState<string[]>([]);
  const [executingBrokerName, setExecutingBrokerName] = useState("");
  const [ctraderConnectEnvironment, setCtraderConnectEnvironment] =
    useState<CtraderEnvironment>("demo");
  const [ctraderHistoryPath, setCtraderHistoryPath] = useState<CtraderHistoryPath | null>(() =>
    loadCtraderHistoryPath()
  );
  const [isCtraderOAuthStarting, setIsCtraderOAuthStarting] = useState(false);
  const ctraderOAuthPopupRef = useRef<Window | null>(null);
  const ctraderOAuthPopupPollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const [credentialsSaved, setCredentialsSaved] = useState(false);
  const [credentialBrokerSlugs, setCredentialBrokerSlugs] = useState<Set<string>>(
    () => new Set()
  );
  /** Brokers kept after DELETE /credentials so zero-trade synced accounts stay manageable. */
  const [accountsKeptAfterCredentialRemoval, setAccountsKeptAfterCredentialRemoval] =
    useState<Record<string, TradeAccountOption[]>>({});
  const [testResult, setTestResult] = useState<string>("");
  const [testResultAction, setTestResultAction] = useState<string>("");
  const [credentialsTestPassed, setCredentialsTestPassed] = useState(false);
  const [isLoadingCapabilities, setIsLoadingCapabilities] = useState(true);
  const [capabilitiesHydrated, setCapabilitiesHydrated] = useState(false);
  const [isLoadingBrokerDetails, setIsLoadingBrokerDetails] = useState(false);
  const [hasLoadedBrokerDetailsOnce, setHasLoadedBrokerDetailsOnce] = useState(false);
  /** Ignore stale responses when the user changes broker before prior loads finish. */
  const brokerDetailsLoadIdRef = useRef(0);
  const [isSaving, setIsSaving] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [justSyncedAccountCodes, setJustSyncedAccountCodes] = useState<Set<string>>(
    () => new Set()
  );
  const [isUploadingCsv, setIsUploadingCsv] = useState(false);
  const csvImportRateLimit = useRateLimitCooldown("csv-import");
  /** When credentials exist on server, start collapsed unless user chooses to update. */
  const [credentialsEditMode, setCredentialsEditMode] = useState(false);
  const [savedCredentialSnapshot, setSavedCredentialSnapshot] = useState<Record<string, string> | null>(null);
  const [isLoadingCredentialsForm, setIsLoadingCredentialsForm] = useState(false);
  const [removeCredentialsDialogOpen, setRemoveCredentialsDialogOpen] = useState(false);
  const [removeCredentialsConfirmText, setRemoveCredentialsConfirmText] = useState("");
  const [isRemovingCredentials, setIsRemovingCredentials] = useState(false);
  const [removeBrokerDialogOpen, setRemoveBrokerDialogOpen] = useState(false);
  const [removeBrokerConfirmText, setRemoveBrokerConfirmText] = useState("");
  const [isRemovingBroker, setIsRemovingBroker] = useState(false);
  const [binanceSecretsOnFile, setBinanceSecretsOnFile] = useState({
    hasApiKeyOnFile: false,
    hasApiSecretOnFile: false,
  });
  /** Broker Sync = 0, CSV import = 1 when both tabs exist (connect wizard only). */
  const [brokerIntegrationTab, setBrokerIntegrationTab] = useState<"sync" | "csv">("sync");
  const [managementCsvImportFocus, setManagementCsvImportFocus] = useState(false);
  const [binanceHistoryPath, setBinanceHistoryPath] = useState<BinanceHistoryPath | null>(() =>
    loadBinanceHistoryPath()
  );
  const [ibkrHistoryPath, setIbkrHistoryPath] = useState<BrokerHistoryPath | null>(() =>
    loadBrokerHistoryPath("ibkr")
  );
  /** IBKR Flex time-format / timezone issues — inline orange gradient banner (Simulator wizard style), not error toast. */
  const [ibkrFlexFormatBannerMessage, setIbkrFlexFormatBannerMessage] = useState<string | null>(
    null
  );

  const selectedCapability = useMemo(
    () => capabilities.find((item) => item.broker_name === selectedBroker),
    [capabilities, selectedBroker]
  );

  const integratedBrokerSlugs = useMemo(() => {
    const slugs = new Set<string>();
    tradeAccountOptions.forEach((account) => {
      const slug = normalizeBrokerName(account.brokerName || "");
      if (slug) slugs.add(slug);
    });
    return slugs;
  }, [tradeAccountOptions]);

  const registerCredentialBroker = useCallback((brokerName: string) => {
    const slug = normalizeBrokerName(brokerName);
    if (!slug) return;
    setCredentialBrokerSlugs((prev) => {
      if (prev.has(slug)) return prev;
      const next = new Set(prev);
      next.add(slug);
      return next;
    });
  }, []);

  const unregisterCredentialBroker = useCallback((brokerName: string) => {
    const slug = normalizeBrokerName(brokerName);
    if (!slug) return;
    setCredentialBrokerSlugs((prev) => {
      if (!prev.has(slug)) return prev;
      const next = new Set(prev);
      next.delete(slug);
      return next;
    });
  }, []);

  const rememberSyncedAccountCodes = useCallback((...codes: Array<string | null | undefined>) => {
    const nextCodes = codes.map((code) => String(code || "").trim()).filter(Boolean);
    if (!nextCodes.length) return;
    setJustSyncedAccountCodes((prev) => {
      let changed = false;
      const next = new Set(prev);
      for (const code of nextCodes) {
        if (next.has(code)) continue;
        next.add(code);
        changed = true;
      }
      return changed ? next : prev;
    });
  }, []);

  const dropAccountsKeptAfterCredentialRemoval = useCallback((brokerName: string) => {
    const slug = normalizeBrokerName(brokerName);
    if (!slug) return;
    setAccountsKeptAfterCredentialRemoval((prev) => {
      if (!(slug in prev)) return prev;
      const next = { ...prev };
      delete next[slug];
      return next;
    });
  }, []);

  const manageableBrokerSlugs = useMemo(() => {
    const slugs = new Set(integratedBrokerSlugs);
    credentialBrokerSlugs.forEach((slug) => slugs.add(slug));
    Object.keys(accountsKeptAfterCredentialRemoval).forEach((slug) => slugs.add(slug));
    return slugs;
  }, [integratedBrokerSlugs, credentialBrokerSlugs, accountsKeptAfterCredentialRemoval]);

  const manageableBrokers = useMemo(
    () =>
      capabilities
        .filter((broker) =>
          manageableBrokerSlugs.has(normalizeBrokerName(broker.broker_name))
        )
        .sort((a, b) =>
          resolveBrokerDisplayName(a).localeCompare(resolveBrokerDisplayName(b))
        ),
    [capabilities, manageableBrokerSlugs]
  );

  const integratedBrokerAccountCounts = useMemo(() => {
    const counts = new Map<string, number>();
    tradeAccountOptions.forEach((account) => {
      const slug = normalizeBrokerName(account.brokerName || "");
      if (!slug) return;
      counts.set(slug, (counts.get(slug) || 0) + 1);
    });
    Object.entries(accountsKeptAfterCredentialRemoval).forEach(([slug, remnant]) => {
      if (counts.has(slug)) return;
      if (remnant.length > 0) counts.set(slug, remnant.length);
    });
    return counts;
  }, [tradeAccountOptions, accountsKeptAfterCredentialRemoval]);

  const normalizedSelectedBrokerSlug = normalizeBrokerName(selectedBroker || "");

  useEffect(() => {
    if (embeddedInConnectWizard || isLoadingCapabilities) return;
    // Wait for accounts + details so a just-connected broker isn't dropped for Liquid.
    if (tradeAccountsLoading || isLoadingBrokerDetails) return;

    const sp = new URLSearchParams(location.search);
    const stillManageable = manageableBrokers.some(
      (broker) => normalizeBrokerName(broker.broker_name) === normalizedSelectedBrokerSlug
    );
    if (selectedBroker && stillManageable) return;

    const fallback = manageableBrokers[0]?.broker_name ?? "";
    if (normalizeBrokerName(fallback) === normalizedSelectedBrokerSlug) return;
    setSelectedBroker(fallback);

    const urlBroker = (sp.get("broker_name") || sp.get("broker") || "").trim();
    if (urlBroker && normalizeBrokerName(urlBroker) === normalizedSelectedBrokerSlug) {
      const next = new URLSearchParams(location.search);
      next.delete("broker_name");
      next.delete("broker");
      const qs = next.toString();
      history.replace(`${location.pathname}${qs ? `?${qs}` : ""}`);
    }
  }, [
    embeddedInConnectWizard,
    isLoadingCapabilities,
    tradeAccountsLoading,
    isLoadingBrokerDetails,
    manageableBrokers,
    normalizedSelectedBrokerSlug,
    selectedBroker,
    location.search,
    location.pathname,
    history,
  ]);

  useEffect(() => {
    setJustSyncedAccountCodes(new Set());
    setRemoveCredentialsDialogOpen(false);
    setRemoveCredentialsConfirmText("");
    setRemoveBrokerDialogOpen(false);
    setRemoveBrokerConfirmText("");
  }, [selectedBroker]);

  const scrollToBrokerManagement = useCallback(() => {
    brokerManagementTopRef.current?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  }, []);

  const manageableBrokersWithCsvImport = useMemo(
    () =>
      manageableBrokers.filter((broker) => {
        const capability = capabilities.find(
          (item) => item.broker_name === broker.broker_name
        );
        return !!resolveBrokerCsvImport(capability)?.supported;
      }),
    [manageableBrokers, capabilities]
  );

  const [connectWizardOpen, setConnectWizardOpen] = useState(false);
  const [connectWizardSeed, setConnectWizardSeed] =
    useState<ConnectBrokerWizardOpenSeed | null>(null);
  const connectWizardOpenHandledRef = useRef(false);

  const openConnectWizard = useCallback((seed?: ConnectBrokerWizardOpenSeed | null) => {
    setConnectWizardSeed(seed ?? null);
    setConnectWizardOpen(true);
  }, []);

  const handleConnectWizardOpenChange = useCallback((nextOpen: boolean) => {
    setConnectWizardOpen(nextOpen);
    if (!nextOpen) {
      setConnectWizardSeed(null);
    }
  }, []);

  /** Deep link: /profile/broker-api?connect=1&broker=&method= (from /connect redirect). */
  useEffect(() => {
    if (embeddedInConnectWizard) return;
    const parsed = parseConnectBrokerWizardOpenSearch(location.search);
    if (!parsed.shouldOpen) {
      connectWizardOpenHandledRef.current = false;
      return;
    }
    if (connectWizardOpenHandledRef.current) return;
    connectWizardOpenHandledRef.current = true;
    openConnectWizard({
      brokerName: parsed.brokerName,
      importMethod: parsed.importMethod,
    });
    const stripped = stripConnectBrokerWizardOpenParams(location.search);
    history.replace(`${accountSettingsTabPath("broker-api")}${stripped}`);
  }, [embeddedInConnectWizard, history, location.search, openConnectWizard]);

  const handleHeaderConnectNewBroker = useCallback(() => {
    openConnectWizard(null);
  }, [openConnectWizard]);

  useEffect(() => {
    setIbkrFlexFormatBannerMessage(null);
  }, [selectedBroker]);
  const normalizedSelectedBroker = normalizeBrokerName(selectedBroker || "");
  const credentialSyncFlags = useMemo(
    () => parseBrokerCredentialSyncFlags(savedCredentialSnapshot, normalizedSelectedBroker),
    [savedCredentialSnapshot, normalizedSelectedBroker]
  );
  const csvImportOnly = credentialSyncFlags.csvImportOnly;
  const isLiquidBroker = normalizedSelectedBroker === "liquidbrokers";
  const isOandaBroker = normalizedSelectedBroker === "oanda";
  const isIbkrBroker = normalizedSelectedBroker === "ibkr";
  const isTradingViewBrokerSelected = isTradingViewBroker(normalizedSelectedBroker);
  const isTradingViewPaperBrokerSelected = isTradingViewPaperBroker(normalizedSelectedBroker);
  const isHyperliquidBrokerSelected = isHyperliquidBroker(normalizedSelectedBroker);
  const isMt5BrokerSelected = isMt5Broker(normalizedSelectedBroker);
  const isMt4BrokerSelected = isMt4Broker(normalizedSelectedBroker);
  const isFuturesOrdersCsvBrokerSelected = isFuturesOrdersCsvBroker(normalizedSelectedBroker);
  const isStatementImportAccountsBrokerSelected = isStatementImportAccountsBroker(
    normalizedSelectedBroker
  );
  const isCtraderBrokerSelected = isCtraderBroker(normalizedSelectedBroker);
  const isCustomCsvBrokerSelected = isCustomCsvBroker(selectedCapability);
  const showConnectMode = !!selectedCapability?.auth.supports_auto_sync;
  const isCtraderOAuthBroker = isCtraderBrokerSelected && showConnectMode;
  const connectTradedAssetClasses = useMemo(
    () => resolveConnectTradedAssetClasses(selectedCapability),
    [selectedCapability]
  );

  const connectExecutingBroker = useMemo(
    () => resolveConnectExecutingBroker(selectedCapability),
    [selectedCapability]
  );

  const showExecutingBrokerOnConnect = useMemo(
    () => shouldShowExecutingBrokerOnConnect(selectedCapability),
    [selectedCapability]
  );

  const credentialFieldsForForm = (fields: string[]) =>
    fields.filter((f) => {
      if (isTradedAssetClassesField(f)) return false;
      if (isExecutingBrokerNameField(f) && showExecutingBrokerOnConnect) return false;
      return true;
    });

  const isStatementImportBrokerSelected =
    isTradingViewBrokerSelected ||
    isTradingViewPaperBrokerSelected ||
    isHyperliquidBrokerSelected ||
    isMt5BrokerSelected ||
    isMt4BrokerSelected ||
    isFuturesOrdersCsvBrokerSelected ||
    (isCtraderBrokerSelected && !isCtraderOAuthBroker);
  const usesLogicalAccountCode =
    isOandaBroker || isIbkrBroker || isCtraderOAuthBroker;

  const requiredCredentialFields = useMemo(() => {
    const raw = credentialFieldsForForm(
      filterCredentialFields(
        selectedCapability?.auth.required_fields ?? [],
        normalizedSelectedBroker
      )
    );
    if (!isIbkrBroker) return raw;
    return raw.filter((f) => isIbkrConnectCredentialField(f));
  }, [selectedCapability, normalizedSelectedBroker, isIbkrBroker, connectTradedAssetClasses]);
  const optionalCredentialFields = useMemo(() => {
    const raw = credentialFieldsForForm(
      filterCredentialFields(
        selectedCapability?.auth.optional_fields ?? [],
        normalizedSelectedBroker
      )
    );
    if (!isIbkrBroker) return raw;
    return raw.filter((f) => isIbkrConnectCredentialField(f));
  }, [selectedCapability, normalizedSelectedBroker, isIbkrBroker, connectTradedAssetClasses]);
  const isBinanceBroker = normalizedSelectedBroker === "binance";
  const binanceAuthRequiredFields = useMemo(
    () =>
      isBinanceBroker
        ? requiredCredentialFields.filter((f) => !isBinanceSyncCredentialField(f))
        : requiredCredentialFields,
    [isBinanceBroker, requiredCredentialFields]
  );
  const binanceSyncRequiredFields = useMemo(
    () =>
      isBinanceBroker
        ? requiredCredentialFields.filter((f) => isBinanceSpotSymbolsField(f))
        : [],
    [isBinanceBroker, requiredCredentialFields]
  );
  const binanceSyncOptionalFields = useMemo(() => {
    if (!isBinanceBroker) return [];
    const fromRequired = requiredCredentialFields.filter((f) =>
      isBinanceAccountStartDateField(f)
    );
    const fromOptional = optionalCredentialFields.filter((f) =>
      isBinanceAccountStartDateField(f)
    );
    return Array.from(new Set([...fromRequired, ...fromOptional]));
  }, [isBinanceBroker, requiredCredentialFields, optionalCredentialFields]);
  const binanceOptionalAuthFields = useMemo(
    () =>
      isBinanceBroker
        ? optionalCredentialFields.filter((f) => !isBinanceAccountStartDateField(f))
        : optionalCredentialFields,
    [isBinanceBroker, optionalCredentialFields]
  );

  const effectiveCsvImport = useMemo(
    () => resolveBrokerCsvImport(selectedCapability),
    [selectedCapability]
  );

  const csvExecutingBroker = useMemo(
    () => resolveCsvExecutingBroker(selectedCapability, effectiveCsvImport),
    [selectedCapability, effectiveCsvImport]
  );

  const showExecutingBrokerOnCsvImport = useMemo(
    () => shouldShowExecutingBrokerOnCsvImport(selectedCapability, effectiveCsvImport),
    [selectedCapability, effectiveCsvImport]
  );

  const usesMultiAssetClassImport = useMemo(() => {
    const csvSpec = resolveCsvTradedAssetClasses(selectedCapability, effectiveCsvImport);
    return csvSpec?.multi ?? false;
  }, [selectedCapability, effectiveCsvImport]);

  const showCsvMode = useMemo(
    () => !!effectiveCsvImport?.supported,
    [effectiveCsvImport]
  );
  const showBothTabs = showConnectMode && showCsvMode;
  const embeddedWizardForcesSingleTab =
    embeddedInConnectWizard &&
    (embeddedImportMethod === "auto_sync" || embeddedImportMethod === "file_upload");
  const embeddedWizardLocksCsvTab = Boolean(
    embeddedInConnectWizard && embeddedImportMethod === "file_upload"
  );
  const embeddedWizardPrefersCsv = useMemo(
    () =>
      embeddedWizardLocksCsvTab ||
      (embeddedImportMethod === "auto_sync" &&
        ((isBinanceBroker && binanceHistoryPath === "needs_csv_for_history") ||
          (isIbkrBroker && ibkrHistoryPath === "needs_csv_for_history") ||
          (isCtraderOAuthBroker && ctraderHistoryPath === "needs_csv_for_history"))),
    [
      embeddedWizardLocksCsvTab,
      embeddedImportMethod,
      isBinanceBroker,
      binanceHistoryPath,
      isIbkrBroker,
      ibkrHistoryPath,
      isCtraderOAuthBroker,
      ctraderHistoryPath,
    ]
  );
  const embeddedWizardTab: "sync" | "csv" = embeddedWizardPrefersCsv ? "csv" : "sync";
  const showSyncCsvTabSwitcher =
    showBothTabs && embeddedInConnectWizard && !embeddedWizardForcesSingleTab;
  const shouldShowConnectOnDetails =
    showConnectMode &&
    (!showCsvMode ||
      (embeddedInConnectWizard && embeddedWizardTab === "sync") ||
      (!embeddedInConnectWizard && !managementCsvImportFocus));
  const shouldShowCsvOnDetails =
    showCsvMode &&
    (!showConnectMode ||
      (embeddedInConnectWizard && embeddedWizardTab === "csv") ||
      (!embeddedInConnectWizard && managementCsvImportFocus));

  const focusBrokerCsvImport = useCallback(() => {
    if (embeddedInConnectWizard) {
      setBrokerIntegrationTab("csv");
      return;
    }
    setManagementCsvImportFocus(true);
  }, [embeddedInConnectWizard]);

  const chooseOlderHistoryViaCsvImport = useCallback(() => {
    if (embeddedInConnectWizard && embeddedImportMethod === "auto_sync") {
      onEmbeddedWizardImportMethodChange?.("file_upload");
    }
    focusBrokerCsvImport();
  }, [
    embeddedInConnectWizard,
    embeddedImportMethod,
    onEmbeddedWizardImportMethodChange,
    focusBrokerCsvImport,
  ]);

  const handleSelectedBrokerChange = useCallback((broker: string) => {
    setManagementCsvImportFocus(false);
    setSelectedBroker(broker);
  }, []);

  useEffect(() => {
    if (!embeddedInConnectWizard || !embeddedImportMethod) return;
    if (embeddedImportMethod === "file_upload") {
      setBrokerIntegrationTab("csv");
      setManagementCsvImportFocus(false);
      return;
    }
    if (embeddedImportMethod === "auto_sync") {
      setManagementCsvImportFocus(false);
      setBrokerIntegrationTab(embeddedWizardPrefersCsv ? "csv" : "sync");
    }
  }, [
    embeddedImportMethod,
    embeddedInConnectWizard,
    selectedCapability,
    embeddedWizardPrefersCsv,
  ]);

  const scrollToBrokerCsvImport = useCallback(() => {
    csvImportFormRef.current?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  }, []);

  const handleBrokerDetailsImportTrades = useCallback(() => {
    if (!selectedBroker) return;

    const capability =
      capabilities.find((item) => item.broker_name === selectedBroker) ?? null;
    const csvSupported =
      !!resolveBrokerCsvImport(capability)?.supported || isCustomCsvBroker(capability);

    if (!csvSupported) {
      toast.info("CSV import unavailable", {
        description: `${resolveBrokerDisplayName(capability)} does not support CSV import in Tessera.`,
      });
      return;
    }

    openConnectWizard({
      brokerName: selectedBroker,
      importMethod: "file_upload",
    });
  }, [capabilities, openConnectWizard, selectedBroker]);

  const brokerManagementHeaderToolbar = useMemo(
    () => (
      <BrokerManagementHeaderActions
        onConnectNewBroker={handleHeaderConnectNewBroker}
        connectDisabled={isLoadingCapabilities}
      />
    ),
    [handleHeaderConnectNewBroker, isLoadingCapabilities]
  );

  const showBrokerDetailsImportTrades = Boolean(
    !embeddedInConnectWizard && selectedCapability && showCsvMode
  );

  useEffect(() => {
    // Embedded connect-wizard instance must not clear the management breadcrumb toolbar.
    if (embeddedInConnectWizard) {
      return;
    }
    if (!shadcnInset) {
      setInsetHeaderActions(null);
      return;
    }
    setInsetHeaderActions(brokerManagementHeaderToolbar);
    return () => setInsetHeaderActions(null);
  }, [
    brokerManagementHeaderToolbar,
    embeddedInConnectWizard,
    setInsetHeaderActions,
    shadcnInset,
  ]);

  const csvBrokerAccounts = useMemo(
    () =>
      tradeAccountOptions.filter(
        (a) => normalizeBrokerName(a.brokerName) === normalizedSelectedBroker
      ),
    [tradeAccountOptions, normalizedSelectedBroker]
  );

  const ctraderOpenApiSync = useMemo(
    () => (isCtraderOAuthBroker ? resolveCtraderOpenApiSync(selectedCapability) : null),
    [isCtraderOAuthBroker, selectedCapability]
  );

  const ctraderBrokerTradeSync = useMemo(
    () =>
      isCtraderOAuthBroker && selectedCapability?.trade_sync
        ? resolveBrokerTradeSync(selectedCapability)
        : null,
    [isCtraderOAuthBroker, selectedCapability]
  );

  const binanceTradeSync = useMemo(
    () => (isBinanceBroker ? resolveBinanceTradeSync(selectedCapability) : null),
    [isBinanceBroker, selectedCapability]
  );

  const brokerTradeSync = useMemo(
    () =>
      !isBinanceBroker && selectedCapability?.trade_sync
        ? resolveBrokerTradeSync(selectedCapability)
        : null,
    [isBinanceBroker, selectedCapability]
  );

  const ibkrAccountInferred = useMemo(
    () =>
      isIbkrBroker &&
      !!(
        selectedCapability?.auth?.account_code_inferred_from_report ||
        brokerTradeSync?.accountCodeInferredFromReport
      ),
    [isIbkrBroker, selectedCapability, brokerTradeSync]
  );

  const credentialLabel = (field: string): string =>
    brokerTradeSyncCredentialLabel(brokerTradeSync, field, makeLabel(field));

  const csvFieldLabel = (field: string): string => {
    const fallback = makeLabel(field);
    if (isTradingViewBrokerSelected) {
      return tradingViewCsvFieldLabel(field, fallback);
    }
    if (isTradingViewPaperBrokerSelected) {
      return tradingViewPaperCsvFieldLabel(field, fallback);
    }
    if (isHyperliquidBrokerSelected) {
      return hyperliquidCsvFieldLabel(field, fallback);
    }
    if (isMt5BrokerSelected) {
      return mt5ImportFieldLabel(field, fallback);
    }
    if (isMt4BrokerSelected) {
      return mt4ImportFieldLabel(field, fallback);
    }
    if (isFuturesOrdersCsvBrokerSelected) {
      return futuresOrdersCsvImportFieldLabel(field, fallback);
    }
    if (isCtraderBrokerSelected) {
      return ctraderImportFieldLabel(field, fallback);
    }
    return brokerCsvImportFieldLabel(effectiveCsvImport, field, fallback);
  };

  const csvRequiredFields = useMemo(
    () =>
      effectiveCsvImport?.requiredFields.length
        ? effectiveCsvImport.requiredFields
        : selectedCapability?.csv_import?.required_fields ??
          selectedCapability?.xlsx_import?.required_fields ??
          selectedCapability?.html_import?.required_fields ??
          [],
    [effectiveCsvImport, selectedCapability]
  );

  const ibkrCsvFormFields = useMemo(() => {
    if (!isIbkrBroker) return csvRequiredFields;
    return csvRequiredFields.filter(
      (f) => f !== "account_code" && f !== "statement_time_zone"
    );
  }, [isIbkrBroker, csvRequiredFields]);

  const tradingViewCsvFormFields = useMemo(() => {
    if (!isTradingViewBrokerSelected) return [];
    return csvRequiredFields.filter((f) => !isTradingViewHiddenCsvField(f));
  }, [isTradingViewBrokerSelected, csvRequiredFields]);

  const tradingViewPaperCsvFormFields = useMemo(() => {
    if (!isTradingViewPaperBrokerSelected) return [];
    return csvRequiredFields.filter((f) => !isTradingViewPaperHiddenCsvField(f));
  }, [isTradingViewPaperBrokerSelected, csvRequiredFields]);

  const mt5ImportFormFields = useMemo(() => {
    if (!isMt5BrokerSelected) return [];
    return csvRequiredFields.filter((f) => !isMt5HiddenImportField(f));
  }, [isMt5BrokerSelected, csvRequiredFields]);

  const mt4ImportFormFields = useMemo(() => {
    if (!isMt4BrokerSelected) return [];
    return csvRequiredFields.filter((f) => !isMt4HiddenImportField(f));
  }, [isMt4BrokerSelected, csvRequiredFields]);

  const futuresOrdersImportFormFields = useMemo(() => {
    if (!isFuturesOrdersCsvBrokerSelected) return [];
    return csvRequiredFields.filter((f) => !isFuturesOrdersCsvHiddenImportField(f));
  }, [isFuturesOrdersCsvBrokerSelected, csvRequiredFields]);

  const ctraderImportFormFields = useMemo(() => {
    if (!isCtraderBrokerSelected) return [];
    return csvRequiredFields.filter(
      (f) => !isCtraderHiddenImportField(f) && !isCtraderUtcOffsetField(f)
    );
  }, [isCtraderBrokerSelected, csvRequiredFields]);

  const hyperliquidImportFormFields = useMemo(() => {
    if (!isHyperliquidBrokerSelected) return [];
    return csvRequiredFields.filter((f) => !isHyperliquidHiddenCsvField(f));
  }, [isHyperliquidBrokerSelected, csvRequiredFields]);

  const ctraderShowUtcOffsetField = useMemo(() => {
    if (!isCtraderBrokerSelected) return false;
    const optional =
      effectiveCsvImport?.optionalFields ??
      selectedCapability?.csv_import?.optional_fields ??
      [];
    return optional.some((f) => isCtraderUtcOffsetField(f));
  }, [isCtraderBrokerSelected, effectiveCsvImport, selectedCapability]);

  const activeCsvFormFields = useMemo(() => {
    if (isIbkrBroker) return ibkrCsvFormFields;
    if (isTradingViewBrokerSelected) return tradingViewCsvFormFields;
    if (isTradingViewPaperBrokerSelected) return tradingViewPaperCsvFormFields;
    if (isMt5BrokerSelected) return mt5ImportFormFields;
    if (isMt4BrokerSelected) return mt4ImportFormFields;
    if (isFuturesOrdersCsvBrokerSelected) return futuresOrdersImportFormFields;
    if (isCtraderBrokerSelected) return ctraderImportFormFields;
    if (isHyperliquidBrokerSelected) return hyperliquidImportFormFields;
    return csvRequiredFields;
  }, [
    isIbkrBroker,
    isTradingViewBrokerSelected,
    isTradingViewPaperBrokerSelected,
    isHyperliquidBrokerSelected,
    isMt5BrokerSelected,
    isMt4BrokerSelected,
    isFuturesOrdersCsvBrokerSelected,
    isCtraderBrokerSelected,
    ibkrCsvFormFields,
    tradingViewCsvFormFields,
    tradingViewPaperCsvFormFields,
    hyperliquidImportFormFields,
    mt5ImportFormFields,
    mt4ImportFormFields,
    futuresOrdersImportFormFields,
    ctraderImportFormFields,
    csvRequiredFields,
  ]);

  const csvAllowedAssetClasses = useMemo(() => {
    const fromTraded = resolveCsvTradedAssetClasses(selectedCapability, effectiveCsvImport);
    if (fromTraded?.allowedAssetClasses.length) {
      return fromTraded.allowedAssetClasses;
    }
    const fromCaps =
      effectiveCsvImport?.allowedAssetClasses ??
      selectedCapability?.csv_import?.allowed_asset_classes ??
      selectedCapability?.xlsx_import?.allowed_asset_classes ??
      selectedCapability?.html_import?.allowed_asset_classes ??
      [];
    if (fromCaps.length > 0) return fromCaps;
    if (isMt5BrokerSelected) return [...MT5_DEFAULT_ASSET_CLASSES];
    if (isMt4BrokerSelected) return [...MT4_DEFAULT_ASSET_CLASSES];
    if (isCtraderBrokerSelected) return [...CTRADER_DEFAULT_ASSET_CLASSES];
    return [...TRADINGVIEW_DEFAULT_ASSET_CLASSES];
  }, [
    effectiveCsvImport,
    selectedCapability,
    isMt5BrokerSelected,
    isMt4BrokerSelected,
    isCtraderBrokerSelected,
  ]);

  const futuresOrdersShowCashHistoryUpload = isFuturesOrdersCsvBrokerSelected;

  const futuresOrdersFixedAssetClassesLabel = useMemo(() => {
    if (!isFuturesOrdersCsvBrokerSelected) return null;
    const fromConnect = connectTradedAssetClasses?.fixedValues;
    if (fromConnect?.length && !connectTradedAssetClasses.userSelectable) {
      return formatTradedAssetClassesReadOnly(fromConnect);
    }
    const fromCaps =
      effectiveCsvImport?.fixedAssetClasses ??
      selectedCapability?.auth?.fixed_traded_asset_classes ??
      selectedCapability?.csv_import?.fixed_traded_asset_classes ??
      selectedCapability?.html_import?.fixed_traded_asset_classes ??
      ["futures"];
    return formatFuturesOrdersFixedAssetClassesLabel(fromCaps);
  }, [
    isFuturesOrdersCsvBrokerSelected,
    effectiveCsvImport,
    selectedCapability,
    connectTradedAssetClasses,
  ]);

  const futuresOrdersPlatformName = futuresOrdersPlatformDisplayName(normalizedSelectedBroker);

  const csvFieldHint = (field: string): string => {
    const metaHint = getFieldMeta(normalizedSelectedBroker, field).hint;
    if (metaHint) return metaHint;
    if (
      isTradingViewBrokerSelected ||
      isTradingViewPaperBrokerSelected ||
      isHyperliquidBrokerSelected ||
      isMt5BrokerSelected ||
      isMt4BrokerSelected ||
      isFuturesOrdersCsvBrokerSelected ||
      isCtraderBrokerSelected
    ) {
      return "";
    }
    const fromApi = effectiveCsvImport?.fieldLabels?.[field];
    if (fromApi && fromApi.trim() !== csvFieldLabel(field)) {
      return fromApi.trim();
    }
    return "";
  };

  const hasExistingBinanceIntegration = useMemo(
    () =>
      isBinanceBroker &&
      (credentialsSaved || integratedBrokerSlugs.has("binance")),
    [isBinanceBroker, credentialsSaved, integratedBrokerSlugs]
  );

  const hasExistingCtraderIntegration = useMemo(
    () =>
      isCtraderOAuthBroker &&
      (credentialsSaved || integratedBrokerSlugs.has("ctrader")),
    [isCtraderOAuthBroker, credentialsSaved, integratedBrokerSlugs]
  );

  const showBinanceHistoryChoice = Boolean(
    isBinanceBroker &&
      binanceTradeSync?.historyBeforeApiRequiresCsvImport &&
      !binanceHistoryPath &&
      !(embeddedInConnectWizard && embeddedImportMethod === "file_upload") &&
      hasLoadedBrokerDetailsOnce &&
      !hasExistingBinanceIntegration
  );

  const showIbkrHistoryChoice = Boolean(
    isIbkrBroker &&
      brokerTradeSync?.historyBeforeSyncRequiresCsvImport &&
      !ibkrHistoryPath &&
      !(embeddedInConnectWizard && embeddedImportMethod === "file_upload")
  );

  const binanceSetupComplete = Boolean(
    !binanceTradeSync?.historyBeforeApiRequiresCsvImport || binanceHistoryPath
  );

  const ibkrSetupComplete = Boolean(
    !brokerTradeSync?.historyBeforeSyncRequiresCsvImport || ibkrHistoryPath
  );

  const showCtraderHistoryChoice = Boolean(
    isCtraderOAuthBroker &&
      ctraderOpenApiSync?.historyBeforeSyncRequiresCsvImport &&
      !ctraderHistoryPath &&
      !(embeddedInConnectWizard && embeddedImportMethod === "file_upload") &&
      hasLoadedBrokerDetailsOnce &&
      !hasExistingCtraderIntegration
  );

  const ctraderSetupComplete = Boolean(
    !ctraderOpenApiSync?.historyBeforeSyncRequiresCsvImport || ctraderHistoryPath
  );
  const dualModeSetupComplete =
    !showBothTabs ||
    ((binanceSetupComplete || !isBinanceBroker) &&
      (ibkrSetupComplete || !isIbkrBroker) &&
      (ctraderSetupComplete || !isCtraderOAuthBroker));

  const hideEmbeddedAutoSyncCredentialActions = Boolean(
    embeddedInConnectWizard &&
      embeddedImportMethod === "auto_sync" &&
      !isCtraderOAuthBroker &&
      dualModeSetupComplete &&
      shouldShowConnectOnDetails
  );

  const showEmbeddedWizardSaveFooter = Boolean(
    hideEmbeddedAutoSyncCredentialActions &&
      !credentialsSaved &&
      !(isBinanceBroker && hasExistingBinanceIntegration)
  );

  /** Credentials already on file — still offer Sync (wizard otherwise has no Sync CTA). */
  const showEmbeddedWizardSyncFooter = Boolean(
    hideEmbeddedAutoSyncCredentialActions &&
      credentialsSaved &&
      !credentialsEditMode &&
      !(isBinanceBroker && hasExistingBinanceIntegration)
  );

  const showEmbeddedWizardCsvImportFooter = Boolean(
    embeddedInConnectWizard &&
      embeddedImportMethod === "file_upload" &&
      !isCustomCsvBrokerSelected
  );

  const showEmbeddedWizardCtraderConnectFooter = Boolean(
    embeddedInConnectWizard &&
      embeddedImportMethod === "auto_sync" &&
      isCtraderOAuthBroker &&
      ctraderSetupComplete &&
      shouldShowConnectOnDetails &&
      !credentialsSaved
  );

  /** Connect wizard: broker sync credentials (or linked accounts) already exist. */
  const embeddedAutoSyncAlreadyComplete = Boolean(
    embeddedInConnectWizard &&
      embeddedImportMethod === "auto_sync" &&
      hasLoadedBrokerDetailsOnce &&
      !isLoadingBrokerDetails &&
      (credentialsSaved ||
        hasExistingBinanceIntegration ||
        hasExistingCtraderIntegration ||
        (showConnectMode &&
          !!normalizedSelectedBrokerSlug &&
          integratedBrokerSlugs.has(normalizedSelectedBrokerSlug)))
  );

  const embeddedAutoSyncCanOfferFileImport = Boolean(
    embeddedAutoSyncAlreadyComplete && showCsvMode
  );

  const embeddedCsvImportFooterLabel = useMemo(() => {
    if (csvImportRateLimit.isCoolingDown) {
      return `Wait ${csvImportRateLimit.retryAfterSeconds}s`;
    }
    if (isMt5BrokerSelected) return "Import XLSX";
    if (isMt4BrokerSelected) return "Import HTML";
    return "Import CSV";
  }, [
    csvImportRateLimit.isCoolingDown,
    csvImportRateLimit.retryAfterSeconds,
    isMt4BrokerSelected,
    isMt5BrokerSelected,
  ]);

  const savedNonSecretSummary = useMemo(() => {
    if (!savedCredentialSnapshot) return null;
    if (normalizedSelectedBroker === "binance") {
      const form = snapshotToBinanceCredentialForm(savedCredentialSnapshot);
      const pairCount = (form.binance_spot_symbols || "")
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean).length;
      const start = form.binance_account_start_date || "";
      if (pairCount > 0 && start) {
        return `${pairCount} trading pair(s), sync from ${start}`;
      }
      if (pairCount > 0) return `${pairCount} trading pair(s)`;
      if (start) return `Sync from ${start}`;
      return null;
    }
    if (isCtraderOAuthBroker) {
      const ctid = pickFirstNonEmptyTrimmed(
        savedCredentialSnapshot.account_code,
        savedCredentialSnapshot.accountCode
      );
      const display = ctraderDisplayFromCredentials(savedCredentialSnapshot, ctid);
      const classes = parseCtraderSavedAssetClasses(savedCredentialSnapshot);
      const env = parseCtraderEnvironment(savedCredentialSnapshot);
      const summary = display
        ? formatCtraderConnectedSummary({
            ...display,
            environment: display.environment ?? env,
          })
        : ctid
          ? `ctid ${ctid}${env ? ` (${env})` : ""}`
          : `cTrader connected${env ? ` (${env})` : ""}`;
      if (classes.length) {
        return `${summary} · ${classes.map(formatAssetClassLabel).join(", ")}`;
      }
      return summary;
    }
    const u =
      savedCredentialSnapshot.username ||
      savedCredentialSnapshot.Username ||
      savedCredentialSnapshot.user_name ||
      "";
    const trimmed = String(u).trim();
    return trimmed || null;
  }, [savedCredentialSnapshot, normalizedSelectedBroker, isCtraderOAuthBroker]);

  /** OANDA / IBKR: logical `account_code` from saved creds (no broker account list). */
  const syncAccountCodeForTrades = useMemo(() => {
    if (normalizedSelectedBroker === "oanda") {
      const fromState = (accountCode || "").trim();
      if (fromState) return fromState;
      if (!savedCredentialSnapshot) return "";
      return pickFirstNonEmptyTrimmed(
        savedCredentialSnapshot.account_code,
        savedCredentialSnapshot.accountCode,
        savedCredentialSnapshot.oanda_account_id,
        savedCredentialSnapshot.oandaAccountId
      );
    }
    if (normalizedSelectedBroker === "ibkr") {
      const fromState = (accountCode || "").trim();
      if (fromState) return fromState;
      if (!savedCredentialSnapshot) return "";
      return pickFirstNonEmptyTrimmed(
        savedCredentialSnapshot.account_code,
        savedCredentialSnapshot.accountCode
      );
    }
    if (isCtraderBroker(normalizedSelectedBroker)) {
      const fromState = (accountCode || "").trim();
      if (fromState) return fromState;
      if (!savedCredentialSnapshot) return "";
      return pickFirstNonEmptyTrimmed(
        savedCredentialSnapshot.account_code,
        savedCredentialSnapshot.accountCode
      );
    }
    return (accountCode || "").trim();
  }, [normalizedSelectedBroker, accountCode, savedCredentialSnapshot]);

  const ctraderDisplayForSync = useMemo(() => {
    if (!isCtraderBroker(normalizedSelectedBroker) || !syncAccountCodeForTrades) return null;
    return ctraderDisplayFromCredentials(savedCredentialSnapshot, syncAccountCodeForTrades);
  }, [normalizedSelectedBroker, syncAccountCodeForTrades, savedCredentialSnapshot]);

  const ctraderSyncHint = useMemo(
    () => (ctraderDisplayForSync ? formatCtraderSyncHint(ctraderDisplayForSync) : null),
    [ctraderDisplayForSync]
  );
  const ctraderDiscoveredAccounts = useMemo(() => {
    const fromHelpers = ctraderFoundAccounts.filter(
      (a) => normalizeBrokerName(a.brokerName) === "ctrader"
    );
    if (fromHelpers.length > 0) return fromHelpers;
    return tradeAccountOptions.filter(
      (a) => normalizeBrokerName(a.brokerName) === "ctrader"
    );
  }, [ctraderFoundAccounts, tradeAccountOptions]);
  const shouldForceCtraderAccountPicker =
    isCtraderOAuthBroker && hasMultipleCtraderAccounts && ctraderDiscoveredAccounts.length > 1;
  const brokerTradeAccountsForSelectedBroker = useMemo(() => {
    const fromApi = tradeAccountOptions.filter(
      (account) => normalizeBrokerName(account.brokerName) === normalizedSelectedBroker
    );
    if (fromApi.length > 0) return fromApi;
    return accountsKeptAfterCredentialRemoval[normalizedSelectedBroker] ?? [];
  }, [
    tradeAccountOptions,
    normalizedSelectedBroker,
    accountsKeptAfterCredentialRemoval,
  ]);

  /** Live sub-accounts from GET /broker/accounts (e.g. Liquid) — show under Available until first sync. */
  const discoveredAccountsFromBrokerApi = useMemo((): TradeAccountOption[] => {
    if (!showConnectMode || !credentialsSaved || accounts.length === 0) return [];
    const displayName =
      (selectedCapability && resolveBrokerDisplayName(selectedCapability)) ||
      normalizedSelectedBroker;
    const existingByCode = new Map(
      brokerTradeAccountsForSelectedBroker.map((account) => [
        account.accountCode.trim(),
        account,
      ])
    );
    return accounts
      .filter((account) => Boolean(account.accountCode?.trim()))
      .map((account) => {
        const code = account.accountCode.trim();
        const existing = existingByCode.get(code);
        return {
          accountCode: code,
          accountStatus: account.accountStatus || "",
          baseCurrency: account.baseCurrency || "",
          balance: account.balance ?? null,
          isDemo: account.isDemo,
          customName: existing?.customName ?? account.customName ?? null,
          brokerName: normalizedSelectedBroker,
          brokerDisplayName: displayName,
          tradeLabel: existing?.tradeLabel || account.customName?.trim() || code,
          accountSource: existing?.accountSource || "broker_list",
          lastBrokerSyncAt: existing?.lastBrokerSyncAt ?? null,
          lastCsvImportAt: existing?.lastCsvImportAt ?? null,
        };
      });
  }, [
    accounts,
    brokerTradeAccountsForSelectedBroker,
    credentialsSaved,
    normalizedSelectedBroker,
    selectedCapability,
    showConnectMode,
  ]);

  const brokerAccountRowsInput = useMemo(
    () => ({
      brokerSlug: normalizedSelectedBroker,
      tradeAccounts: brokerTradeAccountsForSelectedBroker,
      logicalAccountCode: usesLogicalAccountCode ? syncAccountCodeForTrades : null,
      logicalAccountDisplayName:
        isCtraderOAuthBroker && ctraderSyncHint
          ? ctraderSyncHint.headline
          : syncAccountCodeForTrades || null,
      includeDiscoveredAccounts: shouldForceCtraderAccountPicker
        ? ctraderDiscoveredAccounts
        : discoveredAccountsFromBrokerApi,
      forceConnectedAccountCodes: [
        ...Array.from(justSyncedAccountCodes),
        ...(accountsKeptAfterCredentialRemoval[normalizedSelectedBroker] ?? []).map(
          (account) => account.accountCode
        ),
      ],
    }),
    [
      ctraderDiscoveredAccounts,
      ctraderSyncHint,
      discoveredAccountsFromBrokerApi,
      isCtraderOAuthBroker,
      justSyncedAccountCodes,
      normalizedSelectedBroker,
      shouldForceCtraderAccountPicker,
      syncAccountCodeForTrades,
      brokerTradeAccountsForSelectedBroker,
      usesLogicalAccountCode,
      accountsKeptAfterCredentialRemoval,
    ]
  );

  const connectedAccountsForTable = useMemo(
    () => buildBrokerConnectedAccountRows(brokerAccountRowsInput),
    [brokerAccountRowsInput]
  );

  const availableAccountsForTable = useMemo(
    () => buildBrokerAvailableAccountRows(brokerAccountRowsInput),
    [brokerAccountRowsInput]
  );

  const showAvailableAccountsSection = useMemo(
    () =>
      showConnectMode &&
      credentialsSaved &&
      availableAccountsForTable.length > 0,
    [showConnectMode, credentialsSaved, availableAccountsForTable.length]
  );

  const brokerDetailsResyncAccounts = useMemo(
    () =>
      tradeAccountOptions.filter(
        (acc) =>
          normalizeBrokerName(acc.brokerName) === normalizedSelectedBroker &&
          canResyncTradeAccountForUi(acc, showConnectMode)
      ),
    [tradeAccountOptions, normalizedSelectedBroker, showConnectMode]
  );

  const showBrokerDetailsResyncTrades = useMemo(
    () => {
      if (
        embeddedInConnectWizard ||
        !showConnectMode ||
        !credentialsSaved ||
        isCustomCsvBrokerSelected
      ) {
        return false;
      }
      // Liquid (and similar multi-account live brokers): sync per account row only.
      // Header Resync is only for a single already-connected account.
      if (isLiquidBroker) {
        if (availableAccountsForTable.length > 0) return false;
        if (connectedAccountsForTable.length > 1) return false;
        return (
          connectedAccountsForTable.length === 1 || brokerDetailsResyncAccounts.length === 1
        );
      }
      return (
        brokerDetailsResyncAccounts.length > 0 ||
        isIbkrBroker ||
        isBinanceBroker ||
        Boolean(syncAccountCodeForTrades.trim())
      );
    },
    [
      embeddedInConnectWizard,
      showConnectMode,
      credentialsSaved,
      isCustomCsvBrokerSelected,
      isLiquidBroker,
      availableAccountsForTable.length,
      connectedAccountsForTable.length,
      brokerDetailsResyncAccounts.length,
      isIbkrBroker,
      isBinanceBroker,
      syncAccountCodeForTrades,
    ]
  );

  const brokerDetailsHeaderActionsDisabled =
    isLoadingCapabilities || isLoadingBrokerDetails;

  useEffect(() => {
    if (!shouldForceCtraderAccountPicker) return;
    const current = (accountCode || "").trim();
    if (current && ctraderDiscoveredAccounts.some((a) => a.accountCode === current)) return;
    const activeCtrader =
      activeBrokerName && normalizeBrokerName(activeBrokerName) === "ctrader" && activeAccountCode
        ? activeAccountCode
        : "";
    if (activeCtrader && ctraderDiscoveredAccounts.some((a) => a.accountCode === activeCtrader)) {
      setAccountCode(activeCtrader);
      return;
    }
    setAccountCode(ctraderDiscoveredAccounts[0]?.accountCode || "");
  }, [
    shouldForceCtraderAccountPicker,
    ctraderDiscoveredAccounts,
    accountCode,
    activeBrokerName,
    activeAccountCode,
  ]);

  const loadCapabilities = async () => {
    setIsLoadingCapabilities(true);
    try {
      const list = await fetchBrokerCapabilities();
      setCapabilities(list);
      if (embeddedInConnectWizard && embeddedBrokerName) {
        setSelectedBroker(embeddedBrokerName);
      } else if (list.length > 0) {
        const sp = new URLSearchParams(
          typeof window !== "undefined" ? window.location.search : location.search
        );
        const explicitBrokerParam = (sp.get("broker_name") || sp.get("broker") || "").trim();
        const onBrokerManagement =
          location.pathname.includes("/profile/broker-api") || sp.get("tab") === "broker-api";
        const pick = resolveProfileBrokerFromCapabilities(list, {
          search: typeof window !== "undefined" ? window.location.search : location.search,
          pathname: location.pathname,
          activeBrokerName,
        });
        // On Broker Management, only pre-select from a deep-link param — not active account /
        // first capability — so we do not fight manageableBrokers sync while trade accounts load.
        if (pick && (!onBrokerManagement || explicitBrokerParam)) {
          setSelectedBroker(pick);
        }
      }
    } catch (error: any) {
      presentMutationError(error, "Failed to load brokers");
    } finally {
      setIsLoadingCapabilities(false);
      setCapabilitiesHydrated(true);
    }
  };

  const loadBrokerDetails = async (brokerName: string) => {
    if (!brokerName) return;
    const loadId = ++brokerDetailsLoadIdRef.current;
    const normalizedBroker = normalizeBrokerName(brokerName);
    const isLiquidForSelection = normalizedBroker === "liquidbrokers";
    const brokerCap = capabilities.find(
      (b) => normalizeBrokerName(b.broker_name) === normalizedBroker
    );
    const ctraderOAuthEnabled =
      isCtraderBroker(normalizedBroker) && !!brokerCap?.auth?.supports_auto_sync;
    const supportsLiveAccountList = brokerSupportsLiveBrokerAccountsApi(brokerCap);
    const skipCredentialsPrefetch = brokerSkipsCredentialsPrefetch(brokerCap, normalizedBroker);
    const skipAccountList =
      !supportsLiveAccountList ||
      normalizedBroker === "oanda" ||
      normalizedBroker === "ibkr" ||
      isTradingViewBroker(normalizedBroker) ||
      isTradingViewPaperBroker(normalizedBroker) ||
      isHyperliquidBroker(normalizedBroker) ||
      isMt5Broker(normalizedBroker) ||
      isMt4Broker(normalizedBroker) ||
      isFuturesOrdersCsvBroker(normalizedBroker) ||
      (isCtraderBroker(normalizedBroker) && !ctraderOAuthEnabled);
    setIsLoadingBrokerDetails(true);
    setConnectAssetClasses([]);
    setExecutingBrokerName("");
    setTestResult("");
    setTestResultAction("");
    setCredentialsTestPassed(false);
    setCsvFile(null);
    setFuturesOrdersCashHistoryFile(null);

    try {
      // CSV-only lanes (Paper, Backtest, Hyperliquid, MT4/5, …): no live login to hydrate.
      // Credentials appear after first import (or optional save) — do not GET /credentials on open.
      // For live brokers, null credentials = not connected yet (not an error).
      const credentials = skipCredentialsPrefetch
        ? null
        : await getBrokerCredentials(normalizedBroker);
      const syncFlags = skipCredentialsPrefetch
        ? {
            hasCredentialRecord: false,
            supportsLiveSync: false,
            csvImportOnly: true,
          }
        : parseBrokerCredentialSyncFlags(credentials, normalizedBroker);
      // Live account list (Liquid, etc.): still fetch when the broker supports auto-sync,
      // even if the credentials probe was empty this pass — otherwise Available stays blank.
      const shouldFetchBrokerAccounts =
        !skipAccountList &&
        (syncFlags.supportsLiveSync ||
          (supportsLiveAccountList && brokerCap?.auth?.supports_auto_sync === true));
      const brokerAccounts = shouldFetchBrokerAccounts
        ? await fetchBrokerAccounts(normalizedBroker).catch(() => [])
        : ([] as BrokerAccount[]);

      if (loadId !== brokerDetailsLoadIdRef.current) return;

      const hasCreds = !!credentials;
      setCredentialsSaved(hasCreds);
      if (hasCreds) {
        registerCredentialBroker(normalizedBroker);
        dropAccountsKeptAfterCredentialRemoval(normalizedBroker);
      } else {
        unregisterCredentialBroker(normalizedBroker);
      }
      setCredentialsSaved(syncFlags.supportsLiveSync);
      setSavedCredentialSnapshot(credentials ? { ...credentials } : null);
      const keepBrokerWithoutCredentials =
        normalizedBroker in accountsKeptAfterCredentialRemoval;
      setCredentialsEditMode(
        !(syncFlags.supportsLiveSync || keepBrokerWithoutCredentials)
      );
      setCredentialValues({});
      setBinanceSecretsOnFile({ hasApiKeyOnFile: false, hasApiSecretOnFile: false });
      setAccounts(brokerAccounts);

      const savedAccountCode = pickFirstNonEmptyTrimmed(
        credentials?.account_code,
        credentials?.accountCode,
        credentials?.oanda_account_id,
        credentials?.oandaAccountId
      );
      const accountList =
        isLiquidForSelection || isCtraderBroker(normalizedBroker)
          ? brokerAccounts
          : brokerAccounts.slice(0, 1);
      const nextAccountCode = savedAccountCode || accountList[0]?.accountCode || "";
      setAccountCode(nextAccountCode);
      setAccounts(accountList);

      if (
        syncFlags.supportsLiveSync &&
        !isLiquidForSelection &&
        accountList.length === 1 &&
        nextAccountCode
      ) {
        try {
          await setActiveBrokerAccount(normalizedBroker, nextAccountCode);
        } catch {
          // Non-blocking — sync still sends account_code explicitly.
        }
      }

      if (
        isTradingViewBroker(normalizedBroker) ||
        isTradingViewPaperBroker(normalizedBroker) ||
        isHyperliquidBroker(normalizedBroker) ||
        isMt5Broker(normalizedBroker) ||
        isMt4Broker(normalizedBroker) ||
        isFuturesOrdersCsvBroker(normalizedBroker)
      ) {
        setCsvValues({});
        setCsvAssetClasses([]);
      } else if (isCtraderBroker(normalizedBroker)) {
        const savedClasses = parseSavedTradedAssetClasses(credentials);
        const savedEnv = parseCtraderEnvironment(credentials);
        const savedExecuting = parseSavedExecutingBrokerName(credentials);
        setCsvValues({
          account_code: nextAccountCode,
          ...(savedExecuting ? { executing_broker_name: savedExecuting } : {}),
        });
        setCsvAssetClasses(savedClasses);
        if (ctraderOAuthEnabled) {
          setCtraderConnectEnvironment(savedEnv ?? "demo");
        }
      } else if (normalizedBroker === "ibkr") {
        setCsvValues({});
        setCsvAssetClasses([]);
      } else if (csvRequiredFields.includes("account_code")) {
        setCsvValues((prev) => ({
          ...prev,
          account_code: nextAccountCode,
        }));
        setCsvAssetClasses([]);
      } else {
        setCsvValues({});
        setCsvAssetClasses([]);
      }

      const connectSpec = resolveConnectTradedAssetClasses(brokerCap);
      const savedConnectClasses = parseSavedTradedAssetClasses(credentials);
      if (connectSpec?.userSelectable) {
        setConnectAssetClasses(savedConnectClasses.length > 0 ? savedConnectClasses : []);
      } else if (connectSpec?.fixedValues?.length) {
        setConnectAssetClasses(connectSpec.fixedValues);
      } else {
        setConnectAssetClasses([]);
      }

      const savedExecuting = parseSavedExecutingBrokerName(credentials);
      const defaultExecuting =
        !savedExecuting && isTradingViewPaperBroker(normalizedBroker)
          ? TRADINGVIEW_PAPER_DEFAULT_EXECUTING_BROKER
          : savedExecuting;
      setExecutingBrokerName(defaultExecuting);
      if (defaultExecuting && isStatementImportPlatform(normalizedBroker)) {
        setCsvValues((prev) => ({ ...prev, executing_broker_name: defaultExecuting }));
      }
    } catch (error: any) {
      presentMutationError(error, "Couldn't load broker setup");
    } finally {
      if (loadId === brokerDetailsLoadIdRef.current) {
        setIsLoadingBrokerDetails(false);
        setHasLoadedBrokerDetailsOnce(true);
      }
    }
  };

  useEffect(() => {
    loadCapabilities();
  }, []);

  useEffect(() => {
    if (!embeddedInConnectWizard || !embeddedBrokerName) return;
    setSelectedBroker(embeddedBrokerName);
  }, [embeddedBrokerName, embeddedInConnectWizard]);

  /** In-app navigation to Broker Management (e.g. Import from Trade Log) may arrive after mount. */
  useEffect(() => {
    if (isLoadingCapabilities || !capabilities.length) return;
    const sp = new URLSearchParams(location.search);
    const onBrokerTab =
      location.pathname.includes("/profile/broker-api") || sp.get("tab") === "broker-api";
    if (!onBrokerTab) return;
    const explicitBrokerParam = (sp.get("broker_name") || sp.get("broker") || "").trim();
    const shouldAutoPick =
      !!explicitBrokerParam ||
      (!selectedBroker && (embeddedInConnectWizard || manageableBrokers.length > 0));
    if (!shouldAutoPick) return;
    const pick = resolveProfileBrokerFromCapabilities(capabilities, {
      search: location.search,
      pathname: location.pathname,
      activeBrokerName,
    });
    if (!pick || normalizeBrokerName(pick) === normalizeBrokerName(selectedBroker)) return;
    const pickIsManageable = manageableBrokers.some(
      (broker) => normalizeBrokerName(broker.broker_name) === normalizeBrokerName(pick)
    );
    // After accounts load, ignore a stale ?broker_name= for a disconnected broker (don't
    // show e.g. Binance connect under a Liquid Brokers selector).
    if (
      explicitBrokerParam &&
      !pickIsManageable &&
      !tradeAccountsLoading &&
      !isLoadingBrokerDetails
    ) {
      return;
    }
    setSelectedBroker(pick);
  }, [
    location.search,
    location.pathname,
    capabilities,
    isLoadingCapabilities,
    activeBrokerName,
    selectedBroker,
    embeddedInConnectWizard,
    manageableBrokers,
    tradeAccountsLoading,
    isLoadingBrokerDetails,
  ]);

  useEffect(() => {
    if (!isBinanceBroker) {
      setBrokerIntegrationTab("sync");
      setBinanceHistoryPath(null);
      return;
    }
    const stored = loadBinanceHistoryPath();
    const alreadyIntegrated =
      credentialsSaved || integratedBrokerSlugs.has("binance");
    const effectivePath =
      stored ?? (alreadyIntegrated ? ("recent_only" as const) : null);

    if (!stored && alreadyIntegrated) {
      saveBinanceHistoryPath("recent_only");
    }

    setBinanceHistoryPath(effectivePath);
    if (showBothTabs && !embeddedWizardLocksCsvTab) {
      if (effectivePath === "needs_csv_for_history") {
        if (embeddedInConnectWizard && embeddedImportMethod === "auto_sync") {
          setBrokerIntegrationTab("csv");
        } else if (!embeddedInConnectWizard) {
          setManagementCsvImportFocus(true);
        }
      } else if (embeddedInConnectWizard && embeddedImportMethod !== "file_upload") {
        setBrokerIntegrationTab("sync");
      }
    }
  }, [
    selectedBroker,
    isBinanceBroker,
    showBothTabs,
    embeddedInConnectWizard,
    embeddedImportMethod,
    embeddedWizardLocksCsvTab,
    credentialsSaved,
    integratedBrokerSlugs,
  ]);

  useEffect(() => {
    if (!isIbkrBroker) return;
    const stored = loadBrokerHistoryPath("ibkr");
    setIbkrHistoryPath(stored);
    if (showBothTabs && !embeddedWizardLocksCsvTab) {
      if (stored === "needs_csv_for_history") {
        if (embeddedInConnectWizard && embeddedImportMethod === "auto_sync") {
          setBrokerIntegrationTab("csv");
        } else if (!embeddedInConnectWizard) {
          setManagementCsvImportFocus(true);
        }
      } else if (embeddedInConnectWizard && embeddedImportMethod !== "file_upload") {
        setBrokerIntegrationTab("sync");
      }
    }
  }, [
    selectedBroker,
    isIbkrBroker,
    showBothTabs,
    embeddedInConnectWizard,
    embeddedImportMethod,
    embeddedWizardLocksCsvTab,
  ]);

  useEffect(() => {
    if (!isCtraderOAuthBroker) {
      setBrokerIntegrationTab("sync");
      setCtraderHistoryPath(null);
      return;
    }
    const stored = loadCtraderHistoryPath();
    const alreadyIntegrated =
      credentialsSaved || integratedBrokerSlugs.has("ctrader");
    const effectivePath =
      stored ?? (alreadyIntegrated ? ("recent_only" as const) : null);

    if (!stored && alreadyIntegrated) {
      saveCtraderHistoryPath("recent_only");
    }

    setCtraderHistoryPath(effectivePath);
    if (showBothTabs && !embeddedWizardLocksCsvTab) {
      if (effectivePath === "needs_csv_for_history") {
        if (embeddedInConnectWizard && embeddedImportMethod === "auto_sync") {
          setBrokerIntegrationTab("csv");
        } else if (!embeddedInConnectWizard) {
          setManagementCsvImportFocus(true);
        }
      } else if (embeddedInConnectWizard && embeddedImportMethod !== "file_upload") {
        setBrokerIntegrationTab("sync");
      }
    }
  }, [
    selectedBroker,
    isCtraderOAuthBroker,
    showBothTabs,
    embeddedInConnectWizard,
    embeddedImportMethod,
    embeddedWizardLocksCsvTab,
    credentialsSaved,
    integratedBrokerSlugs,
  ]);

  useEffect(() => {
    if (!showBothTabs || !csvImportOnly) return;
    setBrokerIntegrationTab("csv");
  }, [selectedBroker, showBothTabs, csvImportOnly]);

  const handleBinanceChooseRecentOnly = () => {
    saveBinanceHistoryPath("recent_only");
    setBinanceHistoryPath("recent_only");
    setManagementCsvImportFocus(false);
    setBrokerIntegrationTab("sync");
  };

  const handleBinanceChooseNeedsCsv = () => {
    saveBinanceHistoryPath("needs_csv_for_history");
    setBinanceHistoryPath("needs_csv_for_history");
    chooseOlderHistoryViaCsvImport();
  };

  const handleResetBinanceHistoryPath = () => {
    clearBinanceHistoryPath();
    setBinanceHistoryPath(null);
    setManagementCsvImportFocus(false);
    setBrokerIntegrationTab("sync");
  };

  const handleIbkrChooseRecentOnly = () => {
    saveBrokerHistoryPath("ibkr", "recent_only");
    setIbkrHistoryPath("recent_only");
    setManagementCsvImportFocus(false);
    setBrokerIntegrationTab("sync");
  };

  const handleIbkrChooseNeedsCsv = () => {
    saveBrokerHistoryPath("ibkr", "needs_csv_for_history");
    setIbkrHistoryPath("needs_csv_for_history");
    chooseOlderHistoryViaCsvImport();
  };

  const handleCtraderChooseRecentOnly = () => {
    saveCtraderHistoryPath("recent_only");
    setCtraderHistoryPath("recent_only");
    setManagementCsvImportFocus(false);
    setBrokerIntegrationTab("sync");
  };

  const handleCtraderChooseNeedsCsv = () => {
    saveCtraderHistoryPath("needs_csv_for_history");
    setCtraderHistoryPath("needs_csv_for_history");
    chooseOlderHistoryViaCsvImport();
  };

  const clearCtraderOAuthPopupWatch = useCallback(() => {
    if (ctraderOAuthPopupPollRef.current) {
      clearInterval(ctraderOAuthPopupPollRef.current);
      ctraderOAuthPopupPollRef.current = null;
    }
    ctraderOAuthPopupRef.current = null;
  }, []);

  const handleResetCtraderHistoryPath = () => {
    clearCtraderHistoryPath();
    setCtraderHistoryPath(null);
    setManagementCsvImportFocus(false);
    setBrokerIntegrationTab("sync");
  };

  const handleCtraderOAuthConnect = async () => {
    if (!ctraderOpenApiSync?.oauthAuthorizePath) {
      brokerSonnerToast({
        title: "cTrader connect unavailable",
        description: "OAuth is not configured for this environment.",
        status: "error",
        duration: 6000,
      });
      return;
    }
    setIsCtraderOAuthStarting(true);
    try {
      stashCtraderOAuthAssetClasses(connectAssetClasses);
      stashCtraderOAuthEnvironment(ctraderConnectEnvironment);
      const { authorize_url } = await fetchCtraderOAuthAuthorizeUrl(
        ctraderOpenApiSync.oauthAuthorizePath,
        ctraderConnectEnvironment
      );
      clearCtraderOAuthPopupWatch();
      const popup = openCtraderOAuthPopup(authorize_url);
      if (!popup) {
        brokerSonnerToast({
          title: "Popup blocked",
          description: "Allow popups for this site to connect cTrader in a separate window.",
          status: "warning",
          duration: 7000,
        });
        window.location.href = authorize_url;
        return;
      }
      ctraderOAuthPopupRef.current = popup;
      ctraderOAuthPopupPollRef.current = window.setInterval(() => {
        if (popup.closed) {
          clearCtraderOAuthPopupWatch();
          setIsCtraderOAuthStarting(false);
        }
      }, 400);
    } catch (error: any) {
      const msg = error?.message || "Unable to start cTrader OAuth.";
      const title = /501|not configured/i.test(msg) ? "cTrader connect unavailable" : "OAuth failed";
      presentBrokerApiError(error, title, msg);
      setIsCtraderOAuthStarting(false);
    }
  };

  const handleResetIbkrHistoryPath = () => {
    clearBrokerHistoryPath("ibkr");
    setIbkrHistoryPath(null);
    setManagementCsvImportFocus(false);
    setBrokerIntegrationTab("sync");
  };

  useImperativeHandle(
    embeddedSetupBackRef,
    () => ({
      tryHandleBack(): boolean {
        if (!embeddedInConnectWizard) return false;

        if (embeddedImportMethod === "file_upload" && isCustomCsvBrokerSelected) {
          return customCsvWizardNavRef.current?.tryHandleBack() ?? false;
        }

        // File upload / manual: wizard Back goes to import-method selection (step 2).
        if (embeddedImportMethod === "file_upload" || embeddedImportMethod === "manual") {
          return false;
        }

        if (
          isBinanceBroker &&
          binanceTradeSync?.historyBeforeApiRequiresCsvImport &&
          binanceHistoryPath &&
          !hasExistingBinanceIntegration
        ) {
          clearBinanceHistoryPath();
          setBinanceHistoryPath(null);
          setManagementCsvImportFocus(false);
          setBrokerIntegrationTab("sync");
          return true;
        }

        if (
          isIbkrBroker &&
          brokerTradeSync?.historyBeforeSyncRequiresCsvImport &&
          ibkrHistoryPath
        ) {
          handleResetIbkrHistoryPath();
          return true;
        }

        if (
          isCtraderOAuthBroker &&
          ctraderOpenApiSync?.historyBeforeSyncRequiresCsvImport &&
          ctraderHistoryPath &&
          !hasExistingCtraderIntegration
        ) {
          handleResetCtraderHistoryPath();
          return true;
        }

        return false;
      },
    }),
    [
      embeddedInConnectWizard,
      embeddedImportMethod,
      isCustomCsvBrokerSelected,
      isBinanceBroker,
      binanceTradeSync,
      binanceHistoryPath,
      hasExistingBinanceIntegration,
      isIbkrBroker,
      brokerTradeSync,
      ibkrHistoryPath,
      isCtraderOAuthBroker,
      ctraderOpenApiSync,
      ctraderHistoryPath,
      hasExistingCtraderIntegration,
    ]
  );

  useEffect(() => {
    if (selectedBroker) {
      loadBrokerDetails(selectedBroker);
    }
  }, [selectedBroker]);

  /**
   * Deep link from account picker (or bookmarks): append/re-import CSV for an existing account code.
   * Example: /profile?tab=broker-api&broker_name=oanda&account_code=myacct&csv=1
   */
  useEffect(() => {
    if (isLoadingCapabilities || isLoadingBrokerDetails) return;
    if (!selectedBroker || !selectedCapability) return;
    const sp = new URLSearchParams(location.search);
    const onBrokerTab =
      location.pathname.includes("/profile/broker-api") ||
      sp.get("tab") === "broker-api"
    if (!onBrokerTab) return;
    const brokerParam = (sp.get("broker_name") || sp.get("broker") || "").trim();
    if (!brokerParam) return;
    if (normalizeBrokerName(brokerParam) !== normalizeBrokerName(selectedBroker)) return;

    let accountParam = (sp.get("account_code") || sp.get("broker_account_code") || "").trim();
    if (
      !accountParam &&
      activeAccountCode &&
      activeBrokerName &&
      normalizeBrokerName(activeBrokerName) === normalizeBrokerName(selectedBroker)
    ) {
      accountParam = activeAccountCode.trim();
    }
    const wantCsvTab = sp.get("csv") === "1" || sp.get("focus") === "csv";

    if (embeddedInConnectWizard && showBothTabs) {
      setBrokerIntegrationTab(wantCsvTab ? "csv" : "sync");
    } else if (!embeddedInConnectWizard) {
      setManagementCsvImportFocus(wantCsvTab);
    }

    if (!effectiveCsvImport?.supported) {
      if (accountParam || wantCsvTab) {
        toast.info("CSV import unavailable", { description: `${resolveBrokerDisplayName(selectedCapability)} does not support CSV import in Tessera.` });
        history.replace({
          pathname: "/profile/broker-api",
          search: location.search,
        })
      }
      return
    }

    if (accountParam) {
      setAccountCode(accountParam);
      if (!(isCustomCsvBrokerSelected || (isIbkrBroker && ibkrAccountInferred))) {
        if (isTradingViewBrokerSelected || isTradingViewPaperBrokerSelected) {
          setCsvValues((prev) => ({ ...prev, account_name: accountParam }));
        } else {
          setCsvValues((prev) => ({ ...prev, account_code: accountParam }));
        }
      }
    }
    if (wantCsvTab && !embeddedInConnectWizard) {
      window.requestAnimationFrame(() => {
        window.requestAnimationFrame(() => {
          scrollToBrokerCsvImport();
        });
      });
    }
    if (accountParam || wantCsvTab || sp.get("sync") === "1") {
      // Drop one-shot flags but keep broker_name so selection does not fall back to
      // Liquid while OANDA (etc.) credentials hydrate and before the first sync account exists.
      const next = new URLSearchParams();
      const keepBroker = (sp.get("broker_name") || sp.get("broker") || "").trim();
      if (keepBroker) {
        next.set("broker_name", normalizeProfileBrokerSlug(keepBroker));
      }
      if (accountParam) {
        next.set("account_code", accountParam);
      }
      const nextSearch = next.toString();
      history.replace({
        pathname: location.pathname,
        search: nextSearch ? `?${nextSearch}` : "",
      });
    }
  }, [
    isLoadingCapabilities,
    isLoadingBrokerDetails,
    effectiveCsvImport,
    selectedBroker,
    selectedCapability,
    location.search,
    location.pathname,
    showBothTabs,
    embeddedInConnectWizard,
    isIbkrBroker,
    ibkrAccountInferred,
    isTradingViewBrokerSelected,
    isTradingViewPaperBrokerSelected,
    isMt5BrokerSelected,
    isMt4BrokerSelected,
    isCtraderBrokerSelected,
    isCustomCsvBrokerSelected,
    activeAccountCode,
    activeBrokerName,
    history,
    scrollToBrokerCsvImport,
  ]);

  /** OAuth callback stores credentials only; align dashboard active account when returning here. */
  const ctraderConnectedHandledRef = useRef(false);
  useEffect(() => {
    const sp = new URLSearchParams(location.search);
    if (sp.get("ctrader_connected") !== "1") {
      ctraderConnectedHandledRef.current = false;
      return;
    }
    if (isLoadingCapabilities || isLoadingBrokerDetails) return;
    if (!isCtraderOAuthBroker || ctraderConnectedHandledRef.current) return;
    const acct = syncAccountCodeForTrades;
    if (!acct) return;

    ctraderConnectedHandledRef.current = true;
    void (async () => {
      try {
        await setActiveTradeAccount("ctrader", acct);
        await refetchTradeAccounts();
        clearAllTradeCaches();
        window.dispatchEvent(
          new CustomEvent("tradesUpdated", { detail: { action: "ctraderOAuth" } })
        );
      } catch {
        // User can switch manually from the account picker.
      } finally {
        const next = new URLSearchParams(location.search);
        next.delete("ctrader_connected");
        const search = next.toString();
        history.replace({
          pathname: location.pathname,
          search: search ? `?${search}` : "",
        });
      }
    })();
  }, [
    isLoadingCapabilities,
    isLoadingBrokerDetails,
    isCtraderOAuthBroker,
    syncAccountCodeForTrades,
    location.search,
    location.pathname,
    setActiveTradeAccount,
    refetchTradeAccounts,
    history,
  ]);

  useEffect(() => {
    const handleCtraderOAuthPopupMessage = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;
      if (!isCtraderOAuthPopupMessage(event.data)) return;

      clearCtraderOAuthPopupWatch();
      setIsCtraderOAuthStarting(false);

      if (event.data.status === "success") {
        void (async () => {
          try {
            const accountCode = (event.data.accountCode || "").trim();
            if (accountCode) {
              await setActiveTradeAccount("ctrader", accountCode);
            }
            await refetchTradeAccounts();
            clearAllTradeCaches();
            if (selectedBroker) {
              await loadBrokerDetails(selectedBroker);
            }
            window.dispatchEvent(
              new CustomEvent("tradesUpdated", { detail: { action: "ctraderOAuth" } })
            );
            brokerSonnerToast({
              title: "cTrader connected",
              description:
                event.data.message ||
                "Your cTrader account is linked. You can sync trades from Broker Management.",
              status: "success",
              duration: 5000,
            });
          } catch {
            brokerSonnerToast({
              title: "cTrader connected",
              description:
                "Account linked. Refresh Broker Management if details look out of date.",
              status: "success",
              duration: 5000,
            });
          }
        })();
        return;
      }

      if (event.data.status === "error") {
        brokerSonnerToast({
          title: "cTrader connect failed",
          description: event.data.message || "Unable to complete cTrader OAuth.",
          status: "error",
          duration: 7000,
        });
      }
    };

    window.addEventListener("message", handleCtraderOAuthPopupMessage);
    return () => {
      window.removeEventListener("message", handleCtraderOAuthPopupMessage);
      clearCtraderOAuthPopupWatch();
    };
  }, [
    clearCtraderOAuthPopupWatch,
    selectedBroker,
    setActiveTradeAccount,
    refetchTradeAccounts,
  ]);

  useEffect(() => {
    if (!accountCode) return;
    if (isTradingViewBrokerSelected || isTradingViewPaperBrokerSelected) {
      return;
    }
    if (
      isMt5BrokerSelected ||
      isMt4BrokerSelected ||
      isFuturesOrdersCsvBrokerSelected
    ) {
      return;
    }
    setCsvValues((prev) => {
      if ((prev.account_code || "").trim() === accountCode.trim()) return prev;
      return { ...prev, account_code: accountCode };
    });
  }, [
    accountCode,
    isTradingViewBrokerSelected,
    isTradingViewPaperBrokerSelected,
    isMt5BrokerSelected,
    isMt4BrokerSelected,
    isFuturesOrdersCsvBrokerSelected,
  ]);

  const invalidateCredentialsTest = () => {
    setCredentialsTestPassed(false);
    setTestResult("");
    setTestResultAction("");
  };

  const handleChangeCredential = (field: string, value: string) => {
    // Binance sync fields (pairs, start date) are configured after a successful test.
    if (!isBinanceSyncCredentialField(field)) {
      invalidateCredentialsTest();
    }
    setCredentialValues((prev) => ({ ...prev, [field]: value }));
  };

  const handleChangeCsvField = (field: string, value: string) => {
    setCsvValues((prev) => ({ ...prev, [field]: value }));
  };

  const handleExecutingBrokerNameChange = (value: string) => {
    invalidateCredentialsTest();
    setExecutingBrokerName(value);
    setCsvValues((prev) => ({ ...prev, executing_broker_name: value }));
    setCredentialValues((prev) => ({ ...prev, executing_broker_name: value }));
  };

  const binanceSecretsOnFileOpts = isBinanceBroker && credentialsSaved ? binanceSecretsOnFile : undefined;

  const isBinanceAuthFieldRequired = (field: string): boolean => {
    if (!isBinanceBroker) return true;
    if (isBinanceApiKeyField(field)) {
      return !binanceSecretsOnFileOpts?.hasApiKeyOnFile;
    }
    if (isBinanceApiSecretField(field)) {
      return !binanceSecretsOnFileOpts?.hasApiSecretOnFile;
    }
    return true;
  };

  const validateBinanceCredentials = (): boolean => {
    if (!isBinanceBroker) return true;
    const result = validateBinanceCredentialValues(credentialValues, binanceSecretsOnFileOpts);
    if (result.ok) return true;
    toastError("Invalid Binance settings", { description: result.message });
    return false;
  };

  const resolvedExecutingBrokerName = (): string => {
    const fromState =
      executingBrokerName.trim() ||
      String(csvValues.executing_broker_name || credentialValues.executing_broker_name || "").trim();
    if (fromState) return fromState;
    if (isTradingViewPaperBrokerSelected) return TRADINGVIEW_PAPER_DEFAULT_EXECUTING_BROKER;
    return "";
  };

  const validateExecutingBrokerName = (context: "connect" | "csv"): boolean => {
    const required =
      context === "connect"
        ? showExecutingBrokerOnConnect
        : showExecutingBrokerOnCsvImport;
    if (!required) return true;
    if (resolvedExecutingBrokerName()) return true;
    const config =
      context === "connect"
        ? connectExecutingBroker
        : csvExecutingBroker;
    toastError("Executing broker required", {
      description: `Enter your ${executingBrokerFieldLabel(config).toLowerCase()} (e.g. IC Markets, Pepperstone).`,
    });
    return false;
  };

  const buildCredentialPayload = (
    purpose: "save" | "test",
    binanceOpts?: { hasApiKeyOnFile: boolean; hasApiSecretOnFile: boolean }
  ) =>
    buildCredentialPayloadForApi(
      normalizeBrokerName(selectedBroker),
      {
        ...credentialValues,
        executing_broker_name: resolvedExecutingBrokerName(),
      },
      purpose,
      binanceOpts,
      connectAssetClasses,
      connectTradedAssetClasses,
      resolvedExecutingBrokerName(),
      connectExecutingBroker || csvExecutingBroker
    );

  const ensureRequiredCredentials = (): boolean => {
    if (isBinanceBroker) {
      return validateBinanceCredentials();
    }
    if (!validateExecutingBrokerName("connect")) return false;
    const missing = requiredCredentialFields.filter(
      (field) => !String(credentialValues[field] || "").trim()
    );
    if (missing.length > 0) {
      toastError("Missing required credentials", { description: `Please fill: ${missing.map(credentialLabel).join(", ")}` });
      return false;
    }
    return true;
  };

  const loadBinanceCredentialsEditForm = async () => {
    const formPath = resolveBrokerEditFormPath(selectedCapability);
    if (!formPath) {
      throw new Error("Credential edit form is not available for this broker.");
    }
    const form = await fetchBrokerCredentialsForm(normalizedSelectedBroker, formPath);
    const { credentialValues: formValues, secretsOnFile, accountCode: formAccountCode } =
      binanceCredentialsFormToFormValues(form);
    setBinanceSecretsOnFile(secretsOnFile);
    setCredentialValues((prev) => ({
      ...prev,
      ...formValues,
    }));
    if (formAccountCode) {
      setAccountCode(formAccountCode);
    }
  };

  const openBinanceCredentialsEditForm = async () => {
    invalidateCredentialsTest();
    setCredentialsEditMode(true);
    setIsLoadingCredentialsForm(true);
    try {
      await loadBinanceCredentialsEditForm();
    } catch (error: any) {
      setCredentialsEditMode(false);
      presentMutationError(error, "Could not load saved settings");
    } finally {
      setIsLoadingCredentialsForm(false);
    }
  };

  const openBinanceReconnectForm = () => {
    void openBinanceCredentialsEditForm();
  };

  const showBinanceApiError = (title: string, message: string, action?: string) => {
    if (isBinanceReconnectError(message)) {
      openBinanceReconnectForm();
      toastWarning("Reconnect Binance", { description: message });
      return;
    }
    toastUserFacingError(title, { message, action });
  };

  const showBrokerApiError = (title: string, message: string, action?: string) => {
    if (isBinanceBroker) {
      showBinanceApiError(title, message, action);
      return;
    }
    if (isIbkrBroker && isIbkrFlexMissingTimezoneError(message)) {
      setIbkrFlexFormatBannerMessage(message);
      return;
    }
    toastUserFacingError(title, { message, action });
  };

  const presentBrokerApiError = (err: unknown, title: string, messageOverride?: string) => {
    const message =
      messageOverride?.trim() ||
      (err instanceof Error ? err.message.trim() : typeof err === "string" ? err.trim() : "");
    if (isBinanceBroker && message && isBinanceReconnectError(message)) {
      openBinanceReconnectForm();
      toastWarning("Reconnect Binance", { description: message });
      return;
    }
    if (isIbkrBroker && message && isIbkrFlexMissingTimezoneError(message)) {
      setIbkrFlexFormatBannerMessage(message);
      return;
    }
    if (!messageOverride && isApiRequestError(err)) {
      presentMutationError(err, title);
      return;
    }
    const action = isApiRequestError(err) ? err.action : undefined;
    showBrokerApiError(title, message || "Something went wrong.", action);
  };

  const validateOandaEnvironmentCredential = (): boolean => {
    const opts = resolveOandaEnvironmentOptions(selectedCapability);
    if (!opts.length) return true;
    const allFields = [...requiredCredentialFields, ...optionalCredentialFields];
    if (!allFields.some(isOandaEnvironmentCredentialField)) {
      return true;
    }
    const v = (
      credentialValues.oanda_environment ||
      credentialValues.oandaEnvironment ||
      ""
    ).trim();
    if (!v) return true;
    if (!opts.some((o) => o.value === v)) {
      toastError("Invalid environment", { description: "Choose Live or Practice so it matches your OANDA token type." });
      return false;
    }
    return true;
  };

  const validateTimezoneFields = (
    values: Record<string, string>,
    fields: string[],
    sectionName: string
  ): boolean => {
    const invalidTimezones = fields.filter(
      (field) => isTimezoneField(field) && values[field] && !IANA_TIMEZONE_SET.has(values[field])
    );
    if (invalidTimezones.length > 0) {
      toastError("Invalid timezone value", { description: `${sectionName}: ${invalidTimezones.join(", ")} must be a valid IANA timezone.` });
      return false;
    }
    return true;
  };

  const ensureCredentialsForTest = (): boolean => {
    if (isCtraderOAuthBroker) return true;
    if (isBinanceBroker) {
      const result = validateBinanceAuthCredentials(credentialValues, binanceSecretsOnFileOpts);
      if (result.ok) return true;
      toastError("Missing API credentials", { description: result.message });
      return false;
    }
    return ensureRequiredCredentials();
  };

  const handleTestConnection = async () => {
    if (
      !selectedCapability ||
      !ensureCredentialsForTest() ||
      !validateTimezoneFields(credentialValues, requiredCredentialFields, "Credentials") ||
      !validateOandaEnvironmentCredential()
    ) {
      return;
    }
    try {
      const broker = normalizeBrokerName(selectedBroker);
      const payload = buildCredentialPayload("test", binanceSecretsOnFileOpts);
      const response = await testBrokerConnection(broker, payload);
      setTestResult(response.message);
      setTestResultAction(response.action ?? "");
      if (!response.success) {
        setCredentialsTestPassed(false);
        showBrokerApiError("Credentials not validated", response.message, response.action);
        return;
      }
      setCredentialsTestPassed(true);
      setTestResultAction("");
      if (isIbkrBroker) {
        setIbkrFlexFormatBannerMessage(null);
      }
      brokerSonnerToast({
        title: "Connection test passed",
        description: response.message,
        status: "success",
        duration: 5000,
      });
    } catch (error: any) {
      const msg =
        error.message ||
        "Credentials could not be validated. Check your API keys and try again.";
      setCredentialsTestPassed(false);
      setTestResult(msg);
      setTestResultAction(isApiRequestError(error) ? error.action ?? "" : "");
      presentBrokerApiError(error, "Credentials not validated", msg);
    }
  };

  const handleCancelCredentialsEdit = () => {
    setCredentialsEditMode(false);
    setIsLoadingCredentialsForm(false);
    setTestResult("");
    setTestResultAction("");
    setCredentialsTestPassed(false);
    setCredentialValues({});
    setBinanceSecretsOnFile({ hasApiKeyOnFile: false, hasApiSecretOnFile: false });
    if (!credentialsSaved) return;
    const connectSpec = resolveConnectTradedAssetClasses(selectedCapability);
    const savedConnectClasses = parseSavedTradedAssetClasses(savedCredentialSnapshot);
    if (connectSpec?.userSelectable) {
      setConnectAssetClasses(savedConnectClasses.length > 0 ? savedConnectClasses : []);
    } else if (connectSpec?.fixedValues?.length) {
      setConnectAssetClasses(connectSpec.fixedValues);
    } else {
      setConnectAssetClasses([]);
    }
    setExecutingBrokerName(parseSavedExecutingBrokerName(savedCredentialSnapshot) || "");
  };

  const openRemoveCredentialsDialog = () => {
    setRemoveCredentialsConfirmText("");
    setRemoveCredentialsDialogOpen(true);
  };

  const closeRemoveCredentialsDialog = () => {
    if (isRemovingCredentials) return;
    setRemoveCredentialsDialogOpen(false);
    setRemoveCredentialsConfirmText("");
  };

  const openRemoveBrokerDialog = () => {
    setRemoveBrokerConfirmText("");
    setRemoveBrokerDialogOpen(true);
  };

  const closeRemoveBrokerDialog = () => {
    if (isRemovingBroker) return;
    setRemoveBrokerDialogOpen(false);
    setRemoveBrokerConfirmText("");
  };

  const handleDeleteCredentials = async () => {
    if (!selectedCapability) return;
    if (
      removeCredentialsConfirmText.trim().toLowerCase() !==
      (isCtraderOAuthBroker
        ? DISCONNECT_BROKER_OAUTH_CONFIRM_PHRASE
        : REMOVE_BROKER_CREDENTIALS_CONFIRM_PHRASE)
    ) {
      return;
    }
    setIsRemovingCredentials(true);
    try {
      const result = await deleteBrokerCredentials(
        normalizeBrokerName(selectedCapability.broker_name)
      );
      const slug = normalizeBrokerName(selectedCapability.broker_name);
      const displayName = resolveBrokerDisplayName(selectedCapability);
      const keptByCode = new Map<string, TradeAccountOption>();
      for (const account of brokerTradeAccountsForSelectedBroker) {
        const code = account.accountCode.trim();
        if (code) keptByCode.set(code, account);
      }
      const extraCodes = [
        ...Array.from(justSyncedAccountCodes),
        ...connectedAccountsForTable.map((row) => row.accountCode),
        ...availableAccountsForTable.map((row) => row.accountCode),
        ...accounts.map((account) => account.accountCode),
        accountCode,
      ];
      for (const raw of extraCodes) {
        const code = String(raw || "").trim();
        if (!code || keptByCode.has(code)) continue;
        keptByCode.set(code, {
          accountCode: code,
          accountStatus: "",
          baseCurrency: "",
          balance: null,
          customName: null,
          brokerName: slug,
          brokerDisplayName: displayName,
          tradeLabel: code,
          accountSource: "synced_trades",
          lastBrokerSyncAt: new Date().toISOString(),
          lastCsvImportAt: null,
        });
      }
      setAccountsKeptAfterCredentialRemoval((prev) => ({
        ...prev,
        [slug]: Array.from(keptByCode.values()),
      }));
      setCredentialsSaved(false);
      unregisterCredentialBroker(selectedCapability.broker_name);
      setSavedCredentialSnapshot(null);
      setCredentialsEditMode(false);
      setCredentialValues({});
      setAccounts([]);
      setAccountCode("");
      setBinanceSecretsOnFile({ hasApiKeyOnFile: false, hasApiSecretOnFile: false });
      setTestResult("");
      setTestResultAction("");
      setCredentialsTestPassed(false);
      await refetchTradeAccounts();
      const templateNote =
        isCustomCsvBrokerSelected && typeof result.deleted_import_templates === "number"
          ? ` Removed ${result.deleted_import_templates} saved import template(s).`
          : isCustomCsvBrokerSelected
            ? " All saved import templates were removed."
            : "";
      toast.info(isCtraderOAuthBroker ? "cTrader disconnected" : "Credentials removed", {
        description: isCustomCsvBrokerSelected
          ? `Custom CSV disconnected.${templateNote} Trade data is unchanged unless you removed each imported account first.`
          : isCtraderOAuthBroker
            ? "OAuth access was removed. Connected accounts and Tessera trade data were kept. Reconnect to sync again."
            : "API credentials were removed. Connected accounts and Tessera trade data were kept. Add credentials again to resync.",
      });
      setRemoveCredentialsDialogOpen(false);
      setRemoveCredentialsConfirmText("");
    } catch (error: any) {
      presentMutationError(error, "Failed to remove credentials");
    } finally {
      setIsRemovingCredentials(false);
    }
  };

  const handleRemoveBroker = async () => {
    if (!selectedCapability) return;
    if (removeBrokerConfirmText.trim().toLowerCase() !== REMOVE_BROKER_CONFIRM_PHRASE) {
      return;
    }
    const broker = normalizeBrokerName(selectedCapability.broker_name);
    const accountCodes = brokerTradeAccountsForSelectedBroker
      .map((account) => account.accountCode.trim())
      .filter(Boolean);
    const deletedAccountCodes: string[] = [];
    let accountsPhaseComplete = false;
    setIsRemovingBroker(true);
    try {
      for (const code of accountCodes) {
        await deleteBrokerAccount({
          broker_account_code: code,
          broker_name: broker,
        });
        await removeTradeAccount(broker, code);
        deletedAccountCodes.push(code);
      }
      accountsPhaseComplete = true;
      if (credentialsSaved) {
        await deleteBrokerCredentials(broker);
      }
      unregisterCredentialBroker(broker);
      dropAccountsKeptAfterCredentialRemoval(broker);
      setCredentialsSaved(false);
      setSavedCredentialSnapshot(null);
      setCredentialsEditMode(false);
      setCredentialValues({});
      setAccounts([]);
      setAccountCode("");
      setJustSyncedAccountCodes(new Set());
      setBinanceSecretsOnFile({ hasApiKeyOnFile: false, hasApiSecretOnFile: false });
      setTestResult("");
      setTestResultAction("");
      setCredentialsTestPassed(false);
      notifyTradesUpdated({ action: "removeBrokerAccount" });
      await refetchTradeAccounts();
      toast.info("Broker removed", {
        description:
          accountCodes.length > 0
            ? `Credentials and Tessera data for ${accountCodes.length} account(s) were removed.`
            : "Broker credentials were removed.",
      });
      setRemoveBrokerDialogOpen(false);
      setRemoveBrokerConfirmText("");
    } catch (error: any) {
      notifyTradesUpdated({ action: "removeBrokerAccount" });
      await refetchTradeAccounts();
      presentMutationError(
        error,
        accountsPhaseComplete
          ? "Accounts removed, credentials still saved. Retry Remove broker."
          : "Remove broker stopped. Credentials were not deleted. Retry to finish."
      );
    } finally {
      setIsRemovingBroker(false);
    }
  };
  const handleSaveCredentials = async () => {
    if (!credentialsTestPassed) {
      toastWarning("Test connection required", {
        description: "Run Test connection successfully before saving credentials.",
      });
      return;
    }
    if (
      !selectedCapability ||
      !ensureRequiredCredentials() ||
      !validateTimezoneFields(credentialValues, requiredCredentialFields, "Credentials") ||
      !validateOandaEnvironmentCredential()
    ) {
      return;
    }
    const broker = normalizeBrokerName(selectedBroker);
    const requiresAccountCodeForInitialSync =
      isOandaBroker ||
      isCtraderOAuthBroker ||
      (usesLogicalAccountCode && !isIbkrBroker && !isBinanceBroker);
    const initialSyncAccountCode = syncAccountCodeForTrades.trim();
    const shouldRunInitialSync =
      embeddedInConnectWizard &&
      embeddedImportMethod === "auto_sync" &&
      (!requiresAccountCodeForInitialSync || Boolean(initialSyncAccountCode));

    setIsSaving(true);
    try {
      const payload = buildCredentialPayload("save", binanceSecretsOnFileOpts);
      await saveBrokerCredentials(broker, payload);
      setCredentialsSaved(true);
      setCredentialsEditMode(false);
      registerCredentialBroker(broker);
      dropAccountsKeptAfterCredentialRemoval(broker);
      if (isIbkrBroker) {
        setIbkrFlexFormatBannerMessage(null);
      }
      await loadBrokerDetails(selectedCapability.broker_name);

      if (shouldRunInitialSync) {
        if (isIbkrBroker) {
          brokerSonnerToast({
            title: "Syncing IBKR trades",
            description:
              "Credentials saved. Requesting your Flex statement from IBKR. This can take a minute.",
            status: "info",
            duration: 8000,
          });
        } else if (isBinanceBroker) {
          const lookbackHint = binanceTradeSync
            ? `API sync covers the last ${binanceTradeSync.apiSyncLookbackDays} days (${binanceTradeSync.apiSyncFromDate} through ${binanceTradeSync.apiSyncToDate}).`
            : "API sync covers recent Spot fills only (about the last 30 days).";
          toast.info("Syncing Binance trades", {
            description: `${lookbackHint} First sync can take a while. Leave this tab open.`,
          });
        } else {
          brokerSonnerToast({
            title: "Syncing trades",
            description: "Credentials saved. Importing your first trades now.",
            status: "info",
            duration: 6000,
          });
        }

        try {
          const syncAccountArg = isIbkrBroker
            ? undefined
            : initialSyncAccountCode || undefined;
          const result = await syncBrokerTrades(broker, syncAccountArg);
          rememberSyncedAccountCodes(
            initialSyncAccountCode,
            syncAccountArg,
            (result as { account_code?: string } | null)?.account_code
          );
          const stats = result?.sync_stats ?? null;
          await loadBrokerDetails(selectedCapability.broker_name);
          const syncStatus = await afterBrokerResyncWaitForAnalytics({
            accountCode: initialSyncAccountCode || undefined,
            brokerName: broker,
            action: "brokerSync",
            refetchBrokerAccounts: refetchTradeAccounts,
          });
          rememberSyncedAccountCodes(
            initialSyncAccountCode,
            syncAccountArg,
            syncStatus.account_code
          );
          if (syncStatus.phase === "needs_instrument_setup") {
            brokerSonnerToast({
              title: "Instrument setup required",
              description:
                "Credentials saved and sync completed. Resolve pending symbols before analytics can load.",
              status: "info",
              duration: 8000,
            });
            await runInstrumentSetupAfterSync(
              syncStatus,
              syncStatus.account_code || initialSyncAccountCode || syncAccountArg || undefined,
              { skipTradeAccountsRefetch: false }
            );
            return;
          }
          brokerSonnerToast({
            title: "Broker connected",
            description: stats
              ? `Credentials saved and trades synced (${stats.created} new, ${stats.updated} updated). ${syncStatus.closed_trades} closed, ${syncStatus.open_trades} open.`
              : `Credentials saved and your first trade sync completed (${syncStatus.closed_trades} closed, ${syncStatus.open_trades} open).`,
            status: "success",
            duration: 8000,
          });
          await runInstrumentSetupAfterSync(
            result,
            initialSyncAccountCode || syncAccountArg || undefined,
            { skipTradeAccountsRefetch: false }
          );
        } catch (syncError: any) {
          brokerSonnerToast({
            title: "Credentials saved",
            description:
              syncError?.message ||
              "Credentials were saved, but the first trade sync failed. Use Sync trades below to try again.",
            status: "warning",
            duration: 8000,
          });
          // Stay on wizard so Sync is available — don't bounce to Liquid on Broker Management.
          return;
        }
      } else if (
        embeddedInConnectWizard &&
        embeddedImportMethod === "auto_sync" &&
        requiresAccountCodeForInitialSync &&
        !initialSyncAccountCode
      ) {
        brokerSonnerToast({
          title: "Credentials saved",
          description: isOandaBroker
            ? "Add your OANDA account ID / logical account code (Update credentials), then Sync trades."
            : "Add an account code with your credentials, then Sync trades.",
          status: "info",
          duration: 8000,
        });
        // Stay on wizard — redirecting without an account code lands on Liquid with no Sync.
        return;
      } else {
        brokerSonnerToast({
          title: "Credentials saved",
          description: "Broker credentials have been saved securely.",
          status: "success",
          duration: 4000,
        });
      }

      if (embeddedInConnectWizard && embeddedImportMethod === "auto_sync") {
        const params = new URLSearchParams();
        params.set("broker_name", normalizeProfileBrokerSlug(selectedBroker));
        params.set("sync", "1");
        onEmbeddedWizardComplete?.();
        history.replace(`${accountSettingsTabPath("broker-api")}?${params.toString()}`);
      }
    } catch (error: any) {
      presentBrokerApiError(
        error,
        "Failed to save credentials",
        error.message || "Unable to save broker credentials"
      );
    } finally {
      setIsSaving(false);
    }
  };

  useEffect(() => {
    if (!onEmbeddedWizardFooterChange) return;

    const embeddedCustomCsvFileUpload =
      embeddedInConnectWizard &&
      embeddedImportMethod === "file_upload" &&
      isCustomCsvBrokerSelected;
    if (embeddedCustomCsvFileUpload) {
      return;
    }

    if (embeddedAutoSyncAlreadyComplete) {
      onEmbeddedWizardFooterChange(null);
      return;
    }

    if (showEmbeddedWizardSaveFooter) {
      onEmbeddedWizardFooterChange({
        label: "Save Credentials",
        disabled: isSaving || !credentialsTestPassed,
        isLoading: isSaving,
        loadingLabel: "Saving…",
        onClick: () => {
          void handleSaveCredentials();
        },
      });
      return;
    }
    if (showEmbeddedWizardSyncFooter) {
      onEmbeddedWizardFooterChange({
        label: "Sync trades",
        disabled: isSyncing || isLoadingBrokerDetails,
        isLoading: isSyncing,
        loadingLabel: "Syncing…",
        onClick: () => {
          void handleSyncAccount();
        },
      });
      return;
    }
    if (showEmbeddedWizardCsvImportFooter) {
      onEmbeddedWizardFooterChange({
        label: embeddedCsvImportFooterLabel,
        disabled: isUploadingCsv || csvImportRateLimit.isCoolingDown,
        isLoading: isUploadingCsv,
        loadingLabel: "Importing…",
        onClick: () => {
          void handleCsvImport();
        },
      });
      return;
    }
    if (showEmbeddedWizardCtraderConnectFooter) {
      onEmbeddedWizardFooterChange({
        label: "Connect cTrader (OAuth)",
        disabled: isCtraderOAuthStarting,
        isLoading: isCtraderOAuthStarting,
        loadingLabel: "Connecting…",
        onClick: () => {
          void handleCtraderOAuthConnect();
        },
      });
      return;
    }
    onEmbeddedWizardFooterChange(null);
  }, [
    onEmbeddedWizardFooterChange,
    embeddedInConnectWizard,
    embeddedImportMethod,
    isCustomCsvBrokerSelected,
    embeddedAutoSyncAlreadyComplete,
    showEmbeddedWizardSaveFooter,
    showEmbeddedWizardSyncFooter,
    showEmbeddedWizardCsvImportFooter,
    showEmbeddedWizardCtraderConnectFooter,
    embeddedCsvImportFooterLabel,
    isSaving,
    isSyncing,
    isLoadingBrokerDetails,
    credentialsTestPassed,
    isUploadingCsv,
    csvImportRateLimit.isCoolingDown,
    isCtraderOAuthStarting,
    connectAssetClasses.length,
    credentialValues,
    selectedBroker,
    selectedCapability,
    connectAssetClasses,
    executingBrokerName,
    binanceSecretsOnFileOpts,
    csvFile,
    futuresOrdersCashHistoryFile,
    csvValues,
    csvAssetClasses,
  ]);

  useEffect(() => {
    return () => {
      onEmbeddedWizardFooterChange?.(null);
    };
  }, [onEmbeddedWizardFooterChange]);

  const handleSyncAccount = async (targetAccountCode?: string) => {
    const broker = normalizeBrokerName(selectedBroker);
    const code = (targetAccountCode ?? syncAccountCodeForTrades).trim();
    const accountCodeRequired = isOandaBroker;
    const requiresAccountCodeForSync =
      isOandaBroker ||
      isCtraderOAuthBroker ||
      (usesLogicalAccountCode && !isIbkrBroker && !isBinanceBroker);
    if (!selectedCapability || !credentialsSaved) {
      brokerSonnerToast({
        title: "Credentials required",
        description: isCtraderOAuthBroker
          ? "Connect cTrader via OAuth before syncing trades."
          : "Save and test your broker credentials before syncing trades.",
        status: "warning",
        duration: 4000,
      });
      return;
    }
    if (accountCodeRequired && !code) {
      toastWarning("Account code required", {
        description: isOandaBroker
          ? "Save a logical account code and/or your OANDA account ID with your credentials (Update credentials), then sync."
          : "Select an account before syncing trades.",
      });
      return;
    }
    if (requiresAccountCodeForSync && !code) {
      toastWarning("Account code required", {
        description: isOandaBroker
          ? "Save a logical account code and/or your OANDA account ID with your credentials (Update credentials), then sync."
          : "Select an account before syncing trades.",
      });
      return;
    }
    if (targetAccountCode) {
      setAccountCode(targetAccountCode);
    }
    setIsSyncing(true);
    if (isIbkrBroker) {
      setIbkrFlexFormatBannerMessage(null);
      const flexHint = brokerTradeSync
        ? `Flex sync covers the last ${brokerTradeSync.syncLookbackDays} days (${brokerTradeSync.syncFromDate} through ${brokerTradeSync.syncToDate}).`
        : "Flex sync covers the configured lookback window.";
      brokerSonnerToast({
        title: "Syncing IBKR trades",
        description: `${flexHint} Requesting your Flex statement from IBKR. This can take a minute. Leave this tab open.`,
        status: "info",
        duration: 8000,
      });
    }
    if (isBinanceBroker) {
      const lookbackHint = binanceTradeSync
        ? `API sync covers the last ${binanceTradeSync.apiSyncLookbackDays} days (${binanceTradeSync.apiSyncFromDate} through ${binanceTradeSync.apiSyncToDate}).`
        : "API sync covers recent Spot fills only (about the last 30 days).";
      toast.info("Syncing Binance trades", { description: `${lookbackHint} First sync can take a while. Leave this tab open or retry if it times out.` });
    }
    if (isCtraderOAuthBroker) {
      brokerSonnerToast({
        title: "Syncing cTrader trades",
        description:
          "Deal history is imported from cTrader Open API (rate-limited). First sync can take a while. Leave this tab open. Use CSV import for older history.",
        status: "info",
        duration: 8000,
      });
    }
    try {
      const result = await syncBrokerTrades(
        broker,
        isIbkrBroker ? undefined : code || undefined
      );
      rememberSyncedAccountCodes(
        code,
        (result as { account_code?: string } | null)?.account_code
      );
      const stats = result?.sync_stats ?? null;
      const safeStats = stats ?? { created: 0, updated: 0, skipped: 0, total: 0 };
      if (code) {
        const brokerSlug = normalizeBrokerName(broker);
        const activeSlug = activeBrokerName ? normalizeBrokerName(activeBrokerName) : "";
        const accountChanged = code !== activeAccountCode || brokerSlug !== activeSlug;
        if (accountChanged) {
          try {
            await setActiveTradeAccount(brokerSlug, code);
          } catch {
            // Non-blocking — sync may have persisted active on the server.
          }
        }
      }
      const syncStatus = await afterBrokerResyncWaitForAnalytics({
        action: "brokerSync",
        accountCode: code || undefined,
        brokerName: normalizeBrokerName(broker),
        refetchBrokerAccounts: refetchTradeAccounts,
      });
      rememberSyncedAccountCodes(code, syncStatus.account_code);
      if (syncStatus.phase === "needs_instrument_setup") {
        brokerSonnerToast({
          title: "Instrument setup required",
          description: "Sync completed. Resolve pending symbols before analytics can load.",
          status: "info",
          duration: 8000,
        });
        await runInstrumentSetupAfterSync(
          syncStatus,
          syncStatus.account_code || code || undefined,
          { skipTradeAccountsRefetch: false }
        );
        return;
      }
      if (isIbkrBroker) {
        setIbkrFlexFormatBannerMessage(null);
      }
      await runInstrumentSetupAfterSync(result, code || undefined, {
        skipTradeAccountsRefetch: false,
      });
      brokerSonnerToast({
        title: "Sync completed",
        description: stats
          ? `Created ${safeStats.created}, updated ${safeStats.updated}, skipped ${safeStats.skipped} (total ${safeStats.total}).`
          : "Trades were synced successfully.",
        status: "success",
        duration: isBinanceBroker || isIbkrBroker || isCtraderOAuthBroker ? 8000 : 5000,
      });
    } catch (error: any) {
      let msg = error.message || "Unable to sync trades";
      if (
        isBinanceBroker &&
        /rejected|401|unauthorized|invalid api/i.test(msg)
      ) {
        msg = `${msg} Check API keys and that your keys match the server environment (mainnet vs testnet).`;
      }
      presentBrokerApiError(error, "Sync failed", msg);
    } finally {
      setIsSyncing(false);
    }
  };

  const handleCustomCsvImportComplete = async (importResult: {
    account_name?: string;
    account_code?: string;
    sync_stats?: { created?: number; updated?: number; total?: number };
    instrument_setup?: {
      requires_setup?: boolean;
      pending_count?: number;
      setup_url?: string;
    };
  }) => {
    const accountCodeImported = String(
      importResult?.account_code || importResult?.account_name || ""
    ).trim();
    const statsLine = formatCsvImportStatsDescription(importResult);
    brokerSonnerToast({
      title: "Other broker import completed",
      description: statsLine || "Trades were imported successfully.",
      status: "success",
      duration: 6000,
    });
    await refetchTradeAccounts();
    if (accountCodeImported) {
      try {
        await setActiveTradeAccount("custom_csv", accountCodeImported);
      } catch {
        // Non-blocking
      }
    }
    clearAllTradeCaches();
    window.dispatchEvent(
      new CustomEvent("tradesUpdated", { detail: { action: "csvImport" } })
    );
    // Same as other brokers: open instrument setup when import summary or account flags pending.
    await runInstrumentSetupAfterSync(importResult, accountCodeImported || undefined, {
      skipTradeAccountsRefetch: true,
    });
    if (embeddedInConnectWizard) {
      const params = new URLSearchParams();
      params.set("broker_name", normalizeProfileBrokerSlug(selectedBroker || "custom_csv"));
      if (accountCodeImported) {
        params.set("account_code", accountCodeImported);
      }
      onEmbeddedWizardComplete?.();
      history.replace(`${accountSettingsTabPath("broker-api")}?${params.toString()}`);
    }
  };

  const handleCsvImport = async () => {
    if (!selectedCapability || !showCsvMode) return;
    if (csvImportRateLimit.isCoolingDown) {
      brokerSonnerToast({
        title: "Rate limit reached",
        description: `Try again in ${csvImportRateLimit.retryAfterSeconds}s.`,
        status: "warning",
        duration: 5000,
      });
      return;
    }
    const importFileLabel = isMt5BrokerSelected ? "XLSX" : isMt4BrokerSelected ? "HTML" : "CSV";
    if (!csvFile) {
      brokerSonnerToast({
        title: `${importFileLabel} file required`,
        description: `Please choose a ${importFileLabel} file to import.`,
        status: "error",
        duration: 4000,
      });
      return;
    }
    if (!isCsvImportFileWithinLimit(csvFile)) {
      brokerSonnerToast({
        title: "File too large",
        description: csvImportMaxSizeMessage(),
        status: "error",
        duration: 5000,
      });
      return;
    }
    const cashHistoryFileForImport = futuresOrdersCashHistoryFileRef.current;
    if (cashHistoryFileForImport && !isCsvImportFileWithinLimit(cashHistoryFileForImport)) {
      brokerSonnerToast({
        title: "Cash History file too large",
        description: csvImportMaxSizeMessage(),
        status: "error",
        duration: 5000,
      });
      return;
    }
    if (isMt5BrokerSelected && !isMt5XlsxFile(csvFile)) {
      brokerSonnerToast({
        title: "Only .xlsx files are supported",
        description: "Export your MT5 Trade History Report as Excel (.xlsx).",
        status: "error",
        duration: 5000,
      });
      return;
    }
    if (isMt4BrokerSelected && !isMt4HtmlFile(csvFile)) {
      brokerSonnerToast({
        title: "Only .htm/.html files are supported",
        description: "Export your MT4 Detailed Report as HTML (.htm or .html).",
        status: "error",
        duration: 5000,
      });
      return;
    }
    if (isCtraderBrokerSelected && !isCtraderCsvFile(csvFile)) {
      brokerSonnerToast({
        title: "Only .csv files are supported",
        description: "Choose a cTrader statement export (.csv).",
        status: "error",
        duration: 5000,
      });
      return;
    }
    if (isFuturesOrdersCsvBrokerSelected && !isFuturesOrdersCsvFile(csvFile)) {
      brokerSonnerToast({
        title: "Only .csv files are supported",
        description: `Upload your ${futuresOrdersPlatformName} Orders export as CSV.`,
        status: "error",
        duration: 5000,
      });
      return;
    }
    if (
      isFuturesOrdersCsvBrokerSelected &&
      cashHistoryFileForImport &&
      !isFuturesOrdersCsvFile(cashHistoryFileForImport)
    ) {
      brokerSonnerToast({
        title: "Cash History must be a .csv file",
        description: `Export from ${futuresOrdersPlatformName} → Cash History and upload the CSV.`,
        status: "error",
        duration: 5000,
      });
      return;
    }

    const requiredImportFields = isMt5BrokerSelected
      ? mt5ImportFormFields
      : isMt4BrokerSelected
        ? mt4ImportFormFields
      : isFuturesOrdersCsvBrokerSelected
        ? futuresOrdersImportFormFields
        : isCtraderBrokerSelected
          ? ctraderImportFormFields
          : activeCsvFormFields;

    if (!validateExecutingBrokerName("csv")) {
      return;
    }

    const csvTradedSpecForImport = resolveCsvTradedAssetClasses(
      selectedCapability,
      effectiveCsvImport
    );

    const missing = requiredImportFields.filter((field) => {
      if (isTradedAssetClassesField(field)) {
        // Optional at import for normal brokers; fixed classes (crypto/futures) are injected.
        if (!csvTradedSpecForImport?.userSelectable) return false;
        return false;
      }
      if (isExecutingBrokerNameField(field)) {
        return !resolvedExecutingBrokerName();
      }
      return !(csvValues[field] || "").trim();
    });
    if (missing.length > 0) {
      brokerSonnerToast({
        title: "Missing import fields",
        description: `Please fill: ${missing.map((f) => csvFieldLabel(f)).join(", ")}`,
        status: "error",
        duration: 5000,
      });
      return;
    }
    if (
      !isCtraderBrokerSelected &&
      !validateTimezoneFields(csvValues, requiredImportFields, "Import")
    ) {
      return;
    }

    setIsUploadingCsv(true);
    let binanceCsvHeaderLine = "";
    let ctraderCsvText = "";
    let csvFileForUpload: File = csvFile;
    try {
      if (isBinanceBroker) {
        const fileText = await csvFile.text();
        binanceCsvHeaderLine = fileText.split(/\r?\n/).find((line) => line.trim()) ?? "";
        const csvValidation = validateBinanceCsvForImport(fileText);
        if (!csvValidation.ok) {
          brokerSonnerToast({
            title: csvValidation.title || "Invalid Binance CSV",
            description: csvValidation.message || "Use Spot Trade History export from Binance.",
            status: "error",
            duration: 12000,
          });
          return;
        }
      }
      if (isCtraderBrokerSelected) {
        ctraderCsvText = await csvFile.text();
        const csvValidation = validateCtraderCsvForImport(ctraderCsvText);
        if (!csvValidation.ok) {
          brokerSonnerToast({
            title: csvValidation.title || "Invalid cTrader CSV",
            description: csvValidation.message || "Re-export the statement from cTrader History.",
            status: "error",
            duration: 12000,
          });
          return;
        }
        if (ctraderCsvHasNonBreakingSpaces(ctraderCsvText)) {
          ctraderCsvText = sanitizeCtraderCsvText(ctraderCsvText);
          csvFileForUpload = new File([ctraderCsvText], csvFile.name, {
            type: csvFile.type || "text/csv",
          });
        }
      }
      if (isTradingViewPaperBrokerSelected) {
        const paperCsvText = await csvFile.text();
        const headerLine = paperCsvText.split(/\r?\n/).find((line) => line.trim()) ?? "";
        if (looksLikeTradingViewStrategyTesterCsv(headerLine)) {
          brokerSonnerToast({
            title: "Wrong TradingView export",
            description: tradingViewPaperWrongFileHint(),
            status: "error",
            duration: 12000,
          });
          return;
        }
      }

      const brokerSlug = normalizeBrokerName(selectedCapability.broker_name);
      const execName = resolvedExecutingBrokerName();
      // TradingView / Paper csv-import upserts credentials itself; POST /credentials requires
      // account_name + traded_asset_classes and rejects broker_name + executing_broker_name only.
      if (
        showExecutingBrokerOnCsvImport &&
        execName &&
        isStatementImportPlatform(brokerSlug) &&
        !isTradingViewBroker(brokerSlug) &&
        !isTradingViewPaperBroker(brokerSlug) &&
        !isCtraderBrokerSelected
      ) {
        try {
          await saveBrokerCredentials(
            brokerSlug,
            buildCredentialPayloadForApi(
              brokerSlug,
              { ...csvValues, executing_broker_name: execName },
              "save",
              undefined,
              csvAssetClasses,
              null,
              execName,
              csvExecutingBroker || connectExecutingBroker
            )
          );
        } catch {
          // Non-blocking — import still sends executing_broker_name.
        }
      }
      const csvValuesForImport = {
        ...csvValues,
        executing_broker_name: execName || csvValues.executing_broker_name || "",
      };
      const tradingViewAssetClasses = isTradingViewBrokerSelected
        ? usesMultiAssetClassImport
          ? csvAssetClasses
          : (csvValues.traded_asset_classes || "").trim()
            ? [csvValues.traded_asset_classes.trim()]
            : csvAssetClasses
        : [];
      const tradingViewPaperAssetClasses = isTradingViewPaperBrokerSelected
        ? usesMultiAssetClassImport
          ? csvAssetClasses
          : (csvValues.traded_asset_classes || "").trim()
            ? [csvValues.traded_asset_classes.trim()]
            : csvAssetClasses
        : [];
      const genericCsvAssetClasses = usesMultiAssetClassImport
        ? csvAssetClasses
        : (csvValues.traded_asset_classes || "").trim()
          ? [csvValues.traded_asset_classes.trim()]
          : csvAssetClasses;

      const csvParams = isIbkrBroker
        ? {}
        : isTradingViewBrokerSelected
          ? buildTradingViewCsvImportParams(
              csvValuesForImport,
              tradingViewAssetClasses.length > 0
                ? tradingViewAssetClasses
                : (csvTradedSpecForImport?.fixedValues ?? [])
            )
          : isTradingViewPaperBrokerSelected
            ? buildTradingViewPaperCsvImportParams(
                csvValuesForImport,
                tradingViewPaperAssetClasses.length > 0
                  ? tradingViewPaperAssetClasses
                  : (csvTradedSpecForImport?.fixedValues ?? [])
              )
          : isMt5BrokerSelected
            ? buildMt5XlsxImportParams(csvValuesForImport, csvAssetClasses)
            : isMt4BrokerSelected
              ? buildMt4HtmlImportParams(csvValuesForImport, csvAssetClasses)
            : isCtraderBrokerSelected
              ? buildCtraderCsvImportParams(csvValuesForImport, csvAssetClasses)
              : isHyperliquidBrokerSelected
                ? buildHyperliquidCsvImportParams(csvValuesForImport, csvAssetClasses)
              : isFuturesOrdersCsvBrokerSelected
                ? buildFuturesOrdersCsvImportParams(normalizedSelectedBroker, csvValues)
                : buildGenericBrokerCsvImportParams(
                    csvValuesForImport,
                    genericCsvAssetClasses,
                    csvTradedSpecForImport
                  );
      const hadFuturesOrdersCashHistory = Boolean(
        isFuturesOrdersCsvBrokerSelected && cashHistoryFileForImport
      );
      const extraFormFiles =
        hadFuturesOrdersCashHistory && cashHistoryFileForImport
          ? [{ fieldName: FUTURES_ORDERS_CASH_HISTORY_FIELD, file: cashHistoryFileForImport }]
          : undefined;
      const importResult = await uploadBrokerCsvImport(
        brokerSlug,
        csvFileForUpload,
        csvParams,
        extraFormFiles,
        { endpoint: effectiveCsvImport?.endpoint }
      );
      const statsLine = formatCsvImportStatsDescription(importResult);
      const importedAccountCode =
        isMt5BrokerSelected || isMt4BrokerSelected || isFuturesOrdersCsvBrokerSelected
          ? String(importResult?.account_code || "").trim()
          : isCtraderBrokerSelected
            ? String(importResult?.account_code || csvValues.account_code || "").trim()
            : isTradingViewBrokerSelected ||
                isTradingViewPaperBrokerSelected ||
                isHyperliquidBrokerSelected
              ? String(importResult?.account_code || importResult?.account_name || "").trim() ||
                (csvValues.account_name || "").trim()
              : String(importResult?.account_code || csvValues.account_code || "").trim();
      const importTitle = isMt5BrokerSelected
        ? "MT5 import completed"
        : isMt4BrokerSelected
          ? "MT4 import completed"
        : isCtraderBrokerSelected
          ? "cTrader import completed"
          : isFuturesOrdersCsvBrokerSelected
            ? `${futuresOrdersPlatformName} import completed`
            : isHyperliquidBrokerSelected
              ? "Hyperliquid import completed"
            : isTradingViewPaperBrokerSelected
              ? "Paper Trading import completed"
            : isTradingViewBrokerSelected
              ? "CSV import completed"
              : "CSV import completed";
      const ctraderSuccessLine = isCtraderBrokerSelected
        ? formatCtraderImportSuccessDescription(importResult)
        : null;
      const paperSuccessLine = isTradingViewPaperBrokerSelected
        ? formatTradingViewPaperImportSuccessDescription(importResult)
        : null;
      const ctraderDetailLines = isCtraderBrokerSelected
        ? formatCtraderImportDetailLines(importResult)
        : [];
      const tradingViewReimportToExisting =
        isTradingViewBrokerSelected &&
        isTradingViewReimportToExistingAccount(
          csvValues.account_name || "",
          csvBrokerAccounts
        );
      brokerSonnerToast({
        title: importTitle,
        description:
          paperSuccessLine ||
          ctraderSuccessLine ||
          statsLine ||
          (isTradingViewBrokerSelected
            ? tradingViewReimportToExisting
              ? "Trades were merged into the existing account with that name."
              : "A new backtest account was created. Your dashboard active account was not changed."
            : isTradingViewPaperBrokerSelected
              ? "Round-trips from Paper Order History are stored. Re-imports with the same account name merge with existing trades."
            : isMt5BrokerSelected
              ? "Trades from the report are stored. Re-imports merge with existing trades for that account."
              : isMt4BrokerSelected
                ? "Closed trades from the report are stored. Re-imports merge with existing trades for that account."
              : isCtraderBrokerSelected
                ? "Trades from the statement are stored. Re-imports merge with existing trades for that account."
                : isFuturesOrdersCsvBrokerSelected
                  ? `Filled orders are stored. Re-imports merge with existing trades for that account.${futuresOrdersImportSuccessSuffix(importResult, hadFuturesOrdersCashHistory)}`
                  : isHyperliquidBrokerSelected
                    ? "Round-trip fills from the Hyperliquid export are stored. Re-imports with the same account name merge with existing trades."
                  : "The CSV file has been processed. Re-imports with the same account code merge with existing trades."),
        status: "success",
        duration: 6000,
      });
      if (isFuturesOrdersCsvBrokerSelected) {
        for (const feeToast of getFuturesOrdersFeesFollowUpToasts(
          importResult,
          hadFuturesOrdersCashHistory
        )) {
          brokerSonnerToast({
            title: feeToast.title,
            description: feeToast.description,
            status: feeToast.status,
            duration: feeToast.status === "warning" ? 8000 : 5000,
          });
        }
      }
      for (const detailLine of ctraderDetailLines) {
        brokerSonnerToast({
          title: "Import details",
          description: detailLine,
          status: "info",
          duration: 5000,
        });
      }
      const paperOpenLotsWarning = isTradingViewPaperBrokerSelected
        ? formatTradingViewPaperOpenLotsWarning(importResult)
        : null;
      if (paperOpenLotsWarning) {
        brokerSonnerToast({
          title: "Open lots remaining",
          description: paperOpenLotsWarning,
          status: "warning",
          duration: 8000,
        });
      }
      if (isBinanceBroker && binanceCsvImportStatsAreEmpty(importResult)) {
        const kind = detectBinanceCsvFormat(binanceCsvHeaderLine);
        brokerSonnerToast({
          title: "No trades imported",
          description: binanceCsvImportZeroTradesHint(kind),
          status: "warning",
          duration: 12000,
        });
      }
      if (isCtraderBrokerSelected && ctraderCsvImportStatsAreEmpty(importResult)) {
        brokerSonnerToast({
          title: "No trades imported",
          description: ctraderCsvImportZeroTradesHint(ctraderCsvText),
          status: "warning",
          duration: 12000,
        });
      }
      const reconciliationWarning = isMt5BrokerSelected
        ? formatMt5ReconciliationWarning(importResult)
        : isMt4BrokerSelected
          ? formatMt4ReconciliationWarning(importResult)
        : isCtraderBrokerSelected
          ? formatCtraderReconciliationWarning(importResult)
          : null;
      if (reconciliationWarning) {
        brokerSonnerToast({
          title: "Report totals did not fully match",
          description: reconciliationWarning,
          status: "warning",
          duration: 8000,
        });
      }
      await refetchTradeAccounts();
      await runInstrumentSetupAfterSync(importResult, importedAccountCode || undefined, {
        skipTradeAccountsRefetch: true,
      });
      const shouldActivateImportedAccount =
        importedAccountCode &&
        (isCtraderBrokerSelected ||
          isBinanceBroker ||
          isTradingViewPaperBrokerSelected ||
          (isStatementImportBrokerSelected &&
            (!isTradingViewBrokerSelected || tradingViewReimportToExisting)));
      if (shouldActivateImportedAccount) {
        try {
          await setActiveTradeAccount(brokerSlug, importedAccountCode);
        } catch {
          // Non-blocking — user can switch account from the picker.
        }
        if (isStatementImportBrokerSelected || isBinanceBroker) {
          onEmbeddedWizardComplete?.();
          history.push("/trade-log");
        }
      } else if (
        isTradingViewBrokerSelected &&
        importedAccountCode &&
        !tradingViewReimportToExisting
      ) {
        brokerSonnerToast({
          title: "New backtest account created",
          description: `"${importedAccountCode}" is in your account list. Switch to it from the dashboard account picker when you want to review those trades.`,
          status: "info",
          duration: 7000,
        });
      }
      clearAllTradeCaches();
      window.dispatchEvent(
        new CustomEvent("tradesUpdated", {
          detail: {
            action: "csvImport",
            accountCode: importedAccountCode || undefined,
            brokerName: importedAccountCode ? brokerSlug : undefined,
          },
        })
      );
      if (isIbkrBroker) {
        setIbkrFlexFormatBannerMessage(null);
      }
      const navigatedToTradeLogAfterImport =
        shouldActivateImportedAccount &&
        (isStatementImportBrokerSelected || isBinanceBroker);
      if (
        embeddedInConnectWizard &&
        embeddedImportMethod === "file_upload" &&
        !navigatedToTradeLogAfterImport
      ) {
        const params = new URLSearchParams();
        params.set("broker_name", normalizeProfileBrokerSlug(selectedBroker || ""));
        if (importedAccountCode) {
          params.set("account_code", importedAccountCode);
        }
        onEmbeddedWizardComplete?.();
        history.replace(`${accountSettingsTabPath("broker-api")}?${params.toString()}`);
      } else if (!embeddedInConnectWizard) {
        setManagementCsvImportFocus(false);
        if (selectedCapability) {
          await loadBrokerDetails(selectedCapability.broker_name);
        }
        if (showBothTabs) {
          setBrokerIntegrationTab("sync");
        }
      }
    } catch (error: any) {
      const msg = error.message || "Unable to import file";
      presentBrokerApiError(
        error,
        isMt5BrokerSelected
          ? "MT5 import failed"
          : isMt4BrokerSelected
            ? "MT4 import failed"
            : isCtraderBrokerSelected
              ? "cTrader import failed"
              : isFuturesOrdersCsvBrokerSelected
                ? `${futuresOrdersPlatformName} import failed`
                : isTradingViewPaperBrokerSelected
                  ? "Paper Trading import failed"
                : "CSV import failed",
        msg
      );
    } finally {
      setIsUploadingCsv(false);
    }
  };

  const oandaEnvironmentOptions = resolveOandaEnvironmentOptions(selectedCapability);

  const timezoneSelectOptions = useMemo(() => buildTimezoneSelectOptions(), []);

  const renderTradedAssetClassesPicker = ({
    allowed,
    value,
    onChange,
    multi,
    label,
    hint,
    required = true,
  }: {
    allowed: string[];
    value: string[];
    onChange: (next: string[]) => void;
    multi: boolean;
    label: string;
    hint?: string;
    required?: boolean;
  }) => (
    <Field className="gap-2" data-broker-asset-classes-field="">
      <FieldLabel className={BROKER_ALERT_TITLE_CLASS}>
        {label}
        {required ? <span className="text-destructive"> *</span> : null}
      </FieldLabel>
      {hint ? <FieldDescription className={BROKER_FIELD_DESC_CLASS}>{hint}</FieldDescription> : null}
      {multi ? (
        <div
          data-slot="checkbox-group"
          className="grid grid-cols-1 gap-2 sm:grid-cols-2 md:grid-cols-3"
        >
          {allowed.map((slug) => {
            const inputId = `broker-asset-class-${slug}`;
            const checked = value.includes(slug);
            return (
              <div key={slug} className="flex min-w-0 items-start gap-2">
                <Checkbox
                  id={inputId}
                  data-broker-asset-class-checkbox=""
                  checked={checked}
                  className="mt-0.5"
                  onCheckedChange={(nextChecked) => {
                    if (nextChecked === true) {
                      onChange(checked ? value : [...value, slug]);
                      return;
                    }
                    onChange(value.filter((item) => item !== slug));
                  }}
                />
                <FieldLabel
                  htmlFor={inputId}
                  className={`${BROKER_FIELD_DESC_CLASS} cursor-pointer font-normal`}
                >
                  {formatAssetClassLabel(slug)}
                </FieldLabel>
              </div>
            );
          })}
        </div>
      ) : (
        <ShadcnRadioGroup
          data-slot="radio-group"
          value={value[0] || ""}
          onValueChange={(nextValue) => onChange(nextValue ? [nextValue] : [])}
          className="grid grid-cols-2 gap-2 sm:grid-cols-3"
        >
          {allowed.map((slug) => {
            const inputId = `broker-asset-class-${slug}`;
            return (
              <div key={slug} className="flex min-w-0 items-start gap-2">
                <RadioGroupItem
                  value={slug}
                  id={inputId}
                  data-broker-asset-class-radio=""
                  className="mt-0.5"
                />
                <FieldLabel
                  htmlFor={inputId}
                  className={`${BROKER_FIELD_DESC_CLASS} cursor-pointer font-normal`}
                >
                  {formatAssetClassLabel(slug)}
                </FieldLabel>
              </div>
            );
          })}
        </ShadcnRadioGroup>
      )}
    </Field>
  );

  const renderExecutingBrokerNameField = (
    config: BrokerExecutingBrokerConfig | null,
    options?: { required?: boolean; forCsvImport?: boolean }
  ) => {
    const label = executingBrokerFieldLabel(config);
    const hint =
      config?.hint ||
      getFieldMeta(normalizedSelectedBroker, "executing_broker_name").hint ||
      executingBrokerFieldHint(config, normalizedSelectedBroker);
    const placeholder =
      config?.placeholder ||
      getFieldMeta(normalizedSelectedBroker, "executing_broker_name").placeholder ||
      "e.g. IC Markets, Pepperstone";
    const required = options?.required !== false;
    const fieldId = "broker-executing-broker-name";

    return (
      <Field
        className="gap-2"
        data-broker-executing-broker-field=""
        {...(options?.forCsvImport ? { "data-broker-csv-executing-broker-field": "" } : {})}
      >
        <FieldLabel htmlFor={fieldId} className={BROKER_ALERT_TITLE_CLASS}>
          {label}
          {required ? <span className="text-destructive"> *</span> : null}
        </FieldLabel>
        {hint ? <FieldDescription className={BROKER_FIELD_DESC_CLASS}>{hint}</FieldDescription> : null}
        <ShadcnInput
          id={fieldId}
          value={resolvedExecutingBrokerName()}
          onChange={(e) => handleExecutingBrokerNameChange(e.target.value)}
          placeholder={placeholder}
          className="font-[family-name:var(--font-primary)]"
        />
      </Field>
    );
  };

  const renderConnectExecutingBrokerBlock = () => {
    if (!showExecutingBrokerOnConnect) return null;
    return renderExecutingBrokerNameField(connectExecutingBroker, {
      required: connectExecutingBroker?.required !== false,
    });
  };

  const renderCsvExecutingBrokerBlock = () => {
    if (!showExecutingBrokerOnCsvImport) return null;
    return renderExecutingBrokerNameField(csvExecutingBroker, {
      required: csvExecutingBroker?.required !== false,
      forCsvImport: true,
    });
  };

  const renderConnectTradedAssetClassesBlock = () => {
    if (!connectTradedAssetClasses) return null;
    if (shouldRenderConnectAssetClassReadOnly(connectTradedAssetClasses)) {
      const fixed = connectTradedAssetClasses.fixedValues ?? [];
      if (!fixed.length) return null;
      return (
        <p className={BROKER_INTEGRATION_BODY_TEXT_CLASS}>
          {tradedAssetClassesFieldLabel(connectTradedAssetClasses)}:{" "}
          {formatTradedAssetClassesReadOnly(fixed)}
        </p>
      );
    }
    if (!shouldRenderConnectAssetClassPicker(connectTradedAssetClasses)) return null;
    const hint =
      getFieldMeta(normalizedSelectedBroker, "traded_asset_classes").hint ||
      "Optional at connect. Ambiguous symbols are confirmed in instrument setup after sync.";
    return renderTradedAssetClassesPicker({
      allowed: connectTradedAssetClasses.allowedAssetClasses,
      value: connectAssetClasses,
      onChange: handleConnectAssetClassesChange,
      multi: connectTradedAssetClasses.multi,
      label: tradedAssetClassesFieldLabel(connectTradedAssetClasses),
      hint,
      required: false,
    });
  };

  const getCredentialFieldDescription = (field: string): string | null => {
    if (isBinanceAccountStartDateField(field)) {
      return binanceTradeSync
        ? getBinanceAccountStartDateHint(
            binanceTradeSync.apiSyncLookbackDays,
            binanceTradeSync.apiSyncFromDate,
            binanceTradeSync.apiSyncToDate
          )
        : BINANCE_FIELD_HINTS.binance_account_start_date;
    }
    if (isBinanceApiKeyField(field) && binanceSecretsOnFile.hasApiKeyOnFile) {
      return "Leave blank to keep your stored API key. Enter a value only when rotating keys.";
    }
    if (isBinanceApiSecretField(field) && binanceSecretsOnFile.hasApiSecretOnFile) {
      return "Leave blank to keep your stored API secret. Enter a value only when rotating secrets.";
    }
    if (isBinanceSpotSymbolsField(field)) {
      return BINANCE_FIELD_HINTS.binance_spot_symbols;
    }
    return getFieldMeta(normalizedSelectedBroker, field).hint || null;
  };

  const renderCredentialFieldControl = (field: string, fieldId: string) => {
    if (isTradedAssetClassesField(field)) {
      return null;
    }
    if (isExecutingBrokerNameField(field)) {
      return null;
    }
    if (isBinanceSpotSymbolsField(field)) {
      return (
        <BinanceSpotSymbolsField
          id={fieldId}
          value={credentialValues[field] || ""}
          onChange={(next) => handleChangeCredential(field, next)}
        />
      );
    }
    if (isBinanceAccountStartDateField(field)) {
      const restrictToApiWindow =
        binanceTradeSync && binanceHistoryPath === "recent_only";
      return (
        <SingleDatePicker
          id={fieldId}
          className="w-fit max-w-full shrink-0"
          value={credentialValues[field] || ""}
          onChange={(ymd) => handleChangeCredential(field, ymd)}
          minDate={restrictToApiWindow ? binanceTradeSync.apiSyncFromDate : undefined}
          maxDate={binanceTradeSync?.apiSyncToDate ?? new Date().toISOString().slice(0, 10)}
          placeholder={
            getFieldMeta(normalizedSelectedBroker, field).placeholder || "Select date"
          }
        />
      );
    }
    if (isOandaEnvironmentCredentialField(field) && oandaEnvironmentOptions.length > 0) {
      const placeholder =
        getFieldMeta(normalizedSelectedBroker, field).placeholder || "Select environment";
      return (
        <BrokerFormSelect
          value={credentialValues[field]?.trim() ? credentialValues[field] : undefined}
          onValueChange={(next) => handleChangeCredential(field, next)}
        >
          <SelectTrigger
            id={fieldId}
            className="w-full max-w-full font-[family-name:var(--font-primary)] text-sm font-normal normal-case tracking-normal text-gray-500 [&_[data-slot=select-value]]:text-gray-500"
          >
            <SelectValue placeholder={placeholder} />
          </SelectTrigger>
          <SelectContent position="popper" className="z-[200]">
            {oandaEnvironmentOptions.map((opt) => (
              <SelectItem key={opt.value} value={opt.value}>
                {opt.label}
              </SelectItem>
            ))}
          </SelectContent>
        </BrokerFormSelect>
      );
    }
    if (isTimezoneField(field)) {
      const placeholder =
        getFieldMeta(normalizedSelectedBroker, field).placeholder || "Select timezone";
      return (
        <TimezoneCombobox
          id={fieldId}
          value={credentialValues[field] || ""}
          onValueChange={(next) => handleChangeCredential(field, next)}
          placeholder={placeholder}
          options={timezoneSelectOptions}
          triggerClassName="w-full max-w-full font-[family-name:var(--font-primary)] text-sm font-normal normal-case tracking-normal text-gray-500 [&_[data-slot=select-value]]:text-gray-500"
          aria-label={placeholder}
        />
      );
    }
    let placeholder =
      getFieldMeta(normalizedSelectedBroker, field).placeholder || `Enter ${credentialLabel(field)}`;
    if (isBinanceApiKeyField(field) && binanceSecretsOnFile.hasApiKeyOnFile) {
      placeholder = "API key on file. Enter only to replace";
    }
    if (isBinanceApiSecretField(field) && binanceSecretsOnFile.hasApiSecretOnFile) {
      placeholder = "API secret on file. Enter only to replace";
    }
    if (isBrokerMaskedCredentialField(field)) {
      return (
        <BrokerMaskedCredentialInput
          id={fieldId}
          field={field}
          value={credentialValues[field] || ""}
          onChange={(next) => handleChangeCredential(field, next)}
          placeholder={placeholder}
        />
      );
    }
    return (
      <ShadcnInput
        id={fieldId}
        type="text"
        value={credentialValues[field] || ""}
        onChange={(e) => handleChangeCredential(field, e.target.value)}
        placeholder={placeholder}
        className="font-[family-name:var(--font-primary)]"
        {...getBrokerCredentialInputAttributes(field)}
      />
    );
  };

  const renderCredentialFieldRow = (
    field: string,
    required: boolean,
    options?: { recommended?: boolean }
  ) => {
    const fieldId = `broker-credential-${field}`;
    const description = getCredentialFieldDescription(field);

    return (
      <Field key={field} className="gap-2" data-broker-credential-field="">
        <div className="flex flex-wrap items-center gap-2">
          <FieldLabel htmlFor={fieldId} className={BROKER_ALERT_TITLE_CLASS}>
            {credentialLabel(field)}
            {required ? (
              <span className="text-destructive"> *</span>
            ) : options?.recommended ? (
              <span className="font-normal text-gray-500"> (recommended)</span>
            ) : (
              <span className="font-normal text-gray-500"> (optional)</span>
            )}
          </FieldLabel>
          {isBinanceApiKeyField(field) && binanceSecretsOnFile.hasApiKeyOnFile ? (
            <Badge variant="outline" data-broker-outline-badge="" className={BROKER_OUTLINE_BADGE_CLASS}>
              Key on file
            </Badge>
          ) : null}
          {isBinanceApiSecretField(field) && binanceSecretsOnFile.hasApiSecretOnFile ? (
            <Badge variant="outline" data-broker-outline-badge="" className={BROKER_OUTLINE_BADGE_CLASS}>
              Secret on file
            </Badge>
          ) : null}
        </div>
        {description ? (
          <FieldDescription className={BROKER_FIELD_DESC_CLASS}>{description}</FieldDescription>
        ) : null}
        {renderCredentialFieldControl(field, fieldId)}
      </Field>
    );
  };

  const renderBinanceTradeSyncUi = () => {
    if (!binanceTradeSync || showBinanceHistoryChoice) return null;
    return (
      <div className="mb-4">
        <BinanceTradeSyncGuidance tradeSync={binanceTradeSync} />
      </div>
    );
  };

  const renderBinanceHistoryPathInCardHeader = (alertDescription: string | null) => {
    if (!binanceTradeSync || !showBinanceHistoryChoice) return null;
    return (
      <div className="flex w-full flex-col gap-4">
        {alertDescription ? (
          <Alert variant="info" className={BROKER_ALERT_CLASS}>
            <BrokerAlertIcon tone="info" />
            <AlertDescription className={BROKER_ALERT_DESC_CLASS}>
              {alertDescription}
            </AlertDescription>
          </Alert>
        ) : null}
        <BinanceHistoryPathChoice
          variant="plain"
          tradeSync={binanceTradeSync}
          onChooseRecentOnly={handleBinanceChooseRecentOnly}
          onChooseNeedsCsv={handleBinanceChooseNeedsCsv}
        />
      </div>
    );
  };

  const renderIbkrTradeSyncUi = () => {
    if (!brokerTradeSync) return null;
    return (
      <div className="mb-4">
        {showIbkrHistoryChoice ? (
          <BrokerHistoryPathChoice
            tradeSync={brokerTradeSync}
            syncLabel="IBKR Flex sync"
            recentPathLabel="Flex sync"
            onChooseRecentOnly={handleIbkrChooseRecentOnly}
            onChooseNeedsCsv={handleIbkrChooseNeedsCsv}
          />
        ) : null}
      </div>
    );
  };

  const renderCtraderTradeSyncUi = () => {
    if (!ctraderOpenApiSync) return null;
    return (
      <div className="mb-4">
        {showCtraderHistoryChoice && ctraderBrokerTradeSync ? (
          <BrokerHistoryPathChoice
            tradeSync={ctraderBrokerTradeSync}
            syncLabel="cTrader Open API sync"
            recentPathLabel="Broker Sync"
            showSyncDateRange={
              (selectedCapability?.trade_sync as { has_fixed_calendar_window?: boolean } | undefined)
                ?.has_fixed_calendar_window === true
            }
            onChooseRecentOnly={handleCtraderChooseRecentOnly}
            onChooseNeedsCsv={handleCtraderChooseNeedsCsv}
          />
        ) : (
          <>
            <CtraderOpenApiSyncGuidance
              openApiSync={ctraderOpenApiSync}
              emphasizeCsv={ctraderHistoryPath === "needs_csv_for_history"}
              onGoToCsvImport={showBothTabs ? focusBrokerCsvImport : undefined}
              lookbackDays={ctraderBrokerTradeSync?.syncLookbackDays}
            />
          </>
        )}
      </div>
    );
  };

  const renderCtraderOAuthConnectForm = () => {
    return (
      <div className="flex flex-col gap-3">
        <Field className="gap-2" data-ctrader-environment-field="">
          <FieldLabel className={BROKER_ALERT_TITLE_CLASS}>Environment</FieldLabel>
          <FieldDescription className={BROKER_FIELD_DESC_CLASS}>
            Choose which cTrader environment to connect. This is saved per account and used during
            sync.
          </FieldDescription>
          <ShadcnRadioGroup
            data-slot="radio-group"
            value={ctraderConnectEnvironment}
            onValueChange={(value) => setCtraderConnectEnvironment(value as CtraderEnvironment)}
            className="flex flex-row flex-wrap gap-6"
          >
            {(["demo", "live"] as const).map((env) => {
              const inputId = `ctrader-connect-env-${env}`;
              return (
                <div key={env} className="flex min-w-0 items-start gap-2">
                  <RadioGroupItem
                    value={env}
                    id={inputId}
                    data-account-settings-radio=""
                    className="mt-0.5"
                  />
                  <FieldLabel
                    htmlFor={inputId}
                    className={`${BROKER_FIELD_DESC_CLASS} cursor-pointer font-normal`}
                  >
                    {env === "demo" ? "Demo" : "Live"}
                  </FieldLabel>
                </div>
              );
            })}
          </ShadcnRadioGroup>
        </Field>
        {renderConnectTradedAssetClassesBlock()}
        {!showEmbeddedWizardCtraderConnectFooter ? (
          <div className="flex flex-wrap gap-3 pt-1">
            <ShadcnButton
              type="button"
              variant="gradient"
              size="sm"
              data-font="primary"
              className="shrink-0"
              disabled={isCtraderOAuthStarting}
              aria-busy={isCtraderOAuthStarting || undefined}
              onClick={handleCtraderOAuthConnect}
            >
              {isCtraderOAuthStarting ? (
                <Loader2 data-icon="inline-start" className="size-3.5 animate-spin" aria-hidden />
              ) : null}
              Connect cTrader (OAuth)
            </ShadcnButton>
            {credentialsSaved ? (
              <ShadcnButton
                type="button"
                variant="outline-red"
                size="sm"
                data-font="primary"
                className="shrink-0"
                disabled={isRemovingCredentials}
                onClick={openRemoveCredentialsDialog}
              >
                {isRemovingCredentials ? (
                  <>
                    <Loader2 data-icon="inline-start" className="size-3.5 shrink-0 animate-spin" aria-hidden />
                    Disconnect
                  </>
                ) : (
                  "Disconnect"
                )}
              </ShadcnButton>
            ) : null}
          </div>
        ) : null}
        {credentialsSaved ? (
          <Badge variant="outline" className={BROKER_INTEGRATION_SAVED_BADGE_CLASS}>
            Connected
          </Badge>
        ) : null}
      </div>
    );
  };

  const handleConnectAssetClassesChange = (next: string[]) => {
    invalidateCredentialsTest();
    setConnectAssetClasses(next);
  };

  const renderSaveCredentialsButton = (label = "Save credentials") => (
    <ShadcnButton
      type="button"
      variant="gradient"
      size="sm"
      data-font="primary"
      className="shrink-0"
      disabled={isSaving || !credentialsTestPassed}
      onClick={handleSaveCredentials}
    >
      {isSaving ? (
        <>
          <Loader2 className="size-4 animate-spin" aria-hidden />
          Saving…
        </>
      ) : (
        label
      )}
    </ShadcnButton>
  );

  const openUpdateCredentialsDialog = () => {
    invalidateCredentialsTest();
    if (isBinanceBroker && credentialsSaved) {
      void openBinanceCredentialsEditForm();
      return;
    }
    setCredentialsEditMode(true);
    if (!credentialsSaved) {
      setCredentialValues({});
      setBinanceSecretsOnFile({ hasApiKeyOnFile: false, hasApiSecretOnFile: false });
      return;
    }
    setCredentialValues((prev) => {
      const next: Record<string, string> = {
        ...prev,
        ...(savedNonSecretSummary ? { username: savedNonSecretSummary } : {}),
      };
      if (normalizedSelectedBroker === "oanda" && savedCredentialSnapshot) {
        const env =
          savedCredentialSnapshot.oanda_environment ?? savedCredentialSnapshot.oandaEnvironment;
        if (env != null && String(env).trim() !== "") {
          const v = String(env).trim();
          const envField = [...requiredCredentialFields, ...optionalCredentialFields].find(
            isOandaEnvironmentCredentialField
          );
          if (envField) {
            next[envField] = v;
          }
        }
      }
      return next;
    });
  };

  const renderCredentialInputs = ({
    variant = "inline",
  }: {
    variant?: "inline" | "update-dialog";
  } = {}) => {
    const isUpdateDialog = variant === "update-dialog";
    const hideInlineCredentialActions = hideEmbeddedAutoSyncCredentialActions && !isUpdateDialog;
    if (isCtraderOAuthBroker) {
      return renderCtraderOAuthConnectForm();
    }

    const requiredFields = isBinanceBroker ? binanceAuthRequiredFields : requiredCredentialFields;
    const optionalFields = isBinanceBroker ? binanceOptionalAuthFields : optionalCredentialFields;

    return (
      <div
        className="flex flex-col gap-3"
        onSubmit={(event) => event.preventDefault()}
      >
        {isBinanceBroker && binanceTradeSync && binanceSetupComplete
          ? renderBinanceTradeSyncUi()
          : null}
        {renderConnectTradedAssetClassesBlock()}
        {renderConnectExecutingBrokerBlock()}
        {requiredFields.map((field) =>
          renderCredentialFieldRow(field, isBinanceAuthFieldRequired(field))
        )}

        {optionalFields.map((field) => renderCredentialFieldRow(field, false))}

        <div className="flex flex-wrap gap-3 pt-1">
          <ShadcnButton
            type="button"
            variant="outline-brand"
            size="sm"
            data-font="primary"
            className="shrink-0"
            onClick={handleTestConnection}
          >
            Test Connection
          </ShadcnButton>
          {!isBinanceBroker && !isUpdateDialog && !hideInlineCredentialActions && (
            <>
              {renderSaveCredentialsButton("Save Credentials")}
              {credentialsSaved ? (
                <ShadcnButton
                  type="button"
                  variant="outline-red"
                  size="sm"
                  data-font="primary"
                  className="shrink-0"
                  disabled={isRemovingCredentials}
                  onClick={openRemoveCredentialsDialog}
                >
                  {isRemovingCredentials ? (
                    <>
                      <Loader2 data-icon="inline-start" className="size-3.5 shrink-0 animate-spin" aria-hidden />
                      Remove Credentials
                    </>
                  ) : (
                    "Remove Credentials"
                  )}
                </ShadcnButton>
              ) : null}
            </>
          )}
        </div>

        {testResult && (
          <Alert
            variant={brokerAlertVariant(credentialsTestPassed ? "success" : "error")}
            className={BROKER_ALERT_CLASS}
          >
            <BrokerAlertIcon tone={credentialsTestPassed ? "success" : "error"} />
            <AlertDescription className={BROKER_ALERT_DESC_CLASS}>
              <p>{testResult}</p>
              {testResultAction.trim() ? (
                <p className="mt-1 text-muted-foreground">{testResultAction.trim()}</p>
              ) : null}
            </AlertDescription>
          </Alert>
        )}

        {isBinanceBroker &&
          (isUpdateDialog || credentialsTestPassed) && (
          <>
            {(binanceSyncRequiredFields.length > 0 || binanceSyncOptionalFields.length > 0) && (
              <div className="flex flex-col gap-3 border-t border-border pt-4">
                {binanceSyncRequiredFields.map((field) => renderCredentialFieldRow(field, true))}
                {binanceSyncOptionalFields.map((field) => renderCredentialFieldRow(field, false))}
              </div>
            )}

            {!isUpdateDialog && !hideInlineCredentialActions ? (
              <div className="flex flex-wrap gap-3 pt-1">
                {renderSaveCredentialsButton("Save Credentials")}
              </div>
            ) : null}
          </>
        )}

        {credentialsSaved && !isUpdateDialog && (
          <Badge variant="outline" className={BROKER_INTEGRATION_SAVED_BADGE_CLASS}>
            Credentials Saved
          </Badge>
        )}
      </div>
    );
  };

  const renderUpdateCredentialsDialog = () => {
    if (!selectedCapability || isCtraderOAuthBroker) return null;
    const dialogOpen = credentialsEditMode && (credentialsSaved || brokerTradeAccountsForSelectedBroker.length > 0);
    const isAddMode = !credentialsSaved;

    return (
      <StrategyPineDetailDialog
        open={dialogOpen}
        onOpenChange={(open) => {
          if (!open) handleCancelCredentialsEdit();
        }}
        title={isAddMode ? "Add credentials" : "Update credentials"}
        description={
          isAddMode
            ? `Add connection settings for ${resolveBrokerDisplayName(selectedCapability)} to sync again.`
            : `Change saved connection settings for ${resolveBrokerDisplayName(selectedCapability)}.`
        }
        contentClassName="sm:max-w-2xl"
        footer={renderSaveCredentialsButton()}
      >
        {isLoadingCredentialsForm ? (
          <div className="flex items-center gap-3 py-2">
            <Loader2 className="size-4 shrink-0 animate-spin text-[color:var(--brand-tropical-surf)]" aria-hidden />
            <p className={BROKER_INTEGRATION_SECONDARY_TEXT_CLASS}>
              Loading saved settings…
            </p>
          </div>
        ) : (
          renderCredentialInputs({ variant: "update-dialog" })
        )}
      </StrategyPineDetailDialog>
    );
  };

  const renderAccountAndSync = () => {
    if (embeddedInConnectWizard) return null;

    const syncSectionShell = (children: React.ReactNode) => (
      <div className="border-t border-border pt-4">
        {children}
      </div>
    );

    const syncAccountDescription = (() => {
      if (!credentialsSaved) {
        if (isCtraderOAuthBroker) {
          return brokerTradeAccountsForSelectedBroker.length > 0
            ? "Reconnect cTrader before syncing trades. Connected accounts below stay available."
            : "Connect cTrader before syncing trades for this broker.";
        }
        return brokerTradeAccountsForSelectedBroker.length > 0
          ? "Add credentials before syncing trades for this broker. Connected accounts below stay available."
          : "Save credentials before syncing trades for this broker.";
      }
      if (isIbkrBroker) {
        return brokerTradeSync
          ? `Flex sync covers the last ${brokerTradeSync.syncLookbackDays} days (${brokerTradeSync.syncFromDate} through ${brokerTradeSync.syncToDate}). Use the resync action per account.`
          : "Account ID is read from your Flex report on test, save, or sync.";
      }
      if (isBinanceBroker && binanceTradeSync) {
        return `API sync covers the last ${binanceTradeSync.apiSyncLookbackDays} days (${binanceTradeSync.apiSyncFromDate} through ${binanceTradeSync.apiSyncToDate}). Wallet balance is not synced.`;
      }
      if (isCtraderOAuthBroker) {
        return "Resync individual cTrader accounts below. Use CSV import for older history.";
      }
      if (usesLogicalAccountCode && !syncAccountCodeForTrades) {
        return isCtraderOAuthBroker
          ? "Connected, but no account ID on file. Use Reconnect cTrader to pick an account."
          : "Add an account code in your saved credentials, then sync.";
      }
      return showAvailableAccountsSection
        ? "Connected accounts have Tessera trade data. Sync an account from Available accounts below to add it here. Then use the row Resync action to refresh that account only."
        : "Connected accounts for this broker. Resync, rename, clear trade data, or remove all Tessera data per account.";
    })();

    const syncDisabledForRow = (accountCode: string) => {
      if (!credentialsSaved) return true;
      if (isIbkrBroker || isBinanceBroker) return false;
      if (isOandaBroker && !accountCode.trim()) return true;
      if ((isCtraderOAuthBroker || usesLogicalAccountCode) && !accountCode.trim()) return true;
      return !accountCode.trim();
    };

    const handleAccountRemoved = async (brokerName: string, accountCode: string) => {
      await removeTradeAccount(brokerName, accountCode);
      setJustSyncedAccountCodes((prev) => {
        if (!prev.has(accountCode)) return prev;
        const next = new Set(prev);
        next.delete(accountCode);
        return next;
      });
      const slug = normalizeBrokerName(brokerName);
      setAccountsKeptAfterCredentialRemoval((prev) => {
        const remnant = prev[slug];
        if (!remnant) return prev;
        const nextList = remnant.filter((account) => account.accountCode !== accountCode);
        if (nextList.length === remnant.length) return prev;
        if (nextList.length === 0) {
          const next = { ...prev };
          delete next[slug];
          return next;
        }
        return { ...prev, [slug]: nextList };
      });
      if (normalizeBrokerName(brokerName) === normalizedSelectedBroker) {
        setAccountCode((prev) => (prev === accountCode ? "" : prev));
      }
      await refetchTradeAccounts();
      if (selectedCapability) {
        await loadBrokerDetails(selectedCapability.broker_name);
      }
    };

    const handleAccountDataRefresh = async () => {
      await refetchTradeAccounts();
      if (selectedCapability) {
        await loadBrokerDetails(selectedCapability.broker_name);
      }
    };

    return syncSectionShell(
      <>
        <Field className="gap-2">
          <FieldLabel className={BROKER_ALERT_TITLE_CLASS}>Connected Accounts</FieldLabel>
          <FieldDescription className={BROKER_FIELD_DESC_CLASS}>
            {syncAccountDescription}
          </FieldDescription>
        </Field>
        <div className="mt-3">
          <BrokerConnectedAccountsTable
            brokerName={normalizedSelectedBroker}
            accounts={connectedAccountsForTable}
            activeAccountCode={activeAccountCode}
            activeBrokerName={activeBrokerName}
            variant="connected"
            showSyncAction={showConnectMode}
            syncDisabled={isSyncing}
            isAccountSyncDisabled={syncDisabledForRow}
            onSyncAccount={handleSyncAccount}
            onAccountRemoved={handleAccountRemoved}
            onCustomNameUpdated={handleAccountDataRefresh}
            onTradeDataCleared={handleAccountDataRefresh}
          />
        </div>

        {showAvailableAccountsSection ? (
          <>
            <Field className="mt-8 gap-2">
              <FieldLabel className={BROKER_ALERT_TITLE_CLASS}>Available Accounts</FieldLabel>
              <FieldDescription className={BROKER_FIELD_DESC_CLASS}>
                {isLiquidBroker
                  ? "Accounts on your Liquid login that are not synced into Tessera yet. Use Add account on a row to sync that account only."
                  : "Accounts still at your broker after API-synced Tessera data was removed. Add an account to sync trades and move it back to Connected accounts."}
              </FieldDescription>
            </Field>
            <div className="mt-3">
              <BrokerConnectedAccountsTable
                brokerName={normalizedSelectedBroker}
                accounts={availableAccountsForTable}
                activeAccountCode={activeAccountCode}
                activeBrokerName={activeBrokerName}
                variant="available"
                showSyncAction={showConnectMode}
                syncDisabled={isSyncing}
                isAccountSyncDisabled={syncDisabledForRow}
                onSyncAccount={handleSyncAccount}
                onAccountRemoved={handleAccountRemoved}
                onCustomNameUpdated={handleAccountDataRefresh}
                onTradeDataCleared={handleAccountDataRefresh}
              />
            </div>
          </>
        ) : null}
      </>
    );
  };

  const renderCustomCsvImportedAccountsSection = () => {
    if (embeddedInConnectWizard) return null;

    return (
      <div className="w-full min-w-0">
        <Field className="gap-2">
          <FieldLabel className={BROKER_ALERT_TITLE_CLASS}>Imported Accounts</FieldLabel>
          <FieldDescription className={BROKER_FIELD_DESC_CLASS}>
            Accounts created from Custom CSV imports. Rename, clear trade data, or remove all Tessera
            data per account. Saved import templates are not affected.
          </FieldDescription>
        </Field>
        <div className="mt-3">
          <BrokerConnectedAccountsTable
            brokerName={normalizedSelectedBroker}
            accounts={connectedAccountsForTable}
            activeAccountCode={activeAccountCode}
            activeBrokerName={activeBrokerName}
            showSyncAction={false}
            onSyncAccount={handleSyncAccount}
            onAccountRemoved={async (brokerName, accountCode) => {
              await removeTradeAccount(brokerName, accountCode);
              if (normalizeBrokerName(brokerName) === normalizedSelectedBroker) {
                setAccountCode((prev) => (prev === accountCode ? "" : prev));
              }
              await refetchTradeAccounts();
            }}
            onCustomNameUpdated={refetchTradeAccounts}
            onTradeDataCleared={refetchTradeAccounts}
          />
        </div>
      </div>
    );
  };

  const renderFuturesOrdersImportedAccountsSection = () => {
    if (embeddedInConnectWizard) return null;

    return (
      <div className="w-full min-w-0">
        <Field className="gap-2">
          <FieldLabel className={BROKER_ALERT_TITLE_CLASS}>Connected Accounts</FieldLabel>
          <FieldDescription className={BROKER_FIELD_DESC_CLASS}>
            Accounts imported from {futuresOrdersPlatformName} Orders CSV. Use Import trades above to
            add or update trades. Rename, clear trade data, or remove all Tessera data per account.
          </FieldDescription>
        </Field>
        <div className="mt-3">
          <BrokerConnectedAccountsTable
            brokerName={normalizedSelectedBroker}
            accounts={connectedAccountsForTable}
            activeAccountCode={activeAccountCode}
            activeBrokerName={activeBrokerName}
            showSyncAction={false}
            onSyncAccount={handleSyncAccount}
            onAccountRemoved={async (brokerName, accountCode) => {
              await removeTradeAccount(brokerName, accountCode);
              if (normalizeBrokerName(brokerName) === normalizedSelectedBroker) {
                setAccountCode((prev) => (prev === accountCode ? "" : prev));
              }
              await refetchTradeAccounts();
            }}
            onCustomNameUpdated={refetchTradeAccounts}
            onTradeDataCleared={refetchTradeAccounts}
          />
        </div>
      </div>
    );
  };

  const renderStatementImportAccountsSection = () => {
    if (embeddedInConnectWizard) return null;

    const platformName = resolveBrokerDisplayName(selectedCapability);
    const copy = statementImportAccountsManagementCopy(normalizedSelectedBroker, platformName);

    return (
      <div className="w-full min-w-0">
        <Field className="gap-2">
          <FieldLabel className={BROKER_ALERT_TITLE_CLASS}>Connected Accounts</FieldLabel>
          <FieldDescription className={BROKER_FIELD_DESC_CLASS}>
            {copy.accountsDescription}
          </FieldDescription>
        </Field>
        <div className="mt-3">
          <BrokerConnectedAccountsTable
            brokerName={normalizedSelectedBroker}
            accounts={connectedAccountsForTable}
            activeAccountCode={activeAccountCode}
            activeBrokerName={activeBrokerName}
            showSyncAction={false}
            onSyncAccount={handleSyncAccount}
            onAccountRemoved={async (brokerName, accountCode) => {
              await removeTradeAccount(brokerName, accountCode);
              if (normalizeBrokerName(brokerName) === normalizedSelectedBroker) {
                setAccountCode((prev) => (prev === accountCode ? "" : prev));
              }
              await refetchTradeAccounts();
            }}
            onCustomNameUpdated={refetchTradeAccounts}
            onTradeDataCleared={refetchTradeAccounts}
          />
        </div>
      </div>
    );
  };

  const csvImportAnalytics = selectedCapability?.csv_import_analytics;

  const ctraderCsvHasAssetClassesField = useMemo(
    () => activeCsvFormFields.some((field) => isTradedAssetClassesField(field)),
    [activeCsvFormFields]
  );

  const renderCtraderUtcOffsetField = () => {
    if (!ctraderShowUtcOffsetField) return null;
    const fieldId = "broker-csv-field-ctrader-utc-offset";
    const fieldHint = csvFieldHint(CTRADER_UTC_OFFSET_FIELD);
    return (
      <Field key={CTRADER_UTC_OFFSET_FIELD} className="gap-2" data-broker-csv-field="">
        <FieldLabel htmlFor={fieldId} className={BROKER_ALERT_TITLE_CLASS}>
          {csvFieldLabel(CTRADER_UTC_OFFSET_FIELD)}
        </FieldLabel>
        {fieldHint ? (
          <FieldDescription className={BROKER_FIELD_DESC_CLASS}>{fieldHint}</FieldDescription>
        ) : null}
        <ShadcnInput
          id={fieldId}
          type="number"
          step="1"
          value={csvValues[CTRADER_UTC_OFFSET_FIELD] || ""}
          onChange={(e) => handleChangeCsvField(CTRADER_UTC_OFFSET_FIELD, e.target.value)}
          placeholder={
            getFieldMeta(normalizedSelectedBroker, CTRADER_UTC_OFFSET_FIELD).placeholder ||
            "e.g. 8"
          }
          className="font-[family-name:var(--font-primary)]"
        />
      </Field>
    );
  };

  const renderCsvImportForm = () => (
    <div
      ref={csvImportFormRef}
      className="flex flex-col gap-3"
      data-broker-csv-import-form=""
    >
      {renderIbkrFlexFormatBanner()}
      {isTradingViewBrokerSelected ? <TradingViewBacktestGuidance compact /> : null}
      {isTradingViewPaperBrokerSelected ? <TradingViewPaperGuidance compact /> : null}
      {isMt5BrokerSelected ? <Mt5TradeHistoryGuidance compact /> : null}
      {isBinanceBroker ? <BinanceCsvImportGuidance /> : null}
      {isMt4BrokerSelected ? <Mt4StatementImportGuidance compact /> : null}
      {isCtraderBrokerSelected ? (
        <CtraderStatementImportGuidance compact />
      ) : null}
      {isLiquidBroker ? <LiquidBrokersStatementImportGuidance compact /> : null}
      {isOandaBroker ? <OandaTransactionsImportGuidance compact /> : null}
      {isHyperliquidBrokerSelected ? <HyperliquidCsvImportGuidance compact /> : null}
      {isFuturesOrdersCsvBrokerSelected ? (
        <FuturesOrdersCsvImportGuidance brokerName={normalizedSelectedBroker} compact />
      ) : null}
      {futuresOrdersFixedAssetClassesLabel ? (
        <p className={BROKER_INTEGRATION_BODY_TEXT_CLASS}>
          Asset class: {futuresOrdersFixedAssetClassesLabel}
        </p>
      ) : null}
      {renderCsvExecutingBrokerBlock()}
      {!isIbkrBroker && !isCtraderBrokerSelected ? (
        <Alert variant="info" className={BROKER_ALERT_CLASS}>
          <BrokerAlertIcon tone="info" />
          <AlertDescription className={BROKER_ALERT_DESC_CLASS}>
            {isTradingViewBrokerSelected
              ? "Add new account creates a separate backtest in Tessera. Pick an existing account only when you want to merge more trades into that same account name."
              : isTradingViewPaperBrokerSelected
                ? "Add new account creates a separate Paper Trading label in Tessera. Pick an existing account only when you want to merge more Order History into that same account name."
              : "Choose an existing Tessera account below, or add a new account for a separate import. Re-importing the same account adds or updates trades."}
            {csvImportAnalytics?.supports_portfolio_growth === false ? (
              <span className="mt-2 block">
                CSV-imported accounts use trading P&amp;L analytics in Tessera (not live broker
                portfolio equity or transfer-based growth curves).
              </span>
            ) : null}
          </AlertDescription>
        </Alert>
      ) : null}
      {isBinanceBroker ? <BinanceCsvTransactionHistoryWarning /> : null}
      {activeCsvFormFields
        .filter((field) => !(isExecutingBrokerNameField(field) && showExecutingBrokerOnCsvImport))
        .map((field) => {
        if (isTradedAssetClassesField(field)) {
          const csvTradedSpec = resolveCsvTradedAssetClasses(selectedCapability, effectiveCsvImport);
          if (!csvTradedSpec) return null;
          if (csvTradedSpec && !csvTradedSpec.userSelectable && csvTradedSpec.fixedValues?.length) {
            return (
              <React.Fragment key={field}>
                <p className={BROKER_INTEGRATION_BODY_TEXT_CLASS}>
                  {csvFieldLabel(field)}: {formatTradedAssetClassesReadOnly(csvTradedSpec.fixedValues)}
                </p>
                {isCtraderBrokerSelected ? renderCtraderUtcOffsetField() : null}
              </React.Fragment>
            );
          }
          return (
            <React.Fragment key={field}>
              {renderTradedAssetClassesPicker({
                allowed: csvAllowedAssetClasses,
                value: usesMultiAssetClassImport
                  ? csvAssetClasses
                  : csvValues[field]
                    ? [csvValues[field]]
                    : [],
                onChange: (vals) => {
                  if (usesMultiAssetClassImport) {
                    setCsvAssetClasses(vals);
                  } else {
                    handleChangeCsvField(field, vals[0] || "");
                  }
                },
                multi: usesMultiAssetClassImport,
                label: csvFieldLabel(field),
                hint: csvFieldHint(field) || undefined,
                required: csvTradedSpec.required,
              })}
              {isCtraderBrokerSelected ? renderCtraderUtcOffsetField() : null}
            </React.Fragment>
          );
        }
        if (isCsvAccountIdentityField(field)) {
          return (
            <CsvImportAccountCodeField
              key={field}
              field={field}
              label={csvFieldLabel(field)}
              hint={csvFieldHint(field)}
              placeholder={
                getFieldMeta(normalizedSelectedBroker, field).placeholder ||
                `Enter ${csvFieldLabel(field)}`
              }
              value={csvValues[field] ?? ""}
              onChange={(val) => {
                handleChangeCsvField(field, val);
                if (field === "account_code") {
                  setAccountCode(val);
                }
              }}
              brokerAccounts={csvBrokerAccounts}
              preferNewAccountByDefault={
                field === "account_name" ||
                isTradingViewBrokerSelected ||
                isTradingViewPaperBrokerSelected
              }
              pickerHint={
                csvBrokerAccounts.length > 0
                  ? isTradingViewBrokerSelected
                    ? "Add new account keeps this backtest separate. Select an existing account only to merge trades into that label."
                    : isTradingViewPaperBrokerSelected
                      ? "Add new account keeps this paper account separate. Select an existing account only to merge more Order History into that label."
                    : "Add new account for a separate import, or select an existing account to merge trades."
                  : null
              }
            />
          );
        }
        const instrumentUppercase = field === "instrument";
        const fieldId = `broker-csv-field-${field}`;
        const fieldPlaceholder =
          getFieldMeta(normalizedSelectedBroker, field).placeholder ||
          (isTimezoneField(field) ? "Select timezone" : `Enter ${csvFieldLabel(field)}`);
        const fieldHint = csvFieldHint(field);

        return (
          <Field key={field} className="gap-2" data-broker-csv-field="">
            <FieldLabel htmlFor={fieldId} className={BROKER_ALERT_TITLE_CLASS}>
              {csvFieldLabel(field)}
              <span className="text-destructive"> *</span>
            </FieldLabel>
            {fieldHint ? (
              <FieldDescription className={BROKER_FIELD_DESC_CLASS}>{fieldHint}</FieldDescription>
            ) : null}
            {isTimezoneField(field) ? (
              <TimezoneCombobox
                id={fieldId}
                value={csvValues[field] || ""}
                onValueChange={(next) => handleChangeCsvField(field, next)}
                placeholder={fieldPlaceholder}
                options={timezoneSelectOptions}
                triggerClassName="w-full max-w-full font-[family-name:var(--font-primary)] text-sm font-normal normal-case tracking-normal text-gray-500 [&_[data-slot=select-value]]:text-gray-500"
                aria-label={fieldPlaceholder}
              />
            ) : (
              <ShadcnInput
                id={fieldId}
                value={csvValues[field] || ""}
                onChange={(e) =>
                  handleChangeCsvField(
                    field,
                    instrumentUppercase ? e.target.value.toUpperCase() : e.target.value
                  )
                }
                placeholder={fieldPlaceholder}
                className="font-[family-name:var(--font-primary)]"
              />
            )}
          </Field>
        );
      })}

      {ctraderShowUtcOffsetField && !ctraderCsvHasAssetClassesField
        ? renderCtraderUtcOffsetField()
        : null}

      <BrokerCsvFileUploadField
        id="broker-csv-main-file"
        label={
          isMt5BrokerSelected
            ? "Trade History Report (.xlsx)"
            : isMt4BrokerSelected
              ? "Detailed Report (.htm/.html)"
              : isFuturesOrdersCsvBrokerSelected
                ? "Orders CSV"
                : isCtraderBrokerSelected
                  ? "Statement CSV"
                  : "CSV File"
        }
        accept={
          isMt5BrokerSelected
            ? ".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            : isMt4BrokerSelected
              ? ".htm,.html,text/html"
              : ".csv,text/csv"
        }
        file={csvFile}
        onFileChange={setCsvFile}
        required
      />

      {futuresOrdersShowCashHistoryUpload ? (
        <BrokerCsvFileUploadField
          id="broker-csv-cash-history-file"
          label={futuresOrdersCashHistoryFieldLabel(effectiveCsvImport?.fieldLabels)}
          description={futuresOrdersCashHistoryFieldHint(
            effectiveCsvImport?.fieldLabels,
            normalizedSelectedBroker
          )}
          accept=".csv,text/csv"
          file={futuresOrdersCashHistoryFile}
          onFileChange={setFuturesOrdersCashHistoryFile}
        />
      ) : null}

      {!showEmbeddedWizardCsvImportFooter ? (
        <ShadcnButton
          type="button"
          variant="gradient"
          size="sm"
          data-font="primary"
          className="shrink-0"
          disabled={isUploadingCsv || csvImportRateLimit.isCoolingDown}
          aria-busy={isUploadingCsv || undefined}
          onClick={handleCsvImport}
        >
          {isUploadingCsv ? (
            <Loader2 data-icon="inline-start" className="size-3.5 animate-spin" aria-hidden />
          ) : null}
          {embeddedCsvImportFooterLabel}
        </ShadcnButton>
      ) : null}
    </div>
  );

  const renderIbkrFlexFormatBanner = () => {
    if (!isIbkrBroker || !ibkrFlexFormatBannerMessage) return null;
    return (
      <Alert variant="warning" className={`mb-4 ${BROKER_ALERT_CLASS}`}>
        <BrokerAlertIcon tone="warning" />
        <AlertDescription className={BROKER_ALERT_DESC_CLASS}>
          <span className="break-words">{ibkrFlexFormatBannerMessage}</span>
          <ShadcnButton
            type="button"
            variant="link"
            size="sm"
            className="mt-2 h-auto p-0 text-[color:var(--brand-tropical-surf)]"
            onClick={() => setIbkrFlexFormatBannerMessage(null)}
          >
            Dismiss
          </ShadcnButton>
        </AlertDescription>
      </Alert>
    );
  };

  const switchEmbeddedWizardToFileImport = useCallback(() => {
    onEmbeddedWizardImportMethodChange?.("file_upload");
    focusBrokerCsvImport();
  }, [focusBrokerCsvImport, onEmbeddedWizardImportMethodChange]);

  const renderEmbeddedAutoSyncAlreadyComplete = () => {
    const brokerTitle = resolveBrokerDisplayName(selectedCapability);
    return (
      <div className="flex w-full min-w-0 flex-col gap-4" data-connect-broker-sync-already-complete="">
        <Alert variant="info" className={BROKER_ALERT_CLASS}>
          <BrokerAlertIcon tone="info" />
          <AlertDescription className={BROKER_ALERT_DESC_CLASS}>
            <span className={`${BROKER_ALERT_TITLE_CLASS} mb-1 block`}>
              Broker sync is already set up for {brokerTitle}
            </span>
            You&apos;ve already completed sync for this broker. Manage credentials and resync from
            Broker Management.
            {embeddedAutoSyncCanOfferFileImport
              ? " If you were looking for a manual file import instead, you can switch to that below."
              : null}
          </AlertDescription>
        </Alert>
        <div className="flex flex-wrap items-center gap-3">
          {embeddedAutoSyncCanOfferFileImport ? (
            <ShadcnButton
              type="button"
              variant="gradient"
              size="sm"
              data-font="primary"
              className="shrink-0"
              onClick={switchEmbeddedWizardToFileImport}
            >
              Import from a file instead
            </ShadcnButton>
          ) : null}
          <ShadcnButton
            type="button"
            variant="outline-gray"
            size="sm"
            data-font="primary"
            className="shrink-0"
            onClick={() => onEmbeddedWizardComplete?.()}
          >
            Back to Broker Management
          </ShadcnButton>
        </div>
      </div>
    );
  };

  const renderConnectIntegration = () => (
    <div className="flex flex-col gap-4">
      {renderIbkrFlexFormatBanner()}
      {showBothTabs && csvImportOnly && credentialSyncFlags.hasCredentialRecord ? (
        <Alert variant="info" className={BROKER_ALERT_CLASS}>
          <BrokerAlertIcon tone="info" />
          <AlertDescription className={BROKER_ALERT_DESC_CLASS}>
            Trades were imported via CSV. Use the CSV import tab to add more history, or save live API
            credentials below to enable Broker Sync.
          </AlertDescription>
        </Alert>
      ) : null}
      {isBinanceBroker && binanceTradeSync && binanceSetupComplete && credentialsSaved ? (
        <BinanceTradeSyncGuidance tradeSync={binanceTradeSync} />
      ) : null}
      {isCtraderOAuthBroker &&
      ctraderOpenApiSync &&
      ctraderSetupComplete &&
      credentialsSaved ? (
        <CtraderOpenApiSyncGuidance
          openApiSync={ctraderOpenApiSync}
          emphasizeCsv={ctraderHistoryPath === "needs_csv_for_history"}
          onGoToCsvImport={showBothTabs ? focusBrokerCsvImport : undefined}
          lookbackDays={ctraderBrokerTradeSync?.syncLookbackDays}
          compact
        />
      ) : null}
      {credentialsSaved ? (
        <div>
          <Alert variant="success" className={`mb-3 ${BROKER_ALERT_CLASS}`}>
            <BrokerAlertIcon tone="success" />
            <AlertDescription className={BROKER_ALERT_DESC_CLASS}>
              {isCtraderOAuthBroker
                ? savedNonSecretSummary
                  ? `cTrader connected. ${savedNonSecretSummary}. Use Reconnect if you added accounts at Spotware or need to refresh OAuth.`
                  : "cTrader connected via OAuth. Use Reconnect to refresh access or pick another account."
                : isBinanceBroker
                ? savedNonSecretSummary
                  ? `Credentials saved. ${savedNonSecretSummary}. API keys stay encrypted. Use Update credentials to change pairs or dates, and re-enter keys only when rotating.`
                  : "Credentials saved. API keys stay encrypted. Use Update credentials to edit sync settings."
                : savedNonSecretSummary
                  ? `Credentials are saved for this broker. Username on file: ${savedNonSecretSummary}. Password stays encrypted. You only need to re-enter it if you update credentials.`
                  : "Credentials are saved for this broker. Password stays encrypted. You only need to re-enter secrets if you update credentials."}
            </AlertDescription>
          </Alert>
          <div className="flex flex-wrap gap-3">
            {isCtraderOAuthBroker ? (
              <ShadcnButton
                type="button"
                variant="gradient"
                size="sm"
                data-font="primary"
                className="shrink-0"
                disabled={isCtraderOAuthStarting}
                aria-busy={isCtraderOAuthStarting || undefined}
                onClick={handleCtraderOAuthConnect}
              >
                {isCtraderOAuthStarting ? (
                  <Loader2 data-icon="inline-start" className="size-3.5 animate-spin" aria-hidden />
                ) : null}
                Reconnect cTrader
              </ShadcnButton>
            ) : (
              <ShadcnButton
                type="button"
                variant="outline-gray"
                size="sm"
                data-font="primary"
                className="shrink-0"
                onClick={openUpdateCredentialsDialog}
              >
                Update credentials
              </ShadcnButton>
            )}
            <ShadcnButton
              type="button"
              variant="outline-red"
              size="sm"
              data-font="primary"
              className="shrink-0"
              disabled={isRemovingCredentials}
              onClick={openRemoveCredentialsDialog}
            >
              {isRemovingCredentials ? (
                <>
                  <Loader2 data-icon="inline-start" className="size-3.5 shrink-0 animate-spin" aria-hidden />
                  {isCtraderOAuthBroker ? "Disconnect" : "Remove credentials"}
                </>
              ) : isCtraderOAuthBroker ? (
                "Disconnect"
              ) : (
                "Remove credentials"
              )}
            </ShadcnButton>
            <ShadcnButton
              type="button"
              variant="outline-red"
              size="sm"
              data-font="primary"
              className="shrink-0"
              disabled={isRemovingBroker}
              onClick={openRemoveBrokerDialog}
            >
              {isRemovingBroker ? (
                <>
                  <Loader2 data-icon="inline-start" className="size-3.5 shrink-0 animate-spin" aria-hidden />
                  Remove broker
                </>
              ) : (
                "Remove broker"
              )}
            </ShadcnButton>
          </div>
        </div>
      ) : brokerTradeAccountsForSelectedBroker.length > 0 ||
        normalizedSelectedBroker in accountsKeptAfterCredentialRemoval ? (
        <div>
          <Alert variant="info" className={`mb-3 ${BROKER_ALERT_CLASS}`}>
            <BrokerAlertIcon tone="info" />
            <AlertDescription className={BROKER_ALERT_DESC_CLASS}>
              {isCtraderOAuthBroker
                ? "cTrader is not connected. Connected accounts and Tessera trade data are still available. Reconnect to sync again, or remove the broker to delete accounts and OAuth access together."
                : "Credentials are not saved for this broker. Connected accounts and Tessera trade data are still available. Add credentials to sync again, or remove the broker to delete accounts and credentials together."}
            </AlertDescription>
          </Alert>
          <div className="flex flex-wrap gap-3">
            {isCtraderOAuthBroker ? (
              <ShadcnButton
                type="button"
                variant="gradient"
                size="sm"
                data-font="primary"
                className="shrink-0"
                disabled={isCtraderOAuthStarting}
                aria-busy={isCtraderOAuthStarting || undefined}
                onClick={handleCtraderOAuthConnect}
              >
                {isCtraderOAuthStarting ? (
                  <Loader2 data-icon="inline-start" className="size-3.5 animate-spin" aria-hidden />
                ) : null}
                Reconnect cTrader
              </ShadcnButton>
            ) : (
              <ShadcnButton
                type="button"
                variant="outline-gray"
                size="sm"
                data-font="primary"
                className="shrink-0"
                onClick={openUpdateCredentialsDialog}
              >
                Add credentials
              </ShadcnButton>
            )}
            <ShadcnButton
              type="button"
              variant="outline-red"
              size="sm"
              data-font="primary"
              className="shrink-0"
              disabled={isRemovingBroker}
              onClick={openRemoveBrokerDialog}
            >
              {isRemovingBroker ? (
                <>
                  <Loader2 data-icon="inline-start" className="size-3.5 shrink-0 animate-spin" aria-hidden />
                  Remove broker
                </>
              ) : (
                "Remove broker"
              )}
            </ShadcnButton>
          </div>
        </div>
      ) : isCtraderOAuthBroker ? (
        renderCtraderOAuthConnectForm()
      ) : (
        renderCredentialInputs()
      )}
      {renderUpdateCredentialsDialog()}
      {selectedCapability ? (
        <>
          <RemoveBrokerCredentialsDialog
            open={removeCredentialsDialogOpen}
            brokerDisplayName={resolveBrokerDisplayName(selectedCapability)}
            mode={isCtraderOAuthBroker ? "oauth" : "credentials"}
            confirmText={removeCredentialsConfirmText}
            onConfirmTextChange={setRemoveCredentialsConfirmText}
            isRemoving={isRemovingCredentials}
            onCancel={closeRemoveCredentialsDialog}
            onConfirm={handleDeleteCredentials}
          />
          <RemoveBrokerDialog
            open={removeBrokerDialogOpen}
            brokerDisplayName={resolveBrokerDisplayName(selectedCapability)}
            accountCount={brokerTradeAccountsForSelectedBroker.length}
            confirmText={removeBrokerConfirmText}
            onConfirmTextChange={setRemoveBrokerConfirmText}
            isRemoving={isRemovingBroker}
            onCancel={closeRemoveBrokerDialog}
            onConfirm={handleRemoveBroker}
          />
        </>
      ) : null}
      {renderAccountAndSync()}
    </div>
  );

  const getBrokerDetailsCardDescription = (): string | undefined => {
    if (!selectedCapability) return undefined;
    if (showBothTabs && !embeddedInConnectWizard) {
      if (isBinanceBroker && binanceTradeSync) {
        return `Manage API sync for the last ${binanceTradeSync.apiSyncLookbackDays} days and your connected accounts.`;
      }
      if (isCtraderOAuthBroker) {
        return "Manage cTrader connection, credentials, and accounts.";
      }
      if (isIbkrBroker && brokerTradeSync) {
        return brokerTradeSyncShortDescription(brokerTradeSync);
      }
      return "Manage broker credentials and accounts for this connection.";
    }
    if (showBothTabs) {
      if (isBinanceBroker && binanceTradeSync) {
        return `API sync covers the last ${binanceTradeSync.apiSyncLookbackDays} days. Use CSV import for older Binance Spot history.`;
      }
      if (isCtraderOAuthBroker) {
        return "Connect and sync recent trades, or import a statement CSV for older history.";
      }
      if (isIbkrBroker && brokerTradeSync) {
        return brokerTradeSyncShortDescription(brokerTradeSync);
      }
      return "Broker Sync and/or import a statement CSV. Use whichever path you need.";
    }
    if (showConnectMode) {
      if (isBinanceBroker && binanceTradeSync) {
        return `Broker Sync: automated fills for the last ${binanceTradeSync.apiSyncLookbackDays} days.`;
      }
      if (isCtraderOAuthBroker) {
        return "Connect and sync recent trades from cTrader.";
      }
      if (isIbkrBroker && brokerTradeSync) {
        return brokerTradeSyncShortDescription(brokerTradeSync);
      }
      return "Broker Sync: sign in at the broker, pick an account from the list, then sync trades.";
    }
    if (showCsvMode) {
      if (isCustomCsvBrokerSelected) {
        if (embeddedInConnectWizard) {
          return "Upload your CSV and map columns to import trades into Tessera.";
        }
        return "Manage imported accounts from Custom CSV uploads. Use Connect new broker to import more trades.";
      }
      if (isStatementImportAccountsBrokerSelected && selectedCapability && !embeddedInConnectWizard) {
        return statementImportAccountsManagementCopy(
          normalizedSelectedBroker,
          resolveBrokerDisplayName(selectedCapability)
        ).cardDescription;
      }
      if (isTradingViewBrokerSelected) return "Import a TradingView Strategy Tester trade list CSV.";
      if (isTradingViewPaperBrokerSelected) {
        return "Import a TradingView Paper Trading Order History CSV.";
      }
      if (isMt5BrokerSelected) return "Import an MT5 Trade History Report (.xlsx).";
      if (isMt4BrokerSelected) return "Import an MT4 Detailed Report (.htm/.html).";
      if (isHyperliquidBrokerSelected) return "Import a Hyperliquid trade history CSV export.";
      if (isCtraderBrokerSelected) return "Import a cTrader Account or Statement CSV export.";
      if (isFuturesOrdersCsvBrokerSelected) {
        if (!embeddedInConnectWizard) {
          return `Manage imported ${futuresOrdersPlatformName} accounts. Use Import trades to upload another Orders CSV.`;
        }
        return `Import a ${futuresOrdersPlatformName} Orders CSV export.`;
      }
      if (isIbkrBroker && brokerTradeSync) {
        return brokerTradeSyncShortDescription(brokerTradeSync, { emphasizeCsv: true });
      }
      if (isIbkrBroker) return "CSV import: upload your IBKR Flex statement.";
      return "CSV import: upload your broker statement with the required fields below.";
    }
    return "No integration options are configured for this broker.";
  };

  const renderBrokerDetailsLoadingBody = () => (
    <div className="flex items-center gap-3 py-6">
      <Loader2 className="size-4 shrink-0 animate-spin text-[color:var(--brand-tropical-surf)]" aria-hidden />
      <p className={BROKER_INTEGRATION_SECONDARY_TEXT_CLASS}>
        Loading broker settings…
      </p>
    </div>
  );

  const renderConnectedBrokerSelect = () => {
    if (!manageableBrokers.length) {
      return (
        <p className={BROKER_INTEGRATION_SECONDARY_TEXT_CLASS}>
          No brokers connected yet. Use Connect new broker in the header to add your first broker.
        </p>
      );
    }

    const selectValue =
      selectedBroker &&
      manageableBrokers.some((broker) => broker.broker_name === selectedBroker)
        ? selectedBroker
        : selectedBroker || manageableBrokers[0]?.broker_name || "";

    const brokerOptions = manageableBrokers.map((broker) => {
      const slug = normalizeBrokerName(broker.broker_name);
      const accountCount = integratedBrokerAccountCounts.get(slug) ?? 0;
      const label = resolveBrokerDisplayName(broker);
      const itemLabel =
        accountCount > 0
          ? `${label} · ${accountCount} account${accountCount === 1 ? "" : "s"}`
          : label;
      return {
        value: broker.broker_name,
        label: itemLabel,
      };
    });
    if (
      selectedBroker &&
      !brokerOptions.some((option) => option.value === selectedBroker)
    ) {
      const cap = capabilities.find((item) => item.broker_name === selectedBroker);
      brokerOptions.unshift({
        value: selectedBroker,
        label: resolveBrokerDisplayName(cap ?? { broker_name: selectedBroker }),
      });
    }

    return (
      <AccountSettingsFieldSelect
        id="broker-management-broker-select"
        value={selectValue}
        onValueChange={handleSelectedBrokerChange}
        options={brokerOptions}
        placeholder="Select a broker"
      />
    );
  };

  const detailsSectionsReady =
    dualModeSetupComplete ||
    embeddedWizardLocksCsvTab;

  const showEmbeddedSyncHistorySetup =
    !embeddedInConnectWizard || embeddedImportMethod === "auto_sync";

  const renderConnectDetailsSection = () => (
    <>
      {showBothTabs ? (
        <>
          {isIbkrBroker && brokerTradeSync ? (
            <>
              {ibkrHistoryPath === "needs_csv_for_history" ? (
                <IbkrHistoryPathBanner tradeSync={brokerTradeSync} />
              ) : (
                <IbkrFlexSyncGuidance
                  tradeSync={brokerTradeSync}
                  variant="sync_only"
                  portalSetup={brokerTradeSync.portalSetup}
                />
              )}
            </>
          ) : null}
          {isBinanceBroker &&
          binanceHistoryPath === "needs_csv_for_history" &&
          binanceTradeSync ? (
            <Alert variant="warning" className={`mb-4 ${BROKER_ALERT_CLASS}`}>
              <BrokerAlertIcon tone="warning" />
              <AlertDescription className={BROKER_ALERT_DESC_CLASS}>
                You chose to import history older than the last{" "}
                {binanceTradeSync.apiSyncLookbackDays} days.{" "}
                {embeddedInConnectWizard
                  ? "Complete CSV import first, then use Broker Sync for recent fills."
                  : managementCsvImportFocus
                    ? "Complete CSV import below, then use Broker Sync for recent fills."
                    : "Use Import trades above to upload your CSV, then use Broker Sync for recent fills."}
              </AlertDescription>
            </Alert>
          ) : null}
          {isCtraderOAuthBroker &&
          ctraderHistoryPath === "needs_csv_for_history" &&
          ctraderOpenApiSync ? (
            <Alert variant="warning" className={`mb-4 ${BROKER_ALERT_CLASS}`}>
              <BrokerAlertIcon tone="warning" />
              <AlertDescription className={BROKER_ALERT_DESC_CLASS}>
                You chose to import older history via CSV.{" "}
                {embeddedInConnectWizard
                  ? "Complete CSV import on the CSV import tab, then use Broker Sync for recent Open API trades."
                  : managementCsvImportFocus
                    ? "Complete CSV import below, then use Broker Sync for recent Open API trades."
                    : "Use Import trades above to upload your CSV, then use Broker Sync for recent Open API trades."}
              </AlertDescription>
            </Alert>
          ) : null}
          {isCtraderOAuthBroker &&
          ctraderOpenApiSync &&
          ctraderSetupComplete &&
          !credentialsSaved
            ? renderCtraderTradeSyncUi()
            : null}
        </>
      ) : (
        <>
          {isIbkrBroker && brokerTradeSync ? renderIbkrTradeSyncUi() : null}
          {isIbkrBroker && brokerTradeSync ? (
            <>
              {ibkrHistoryPath === "needs_csv_for_history" ? (
                <IbkrHistoryPathBanner tradeSync={brokerTradeSync} />
              ) : ibkrHistoryPath === "recent_only" ? (
                <IbkrFlexSyncGuidance
                  tradeSync={brokerTradeSync}
                  variant="sync_only"
                  portalSetup={brokerTradeSync.portalSetup}
                />
              ) : null}
            </>
          ) : null}
          {isBinanceBroker && binanceTradeSync ? (
            <div className="mb-4">
              <BinanceTradeSyncGuidance tradeSync={binanceTradeSync} />
            </div>
          ) : null}
          {isCtraderOAuthBroker && ctraderOpenApiSync && !ctraderSetupComplete
            ? renderCtraderTradeSyncUi()
            : null}
          {isCtraderOAuthBroker &&
          ctraderOpenApiSync &&
          ctraderSetupComplete &&
          !credentialsSaved
            ? renderCtraderTradeSyncUi()
            : null}
        </>
      )}
      {renderConnectIntegration()}
    </>
  );

  const renderCsvDetailsSection = () =>
    isCustomCsvBrokerSelected ? (
      embeddedInConnectWizard ? (
        <CustomCsvImportWizard
          capability={selectedCapability}
          allCapabilities={capabilities}
          brokerAccounts={csvBrokerAccounts}
          onImportComplete={handleCustomCsvImportComplete}
          embeddedInConnectWizard
          navigationRef={customCsvWizardNavRef}
          onEmbeddedFooterChange={onEmbeddedWizardFooterChange}
        />
      ) : (
        renderCustomCsvImportedAccountsSection()
      )
    ) : isFuturesOrdersCsvBrokerSelected && !embeddedInConnectWizard ? (
      renderFuturesOrdersImportedAccountsSection()
    ) : isStatementImportAccountsBrokerSelected && !embeddedInConnectWizard ? (
      renderStatementImportAccountsSection()
    ) : (
      <>
        {isBinanceBroker &&
        binanceHistoryPath === "needs_csv_for_history" &&
        binanceTradeSync ? (
          <Alert variant="warning" className={`mb-4 ${BROKER_ALERT_CLASS}`}>
            <BrokerAlertIcon tone="warning" />
            <AlertDescription className={BROKER_ALERT_DESC_CLASS}>
              You chose to import history older than the last{" "}
              {binanceTradeSync.apiSyncLookbackDays} days. Complete CSV import below, then use
              Broker Sync for recent fills.
            </AlertDescription>
          </Alert>
        ) : null}
        {isIbkrBroker && brokerTradeSync ? (
          <>
            {ibkrHistoryPath === "needs_csv_for_history" ? (
              <IbkrHistoryPathBanner tradeSync={brokerTradeSync} />
            ) : null}
            <IbkrFlexSyncGuidance
              tradeSync={brokerTradeSync}
              variant="csv_tab"
              portalSetup={brokerTradeSync.portalSetup}
              compact
            />
          </>
        ) : null}
        {renderCsvImportForm()}
      </>
    );

  const renderBrokerDetailsBody = () => (
    <>
      {showBothTabs && showEmbeddedSyncHistorySetup ? (
        <>
          {isIbkrBroker && brokerTradeSync && !ibkrSetupComplete ? renderIbkrTradeSyncUi() : null}
          {isCtraderOAuthBroker && ctraderOpenApiSync && !ctraderSetupComplete
            ? renderCtraderTradeSyncUi()
            : null}
        </>
      ) : null}

      {detailsSectionsReady ? (
        showSyncCsvTabSwitcher ? (
          <ShadcnTabs
            data-market-context-buckets-tabs=""
            className="w-full"
            value={brokerIntegrationTab}
            onValueChange={(value) => setBrokerIntegrationTab(value as "sync" | "csv")}
          >
            <div className="w-full overflow-x-auto overflow-y-hidden">
              <TabsList className="min-w-max">
                <TabsTrigger value="sync">Broker Sync</TabsTrigger>
                <TabsTrigger value="csv">CSV import</TabsTrigger>
              </TabsList>
            </div>
            <TabsContent value="sync" className="mt-0 pt-4">
              {renderConnectDetailsSection()}
            </TabsContent>
            <TabsContent value="csv" className="mt-0 pt-4">
              {renderCsvDetailsSection()}
            </TabsContent>
          </ShadcnTabs>
        ) : (
          <div className="flex flex-col gap-6">
            {shouldShowConnectOnDetails ? renderConnectDetailsSection() : null}
            {shouldShowCsvOnDetails ? renderCsvDetailsSection() : null}
          </div>
        )
      ) : null}

      {!showConnectMode && !showCsvMode ? (
        <p className={`${BROKER_INTEGRATION_BODY_TEXT_CLASS} text-gray-500 dark:text-white`}>
          Contact support if this looks wrong.
        </p>
      ) : null}
    </>
  );

  const brokerFieldLabelClass =
    "font-[family-name:var(--font-primary)] text-sm font-normal text-[color:var(--brand-tropical-surf)]";
  const brokerFieldDescClass = BROKER_FIELD_DESC_CLASS;

  const awaitingInitialBrokerDetails =
    Boolean(selectedBroker) &&
    !hasLoadedBrokerDetailsOnce &&
    (embeddedInConnectWizard || manageableBrokers.length > 0);

  const showPageLoading =
    !capabilitiesHydrated ||
    (!embeddedInConnectWizard && tradeAccountsLoading && manageableBrokers.length === 0) ||
    awaitingInitialBrokerDetails;
  const brokerDetailsCardDescription = getBrokerDetailsCardDescription();
  const showBrokerDetailsCardBody =
    isLoadingBrokerDetails ||
    detailsSectionsReady ||
    showIbkrHistoryChoice ||
    showCtraderHistoryChoice;

  return (
    <div
      ref={brokerManagementTopRef}
      className="flex w-full min-w-0 flex-col"
      style={
        embeddedInConnectWizard
          ? undefined
          : { minHeight: statisticsPageMinHeight(shadcnInset) }
      }
    >
      {showPageLoading ? (
        <div className="flex min-h-0 w-full flex-1 items-center justify-center py-10">
          <StatisticsEmptyState
            variant="loading"
            fillParent
            title={
              embeddedInConnectWizard
                ? embeddedImportMethod === "file_upload"
                  ? "Preparing import"
                  : "Preparing connection"
                : "Loading broker management"
            }
            description={
              embeddedInConnectWizard
                ? embeddedImportMethod === "file_upload"
                  ? "Loading import options for this broker…"
                  : "Loading connection details for this broker…"
                : "Please wait while we load your broker connections and settings."
            }
          />
        </div>
      ) : (
        <>
      {!embeddedInConnectWizard && !shadcnInset ? (
        <div className="mb-6 flex justify-end">
          {brokerManagementHeaderToolbar}
        </div>
      ) : null}
      <div
        className={
          embeddedInConnectWizard ? "w-full min-w-0" : accountSettingsCardStackClassName
        }
        data-account-settings-card-stack={embeddedInConnectWizard ? undefined : ""}
      >
      {!embeddedInConnectWizard ? (
        <Card
          className={cn(accountSettingsNestedCardClassName, "w-full")}
        >
          <CardContent className={cn(statisticsShadcnCardContentClassName, "pt-[22px]")}>
            <Field className={cn(accountSettingsFieldClassName, "w-full max-w-md")}>
              <FieldLabel
                htmlFor="broker-management-broker-select"
                className={brokerFieldLabelClass}
              >
                Broker
              </FieldLabel>
              <FieldDescription className={brokerFieldDescClass}>
                Select a connected broker to manage settings and accounts. Use Connect new broker in
                the header to add a broker that is not listed yet.
              </FieldDescription>
              {renderConnectedBrokerSelect()}
            </Field>
          </CardContent>
        </Card>
      ) : null}

      {selectedCapability &&
      (embeddedInConnectWizard || manageableBrokers.length > 0 || selectedBroker) ? (
        embeddedInConnectWizard ? (
          <div className="flex w-full min-w-0 flex-col gap-4">
            {embeddedAutoSyncAlreadyComplete ? (
              renderEmbeddedAutoSyncAlreadyComplete()
            ) : (
              <>
                {showBinanceHistoryChoice && isBinanceBroker
                  ? renderBinanceHistoryPathInCardHeader(brokerDetailsCardDescription)
                  : null}
                {showBrokerDetailsCardBody ? (
                  isLoadingBrokerDetails
                    ? renderBrokerDetailsLoadingBody()
                    : renderBrokerDetailsBody()
                ) : null}
              </>
            )}
          </div>
        ) : (
        <Card
          className={cn(accountSettingsNestedCardClassName, "w-full")}
        >
          <div
            className={cn(
              "px-[22px] pt-[22px]",
              !showBrokerDetailsCardBody && showBinanceHistoryChoice && "pb-[22px]"
            )}
          >
            {showBinanceHistoryChoice && isBinanceBroker ? (
              renderBinanceHistoryPathInCardHeader(brokerDetailsCardDescription)
            ) : (
              <div className="mb-4 flex items-start justify-between gap-4">
                <Field className={cn(accountSettingsFieldClassName, "min-w-0 flex-1")}>
                  <FieldLabel className={BROKER_ALERT_TITLE_CLASS}>
                    {resolveBrokerDisplayName(selectedCapability)}
                  </FieldLabel>
                  {brokerDetailsCardDescription ? (
                    <FieldDescription className={brokerFieldDescClass}>
                      {brokerDetailsCardDescription}
                    </FieldDescription>
                  ) : null}
                </Field>
                {(showBrokerDetailsResyncTrades || showBrokerDetailsImportTrades) ? (
                  <div className="flex shrink-0 flex-wrap items-center justify-end gap-2">
                    {showBrokerDetailsResyncTrades ? (
                      <BrokerResyncTradesButton
                        onResyncTrades={() => {
                          const liquidOnlyAccount =
                            isLiquidBroker && connectedAccountsForTable.length === 1
                              ? connectedAccountsForTable[0]?.accountCode
                              : undefined;
                          void handleSyncAccount(
                            liquidOnlyAccount || brokerDetailsResyncAccounts[0]?.accountCode
                          );
                        }}
                        disabled={brokerDetailsHeaderActionsDisabled}
                        isLoading={isSyncing}
                      />
                    ) : null}
                    {showBrokerDetailsImportTrades ? (
                      <BrokerImportTradesButton
                        onImportTrades={handleBrokerDetailsImportTrades}
                        disabled={brokerDetailsHeaderActionsDisabled}
                      />
                    ) : null}
                  </div>
                ) : null}
              </div>
            )}
          </div>
          {showBrokerDetailsCardBody ? (
            <CardContent className={cn(statisticsShadcnCardContentClassName, "pt-0")}>
              {isLoadingBrokerDetails
                ? renderBrokerDetailsLoadingBody()
                : renderBrokerDetailsBody()}
            </CardContent>
          ) : null}
        </Card>
        )
      ) : null}
      </div>
        </>
      )}
      {!embeddedInConnectWizard ? (
        <ConnectBrokerWizardDialog
          open={connectWizardOpen}
          onOpenChange={handleConnectWizardOpenChange}
          seed={connectWizardSeed}
        />
      ) : null}
    </div>
  );
};

export default BrokerIntegrationSection;
